<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import DeleteDialog from 'dashboard/components-next/ailab/pageComponents/DeleteDialog.vue';
import ConnectInboxDialog from 'dashboard/components-next/ailab/pageComponents/inbox/ConnectInboxDialog.vue';
import AiCloserConfigVersionsDialog from 'dashboard/components-next/ailab/pageComponents/assistant/settings/AiCloserConfigVersionsDialog.vue';
import ConnectInboxDialogAIGamer from '../../../tabs/inboxes/ConnectInboxDialogAIGamer.vue';
import AiCloserAPI from 'dashboard/api/aiCloser';
import {
  advanceRollbackSessionContext,
  buildRollbackJourneyItemAction,
  buildRollbackSessionJourneyItemBadges,
  buildRollbackSessionJourneySummary,
  buildOperatorNoteTimelineBadges,
  buildOperatorNoteTimelineContextLabel,
  buildOperatorNoteTimelineVersionDialogContext,
  buildRollbackBreadcrumbBadges,
  buildRollbackEvidenceCompareBundle,
  buildReleaseReviewSnapshotKey,
  ensureRollbackSessionContext,
  findBatchBaseline,
  findReleaseReviewSnapshot,
  groupRollbackSessionJourney,
  humanizeRollbackJourneyHopKind,
  normalizeRollbackSessionContext,
  rollbackSessionContextFromEntry,
  latestOperatorNoteTimelineEntries,
  mergeOperatorNoteTimelineEntries,
  mergeReleaseReviewSnapshots,
  normalizeBatchBaselines,
  normalizeReleaseReviewSnapshots,
} from '../../lib/releaseLoopSnapshots';

const props = defineProps({
  assistant: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const alert = useAlert();
const DEPLOY_SEED_QUERY_KEYS = [
  'deploy_seed',
  'deploy_seed_source',
  'deploy_seed_focus',
  'deploy_seed_reason',
  'deploy_seed_inbox_id',
  'deploy_seed_confidence_level',
  'deploy_seed_confidence_runs',
  'deploy_seed_confidence_title',
  'deploy_seed_batch_baseline_key',
  'deploy_seed_batch_set_key',
  'deploy_seed_batch_title',
  'deploy_seed_batch_overall_level',
  'deploy_seed_batch_total_cases',
  'deploy_seed_batch_ready_cases',
  'deploy_seed_batch_watch_cases',
  'deploy_seed_batch_blocked_cases',
  'deploy_seed_batch_ran_at',
  'deploy_seed_batch_top_blocker',
  'deploy_seed_batch_top_watch_signal',
  'deploy_seed_followup_intent',
  'deploy_seed_target_version_label',
  'deploy_seed_compare_version_label',
  'deploy_seed_prefill_note',
  'deploy_seed_open_versions',
  'deploy_seed_session_id',
  'deploy_seed_session_label',
  'deploy_seed_session_step',
  'deploy_seed_session_origin',
  'deploy_seed_session_item_key',
];
const DEPLOY_BATCH_SEED_QUERY_KEYS = [
  'deploy_seed_batch_baseline_key',
  'deploy_seed_batch_set_key',
  'deploy_seed_batch_title',
  'deploy_seed_batch_overall_level',
  'deploy_seed_batch_total_cases',
  'deploy_seed_batch_ready_cases',
  'deploy_seed_batch_watch_cases',
  'deploy_seed_batch_blocked_cases',
  'deploy_seed_batch_ran_at',
  'deploy_seed_batch_top_blocker',
  'deploy_seed_batch_top_watch_signal',
];
const DEPLOY_COMPARE_QUERY_KEYS = [
  'deploy_seed_bundle',
  'deploy_seed_title',
  'deploy_seed_focus_label',
  'deploy_seed_strategy_group_title',
  'deploy_seed_recommendation_code',
  'deploy_seed_owner_workspace',
  'deploy_seed_owner_action_title',
  'deploy_seed_guidance_editor',
  'deploy_seed_escalation_focus',
  'deploy_seed_hotspot_summary',
  'deploy_seed_resolved_summary',
  'deploy_seed_escalated_summary',
  'deploy_seed_validation_summary',
  'deploy_seed_stage',
  'deploy_seed_rollback_trigger',
];
const TEST_SEED_QUERY_KEYS = ['test_query', 'test_inbox_id', 'test_reason', 'test_source'];
const TEST_COMPARE_QUERY_KEYS = [
  'test_compare_bundle',
  'test_compare_title',
  'test_compare_focus_label',
  'test_compare_strategy_group_title',
  'test_compare_recommendation_code',
  'test_compare_owner_workspace',
  'test_compare_owner_action_title',
  'test_compare_guidance_editor',
  'test_compare_escalation_focus',
  'test_compare_hotspot_summary',
  'test_compare_resolved_summary',
  'test_compare_escalated_summary',
  'test_compare_validation_summary',
  'test_compare_stage',
];
const TEST_FOLLOWUP_QUERY_KEYS = [
  'test_followup_intent',
  'test_followup_target_version_label',
  'test_followup_compare_version_label',
  'test_followup_session_id',
  'test_followup_session_label',
  'test_followup_session_step',
  'test_followup_session_origin',
  'test_followup_session_item_key',
];
const ANALYZE_SEED_QUERY_KEYS = [
  'analyze_seed',
  'analyze_source',
  'analyze_time_window',
  'analyze_inbox_id',
  'analyze_content_gap_reason',
  'analyze_article_scope_warning',
  'analyze_delivery_mode',
];
const ANALYZE_FOLLOWUP_QUERY_KEYS = [
  'analyze_followup_intent',
  'analyze_followup_target_version_label',
  'analyze_followup_compare_version_label',
  'analyze_followup_session_id',
  'analyze_followup_session_label',
  'analyze_followup_session_step',
  'analyze_followup_session_origin',
  'analyze_followup_session_item_key',
];
const ROLLBACK_EXPLORER_QUERY_KEYS = [
  'rollback_session_search',
  'rollback_session_workspace',
  'rollback_session_hop',
  'rollback_session_key',
  'rollback_session_item_key',
];

const dialogType = ref('');
const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);
const connectInboxDialog = ref(null);
const configVersionsDialogRef = ref(null);

const isSavingReplyMode = ref(false);
const isSavingStatus = ref(false);
const isSavingHandoff = ref(false);
const isSavingInboxRuntime = ref(false);
const isSavingChannelStrategy = ref(false);
const isSavingBusinessHours = ref(false);
const isSavingReleaseReview = ref(false);
const isSavingRollout = ref(false);
const historyActionKey = ref('');
const expandedEvidenceHistoryKeys = ref({});
const expandedRollbackSessionKeys = ref({});
const rollbackSessionSearch = ref('');
const rollbackSessionWorkspaceFilter = ref('all');
const rollbackSessionHopFilter = ref('all');
const rollbackActionKey = ref('');
const selectedRolloutVersionId = ref(null);
const selectedRolloutInboxIds = ref([]);
const rolloutTrafficPercentage = ref(100);
const releaseSectionRef = ref(null);
const channelStrategySectionRef = ref(null);
const inboxControlsSectionRef = ref(null);
const businessHoursSectionRef = ref(null);
const regionComplianceSectionRef = ref(null);
const loadWarnings = ref({
  assistant: '',
  config: '',
  inboxes: '',
  versions: '',
  abReport: '',
});
const isLoadingVersionSummary = ref(false);
const isLoadingAbReport = ref(false);
const abReportDays = ref(7);
const abReport = ref({ versions: [] });
const configVersionSummary = ref({
  versions: [],
  inboxes: [],
  rollouts: {},
  rollout_details: [],
  publish_ledger: [],
  published_version_id: null,
  draft_version_id: null,
  release_readiness: {},
});

const assistantId = computed(() => props.assistant?.id);
const currentAssistant = computed(
  () => store.getters['ailabAssistants/getRecord']?.(Number(assistantId.value)) || props.assistant || {}
);
const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);
const aiCloserUiFlags = computed(() => store.getters['aiCloser/getUIFlags'] || {});
const isLoadingConfig = computed(() => aiCloserUiFlags.value.isFetchingConfig);
const uiFlags = useMapGetter('ailabInboxes/getUIFlags');
const isFetchingInboxes = computed(() => uiFlags.value.fetchingList);
const ailabInboxes = useMapGetter('ailabInboxes/getRecords');
const inboxUiFlags = computed(() => store.getters['inboxes/getUIFlags'] || {});
const agentUiFlags = computed(() => store.getters['agents/getUIFlags'] || {});
const isUpdatingInbox = computed(() => inboxUiFlags.value.isUpdating);
const isFetchingAgents = computed(() => agentUiFlags.value.isFetching);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const inboxById = computed(() => store.getters['inboxes/getInbox']);
const connectedInboxes = computed(() => ailabInboxes.value || []);

const isAIGamer = computed(
  () => props.assistant?.config?.wlab_sidebar_key === 'ai_gamer'
);
const replyMode = ref('auto_reply');
const handoffEnabled = ref(true);
const handoffMessage = ref('');
const handoffAssignStrategy = ref('inherit');
const handoffAssignAgentIds = ref([]);
const resolutionMessage = ref('');
const selectedInboxId = ref(null);
const autoResolveAfterMinutes = ref(60);
const aiOverrideMode = ref('inherit');
const aiOverrideReason = ref('');
const forcedReplyLanguage = ref('');
const communicationStyleMode = ref('');
const segmentRuntimeBindings = ref([]);
const businessHoursEnabled = ref(false);
const outsideBusinessHoursReplyMode = ref('suggest');
const outsideBusinessHoursResponseBehavior = ref('normal_ai');
const defaultComplianceProfile = ref('standard');
const regionComplianceBindings = ref([]);
const aiOverrideMeta = ref({
  setAt: '',
  setByUserId: null,
});
const releaseReviewNote = ref('');
const releaseReviewEntry = ref(null);

const runtimeStatus = computed(() => aiCloserConfig.value?.status || 'inactive');
const selectedInboxRecord = computed(
  () => inboxById.value?.(Number(selectedInboxId.value)) || {}
);
const deploySeedContext = computed(() => {
  const source = String(route.query?.deploy_seed_source || '').trim();
  const focus = String(route.query?.deploy_seed_focus || '').trim();
  const reason = String(route.query?.deploy_seed_reason || '').trim();
  const inboxId = String(route.query?.deploy_seed_inbox_id || '').trim();
  const confidenceLevel = String(
    route.query?.deploy_seed_confidence_level || ''
  ).trim();
  const confidenceRuns = String(route.query?.deploy_seed_confidence_runs || '').trim();
  const confidenceTitle = String(route.query?.deploy_seed_confidence_title || '').trim();
  const followupIntent = String(route.query?.deploy_seed_followup_intent || '').trim();
  const targetVersionLabel = String(
    route.query?.deploy_seed_target_version_label || ''
  ).trim();
  const compareVersionLabel = String(
    route.query?.deploy_seed_compare_version_label || ''
  ).trim();
  const prefillNote = String(route.query?.deploy_seed_prefill_note || '').trim();
  const openVersions = String(route.query?.deploy_seed_open_versions || '').trim() === '1';
  const sessionId = String(route.query?.deploy_seed_session_id || '').trim();
  const sessionLabel = String(route.query?.deploy_seed_session_label || '').trim();
  const sessionStep = String(route.query?.deploy_seed_session_step || '').trim();
  const sessionOrigin = String(route.query?.deploy_seed_session_origin || '').trim();
  const sessionItemKey = String(route.query?.deploy_seed_session_item_key || '').trim();
  const enabled =
    String(route.query?.deploy_seed || '').trim() === '1' ||
    [
      source,
      focus,
      reason,
      inboxId,
      confidenceLevel,
      confidenceRuns,
      confidenceTitle,
      followupIntent,
      targetVersionLabel,
      compareVersionLabel,
      prefillNote,
      sessionId,
      sessionLabel,
      sessionStep,
      sessionOrigin,
      sessionItemKey,
    ].some(Boolean);

  return {
    enabled,
    source,
    focus,
    reason,
    inboxId,
    confidenceLevel,
    confidenceRuns,
    confidenceTitle,
    followupIntent,
    targetVersionLabel,
    compareVersionLabel,
    prefillNote,
    openVersions,
    sessionId,
    sessionLabel,
    sessionStep,
    sessionOrigin,
    sessionItemKey,
  };
});
const seededReleaseBundle = computed(() => {
  const title = String(route.query?.deploy_seed_title || '').trim();
  const focusLabel = String(route.query?.deploy_seed_focus_label || '').trim();
  const strategyGroupTitle = String(
    route.query?.deploy_seed_strategy_group_title || ''
  ).trim();
  const recommendationCode = String(
    route.query?.deploy_seed_recommendation_code || ''
  ).trim();
  const ownerWorkspace = String(
    route.query?.deploy_seed_owner_workspace || ''
  ).trim();
  const ownerActionTitle = String(
    route.query?.deploy_seed_owner_action_title || ''
  ).trim();
  const guidanceEditor = String(
    route.query?.deploy_seed_guidance_editor || ''
  ).trim();
  const escalationFocus = String(
    route.query?.deploy_seed_escalation_focus || ''
  ).trim();
  const hotspotSummary = String(
    route.query?.deploy_seed_hotspot_summary || ''
  ).trim();
  const resolvedSummary = String(
    route.query?.deploy_seed_resolved_summary || ''
  ).trim();
  const escalatedSummary = String(
    route.query?.deploy_seed_escalated_summary || ''
  ).trim();
  const validationSummary = String(
    route.query?.deploy_seed_validation_summary || ''
  ).trim();
  const stage = String(route.query?.deploy_seed_stage || '').trim();
  const rollbackTrigger = String(
    route.query?.deploy_seed_rollback_trigger || ''
  ).trim();
  return {
    enabled:
      String(route.query?.deploy_seed_bundle || '').trim() === '1' ||
      Boolean(
        title ||
          hotspotSummary ||
          resolvedSummary ||
          escalatedSummary ||
          validationSummary
      ),
    title,
    focusLabel,
    strategyGroupTitle,
    recommendationCode,
    ownerWorkspace,
    ownerActionTitle,
    guidanceEditor,
    escalationFocus,
    hotspotSummary,
    resolvedSummary,
    escalatedSummary,
    validationSummary,
    stage,
    rollbackTrigger,
  };
});
const seededBatchBaselineContext = computed(() => ({
  baseline_key: String(route.query?.deploy_seed_batch_baseline_key || '').trim(),
  set_key: String(route.query?.deploy_seed_batch_set_key || '').trim(),
  title: String(route.query?.deploy_seed_batch_title || '').trim(),
  overall_level: String(route.query?.deploy_seed_batch_overall_level || '').trim(),
  total_cases: String(route.query?.deploy_seed_batch_total_cases || '').trim(),
  ready_cases: String(route.query?.deploy_seed_batch_ready_cases || '').trim(),
  watch_cases: String(route.query?.deploy_seed_batch_watch_cases || '').trim(),
  blocked_cases: String(route.query?.deploy_seed_batch_blocked_cases || '').trim(),
  ran_at: String(route.query?.deploy_seed_batch_ran_at || '').trim(),
  top_blocker: String(route.query?.deploy_seed_batch_top_blocker || '').trim(),
  top_watch_signal: String(route.query?.deploy_seed_batch_top_watch_signal || '').trim(),
}));
const deploySeedSummary = computed(() => {
  if (!deploySeedContext.value.enabled) return '';

  const parts = [];
  if (deploySeedContext.value.source) parts.push(`from ${deploySeedContext.value.source}`);
  if (deploySeedContext.value.focus === 'release') parts.push('release readiness');
  if (deploySeedContext.value.focus === 'channels') parts.push('channel strategy');
  if (deploySeedContext.value.focus === 'inbox_controls') parts.push('per-inbox runtime controls');
  if (deploySeedContext.value.focus === 'business_hours') parts.push('business-hours strategy');
  if (deploySeedContext.value.focus === 'region_compliance') parts.push('region compliance');
  if (deploySeedContext.value.inboxId) {
    parts.push(resolveInboxName(deploySeedContext.value.inboxId) || `Inbox ${deploySeedContext.value.inboxId}`);
  }
  if (deploySeedContext.value.confidenceLevel) {
    parts.push(`confidence ${deploySeedContext.value.confidenceLevel}`);
  }
  if (deploySeedContext.value.confidenceRuns) {
    parts.push(`${deploySeedContext.value.confidenceRuns} recent run${deploySeedContext.value.confidenceRuns === '1' ? '' : 's'}`);
  }
  if (deploySeedContext.value.followupIntent) parts.push(deploySeedContext.value.followupIntent);
  if (deploySeedContext.value.targetVersionLabel) {
    parts.push(
      deploySeedContext.value.compareVersionLabel
        ? `${deploySeedContext.value.targetVersionLabel} vs ${deploySeedContext.value.compareVersionLabel}`
        : deploySeedContext.value.targetVersionLabel
    );
  }
  if (deploySeedContext.value.reason) parts.push(deploySeedContext.value.reason);
  return parts.join(' • ');
});
const deploySeedPrimaryActionLabel = computed(() => {
  switch (deploySeedContext.value.focus) {
    case 'release':
      return 'Open release snapshot';
    case 'inbox_controls':
      return 'Open inbox controls';
    case 'business_hours':
      return 'Open business-hours strategy';
    case 'region_compliance':
      return 'Open region compliance';
    default:
      return 'Open channel strategy';
  }
});
const deploySeedReadyForRelease = computed(
  () => deploySeedContext.value.confidenceLevel === 'ready'
);
const deploySeedPrefillNote = computed(() =>
  String(deploySeedContext.value.prefillNote || '').trim()
);
const currentRollbackSessionContext = computed(() =>
  normalizeRollbackSessionContext({
    sessionId: deploySeedContext.value.sessionId,
    sessionLabel: deploySeedContext.value.sessionLabel,
    sessionStep: deploySeedContext.value.sessionStep,
    sessionOrigin: deploySeedContext.value.sessionOrigin,
  })
);
const currentRollbackSessionItemKey = computed(() =>
  String(selectedRollbackItemKey.value || deploySeedContext.value.sessionItemKey || '').trim()
);
const deploySeedBadges = computed(() =>
  buildRollbackBreadcrumbBadges({
    source: deploySeedContext.value.source,
    focus: deploySeedContext.value.focus,
    intentLabel: deploySeedContext.value.followupIntent,
    targetVersionLabel: deploySeedContext.value.targetVersionLabel,
    compareVersionLabel: deploySeedContext.value.compareVersionLabel,
    confidenceLabel:
      deploySeedContext.value.confidenceTitle || deploySeedContext.value.confidenceLevel,
    reason: deploySeedContext.value.reason,
    nextLabel: deploySeedContext.value.openVersions ? 'open versions' : '',
    sessionLabel: currentRollbackSessionContext.value.sessionLabel,
    sessionStep: currentRollbackSessionContext.value.sessionStep,
    sessionOrigin: currentRollbackSessionContext.value.sessionOrigin,
  })
);
const humanReleaseReviewStatus = status => {
  switch (String(status || '').trim()) {
    case 'reviewing':
      return 'Review open';
    case 'released':
      return 'Released';
    case 'rolled_back':
      return 'Rollback triggered';
    default:
      return 'Not captured';
  }
};
const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};
const buildEvidenceHistoryToggleKey = (kind, row) =>
  `${kind}:${row?.id || row?.snapshot_key || row?.baseline_key || row?.title || 'evidence'}`;
const toggleEvidenceHistory = (kind, row) => {
  const key = buildEvidenceHistoryToggleKey(kind, row);
  expandedEvidenceHistoryKeys.value = {
    ...expandedEvidenceHistoryKeys.value,
    [key]: !expandedEvidenceHistoryKeys.value[key],
  };
};
const evidenceHistoryExpanded = (kind, row) =>
  Boolean(expandedEvidenceHistoryKeys.value[buildEvidenceHistoryToggleKey(kind, row)]);
const evidenceHistoryTotalCount = row =>
  mergeOperatorNoteTimelineEntries({ records: [row], limit: 25 }).length;
const evidenceHistoryEntries = (kind, row) =>
  latestOperatorNoteTimelineEntries({
    record: row,
    limit: evidenceHistoryExpanded(kind, row) ? 6 : 2,
  });
const evidenceHistoryNeedsToggle = row => evidenceHistoryTotalCount(row) > 2;
const evidenceHistoryToggleLabel = (kind, row) =>
  evidenceHistoryExpanded(kind, row)
    ? 'Hide note trail'
    : `Show note trail${evidenceHistoryTotalCount(row) > 0 ? ` (${evidenceHistoryTotalCount(row)})` : ''}`;
const evidenceHistoryEntryKey = (entry, index = 0) =>
  `${entry?.created_at || 'no-time'}::${entry?.source || 'unknown'}::${entry?.body || index}`;
const evidenceHistoryEntryMeta = entry => {
  const parts = [];
  if (entry?.source) parts.push(`via ${entry.source}`);
  if (entry?.created_at) parts.push(formatDateTime(entry.created_at));
  const context = buildOperatorNoteTimelineContextLabel(entry);
  if (context) parts.push(context);
  return parts.join(' • ');
};
const evidenceHistoryEntryBadges = entry => buildOperatorNoteTimelineBadges(entry);
const rollbackSessionSources = computed(() => {
  const rows = [];
  const seen = new Set();
  const appendRecord = (kind, record) => {
    if (!record || typeof record !== 'object') return;
    const key = `${kind}:${record.id || record.snapshot_key || record.baseline_key || record.title || 'evidence'}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ kind, record });
  };
  appendRecord('release', releaseReviewEntry.value);
  appendRecord('baseline', activeBatchBaseline.value);
  recentReleaseSnapshots.value.forEach(record => appendRecord('release', record));
  recentBatchBaselines.value.forEach(record => appendRecord('baseline', record));
  return rows;
});
const normalizeRollbackExplorerToken = value =>
  String(value || '')
    .trim()
    .toLowerCase();
const rollbackOperatorSessions = computed(() =>
  groupRollbackSessionJourney({
    noteSources: rollbackSessionSources.value,
    ledgerEntries: publishLedgerEntries.value,
    limitSessions: 4,
    limitItemsPerSession: 25,
  })
);
const rollbackSessionWorkspaceOptions = computed(() => {
  const values = new Set();
  rollbackOperatorSessions.value.forEach(session => {
    (session?.items || []).forEach(item => {
      const value = normalizeRollbackExplorerToken(item?.workspaceLabel);
      if (value) values.add(value);
    });
  });
  return ['all', ...Array.from(values)];
});
const rollbackSessionHopOptions = computed(() => {
  const values = new Set();
  rollbackOperatorSessions.value.forEach(session => {
    (session?.items || []).forEach(item => {
      const value = normalizeRollbackExplorerToken(item?.hopKind);
      if (value) values.add(value);
    });
  });
  return ['all', ...Array.from(values)];
});
const rollbackSessionUrlKey = session =>
  session
    ? String(
        session?.sessionId || session?.sessionKey || buildRollbackSessionToggleKey(session)
      ).trim()
    : '';
const selectedRollbackSessionKey = computed(() =>
  String(route.query?.rollback_session_key || '').trim()
);
const selectedRollbackItemKey = computed(() =>
  String(route.query?.rollback_session_item_key || '').trim()
);
const selectedRollbackSession = computed(() => {
  const sessionKey = selectedRollbackSessionKey.value;
  const itemKey = selectedRollbackItemKey.value;
  if (itemKey) {
    const itemSession = rollbackOperatorSessions.value.find(session =>
      (session?.items || []).some(item => String(item?.itemKey || '').trim() === itemKey)
    );
    if (itemSession) return itemSession;
  }
  if (sessionKey) {
    const sessionMatch = rollbackOperatorSessions.value.find(
      session => rollbackSessionUrlKey(session) === sessionKey
    );
    if (sessionMatch) return sessionMatch;
  }
  if (!itemKey) return null;
  return null;
});
const selectedRollbackJourneyItem = computed(() => {
  const itemKey = selectedRollbackItemKey.value;
  if (!itemKey) return null;
  for (const session of rollbackOperatorSessions.value) {
    const match = (session?.items || []).find(item => String(item?.itemKey || '').trim() === itemKey);
    if (match) return match;
  }
  return null;
});
const findRollbackSessionForItem = item => {
  const itemKey = String(item?.itemKey || '').trim();
  if (!itemKey) return null;
  return (
    rollbackOperatorSessions.value.find(session =>
      (session?.items || []).some(entry => String(entry?.itemKey || '').trim() === itemKey)
    ) || null
  );
};
const rollbackSessionMatchesExplorer = session => {
  const search = normalizeRollbackExplorerToken(rollbackSessionSearch.value);
  const workspaceFilter = normalizeRollbackExplorerToken(
    rollbackSessionWorkspaceFilter.value
  );
  const hopFilter = normalizeRollbackExplorerToken(rollbackSessionHopFilter.value);
  const items = session?.items || [];
  const matchesWorkspace =
    workspaceFilter === 'all' ||
    items.some(
      item => normalizeRollbackExplorerToken(item?.workspaceLabel) === workspaceFilter
    );
  if (!matchesWorkspace) return false;
  const matchesHop =
    hopFilter === 'all' ||
    items.some(item => normalizeRollbackExplorerToken(item?.hopKind) === hopFilter);
  if (!matchesHop) return false;
  if (!search) return true;
  const haystack = [
    session?.sessionId,
    session?.sessionLabel,
    session?.sessionOrigin,
    ...(session?.evidenceRefs || []).map(ref => ref?.label),
    ...items.flatMap(item => [
      item?.actorActionLabel,
      item?.workspaceLabel,
      item?.summary,
      item?.targetVersionLabel,
      item?.compareVersionLabel,
      item?.row?.bundle_title,
      item?.row?.focus_label,
      item?.entry?.body,
    ]),
  ]
    .map(normalizeRollbackExplorerToken)
    .filter(Boolean)
    .join(' ');
  return haystack.includes(search);
};
const filteredRollbackOperatorSessions = computed(() => {
  const rows = rollbackOperatorSessions.value.filter(rollbackSessionMatchesExplorer);
  const selected = selectedRollbackSession.value;
  if (
    selected &&
    !rows.some(session => rollbackSessionUrlKey(session) === rollbackSessionUrlKey(selected))
  ) {
    return [selected, ...rows];
  }
  return rows;
});
const rollbackSessionExplorerFiltered = computed(
  () =>
    normalizeRollbackExplorerToken(rollbackSessionSearch.value) ||
    normalizeRollbackExplorerToken(rollbackSessionWorkspaceFilter.value) !== 'all' ||
    normalizeRollbackExplorerToken(rollbackSessionHopFilter.value) !== 'all'
);
const clearRollbackSessionExplorerFilters = () => {
  rollbackSessionSearch.value = '';
  rollbackSessionWorkspaceFilter.value = 'all';
  rollbackSessionHopFilter.value = 'all';
};
const buildRollbackExplorerDeepLinkQuery = ({ session = null, item = null } = {}) => {
  const nextQuery = { ...(route.query || {}) };
  const sessionKey = rollbackSessionUrlKey(session);
  const itemKey = String(item?.itemKey || '').trim();
  if (sessionKey) {
    nextQuery.rollback_session_key = sessionKey;
  } else {
    delete nextQuery.rollback_session_key;
  }
  if (itemKey) {
    nextQuery.rollback_session_item_key = itemKey;
  } else {
    delete nextQuery.rollback_session_item_key;
  }
  return nextQuery;
};
const buildRollbackExplorerDeepLinkUrl = ({ session = null, item = null } = {}) => {
  const resolved = router.resolve({
    path: route.path,
    query: buildRollbackExplorerDeepLinkQuery({ session, item }),
  });
  const href = String(resolved?.href || '').trim();
  if (!href) return '';
  if (/^https?:\/\//i.test(href)) return href;
  const origin = String(globalThis?.location?.origin || '').trim();
  if (!origin) return href;
  return href.startsWith('/') ? `${origin}${href}` : `${origin}/${href.replace(/^\/+/, '')}`;
};
const writeTextToClipboard = async text => {
  const normalized = String(text || '').trim();
  if (!normalized) return false;
  if (globalThis?.navigator?.clipboard?.writeText) {
    await globalThis.navigator.clipboard.writeText(normalized);
    return true;
  }
  const doc = globalThis?.document;
  if (!doc?.createElement || !doc?.body?.appendChild) return false;
  const textarea = doc.createElement('textarea');
  textarea.value = normalized;
  textarea.setAttribute('readonly', 'readonly');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  doc.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try {
    copied = Boolean(doc.execCommand?.('copy'));
  } finally {
    textarea.remove();
  }
  return copied;
};
const copyRollbackExplorerDeepLink = async ({
  session = null,
  item = null,
  successMessage = 'Rollback link copied',
} = {}) => {
  const url = buildRollbackExplorerDeepLinkUrl({ session, item });
  if (!url) {
    alert('Could not build a rollback deep link yet');
    return;
  }
  try {
    const copied = await writeTextToClipboard(url);
    if (!copied) {
      alert('Clipboard copy is not available in this browser session');
      return;
    }
    alert(successMessage);
  } catch (error) {
    alert(error?.message || 'Failed to copy rollback deep link');
  }
};
const copyRollbackSessionDeepLink = async session => {
  await copyRollbackExplorerDeepLink({
    session,
    successMessage: 'Rollback session link copied',
  });
};
const copyRollbackJourneyItemDeepLink = async (session, item) => {
  await copyRollbackExplorerDeepLink({
    session,
    item,
    successMessage: 'Rollback step link copied',
  });
};
const rollbackExplorerCurrentShareTarget = computed(() => {
  const selectedItem = selectedRollbackJourneyItem.value;
  if (selectedItem) {
    const session = findRollbackSessionForItem(selectedItem) || selectedRollbackSession.value;
    return {
      kind: 'step',
      session,
      item: selectedItem,
      title: 'Current target: selected rollback step',
      detail:
        selectedItem.actorActionLabel ||
        selectedItem.summary ||
        selectedItem.workspaceLabel ||
        'Selected rollback step',
      successMessage: 'Current rollback step link copied',
    };
  }
  const selectedSession = selectedRollbackSession.value;
  if (selectedSession) {
    return {
      kind: 'session',
      session: selectedSession,
      item: null,
      title: 'Current target: selected rollback session',
      detail:
        selectedSession.sessionLabel ||
        selectedSession.latestItem?.actorActionLabel ||
        'Selected rollback session',
      successMessage: 'Current rollback session link copied',
    };
  }
  return {
    kind: 'view',
    session: null,
    item: null,
    title: 'Current target: rollback explorer view',
    detail: rollbackSessionExplorerFiltered.value
      ? 'Filtered rollback explorer view'
      : 'Current rollback explorer view',
    successMessage: 'Current rollback explorer link copied',
  };
});
const rollbackExplorerCurrentShareSummary = computed(() => {
  const target = rollbackExplorerCurrentShareTarget.value;
  return [target?.title, target?.detail].filter(Boolean).join(' - ');
});
const rollbackExplorerFilterSummary = computed(() => {
  const parts = [];
  const search = String(rollbackSessionSearch.value || '').trim();
  const workspace = String(rollbackSessionWorkspaceFilter.value || 'all').trim() || 'all';
  const hop = String(rollbackSessionHopFilter.value || 'all').trim() || 'all';
  if (search) parts.push(`search "${search}"`);
  if (workspace !== 'all') {
    parts.push(`workspace ${humanRollbackSessionExplorerWorkspaceLabel(workspace)}`);
  }
  if (hop !== 'all') {
    parts.push(`hop ${humanRollbackSessionExplorerHopLabel(hop)}`);
  }
  return parts.join(' • ');
});
const inferRollbackHandoffDecisionLabel = ({ item = null, session = null, kind = '' } = {}) => {
  const hopKind = String(item?.hopKind || session?.latestItem?.hopKind || '').trim();
  if (hopKind === 'rollback') return 'Rollback follow-through';
  if (hopKind === 'publish' || hopKind === 'initial_publish' || hopKind === 'rollout_publish') {
    return 'Live publish move';
  }
  if (hopKind === 'clear_rollouts') return 'Rollout cleanup';
  if (hopKind === 'evidence_note') return 'Evidence review';
  if (kind === 'session') return 'Rollback session review';
  if (kind === 'view') {
    return rollbackSessionExplorerFiltered.value ? 'Filtered rollback replay' : 'Rollback replay';
  }
  return 'Operator handoff';
};
const inferRollbackHandoffConfidenceLabel = ({ item = null, session = null, badges = [] } = {}) => {
  const explicit = String(
    item?.entry?.confidence_label ||
      item?.entry?.confidenceLabel ||
      session?.latestItem?.entry?.confidence_label ||
      session?.latestItem?.entry?.confidenceLabel ||
      ''
  ).trim();
  if (explicit) return explicit;
  return (
    badges
      .map(badge => String(badge?.label || '').trim())
      .find(label => /confidence/i.test(label)) || ''
  );
};
const inferRollbackHandoffRiskLabel = ({
  item = null,
  session = null,
  summary = '',
  why = '',
  next = '',
} = {}) => {
  const combined = [
    item?.summary,
    item?.actorActionLabel,
    session?.latestItem?.summary,
    summary,
    why,
    next,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (/risky|risk|blocked|rollback/.test(combined)) return 'Risk-bearing follow-through';
  if (/watch|waiting/.test(combined)) return 'Watch closely';
  if (/ready|released|publish/.test(combined)) return 'Publishing context';
  if (String(item?.hopKind || '').trim() === 'evidence_note') return 'Evidence review in progress';
  return 'Needs operator review';
};
const normalizeRollbackOutcomeMatchToken = value =>
  String(value || '')
    .trim()
    .toLowerCase();
const buildRollbackHandoffOutcomeCandidate = item => {
  if (!item) return null;
  const action = buildRollbackJourneyItemAction(item || {});
  const snapshot = rollbackOperatorSessionSnapshotRecord(item);
  const baseline = rollbackOperatorSessionBaselineRecord(item);
  return {
    itemKey: String(item?.itemKey || '').trim(),
    releaseSnapshotId: Number(snapshot?.id || action?.release_snapshot_id || 0),
    releaseSnapshotKey: normalizeRollbackOutcomeMatchToken(
      snapshot?.snapshot_key || action?.release_snapshot_key
    ),
    batchBaselineId: Number(baseline?.id || action?.batch_baseline_id || 0),
    batchBaselineKey: normalizeRollbackOutcomeMatchToken(
      baseline?.baseline_key || action?.batch_baseline_key
    ),
    targetVersionId: Number(item?.row?.target_version_id || action?.target_version_id || 0),
    targetVersionLabel: normalizeRollbackOutcomeMatchToken(
      action?.target_version_label || item?.targetVersionLabel
    ),
    previousVersionLabel: normalizeRollbackOutcomeMatchToken(
      action?.previous_version_label || item?.compareVersionLabel
    ),
    bundleLabel: normalizeRollbackOutcomeMatchToken(
      action?.bundle_label || action?.bundle_title || action?.focus_label
    ),
  };
};
const buildRollbackHandoffOutcomeCandidates = ({ session = null, item = null } = {}) => {
  const items = item ? [item] : session?.items || [];
  return items
    .map(buildRollbackHandoffOutcomeCandidate)
    .filter(candidate =>
      candidate &&
      (
        candidate.releaseSnapshotId > 0 ||
        candidate.releaseSnapshotKey ||
        candidate.batchBaselineId > 0 ||
        candidate.batchBaselineKey ||
        candidate.targetVersionId > 0 ||
        candidate.bundleLabel ||
        candidate.targetVersionLabel
      )
    );
};
const scoreRollbackHandoffOutcomeMatch = (row, candidate) => {
  if (!row || !candidate) return { score: 0, reason: '' };
  const releaseSnapshotKey = normalizeRollbackOutcomeMatchToken(row?.release_snapshot_key);
  const batchBaselineKey = normalizeRollbackOutcomeMatchToken(row?.batch_baseline_key);
  const bundleLabel = normalizeRollbackOutcomeMatchToken(
    row?.bundle_label || row?.bundle_title || row?.focus_label
  );
  const targetVersionLabel = normalizeRollbackOutcomeMatchToken(row?.target_version_label);
  const previousVersionLabel = normalizeRollbackOutcomeMatchToken(row?.previous_version_label);
  if (candidate.releaseSnapshotKey && releaseSnapshotKey === candidate.releaseSnapshotKey) {
    return { score: 120, reason: 'release snapshot' };
  }
  if (candidate.releaseSnapshotId > 0 && Number(row?.release_snapshot_id || 0) === candidate.releaseSnapshotId) {
    return { score: 115, reason: 'release snapshot' };
  }
  if (candidate.batchBaselineKey && batchBaselineKey === candidate.batchBaselineKey) {
    return { score: 110, reason: 'batch baseline' };
  }
  if (candidate.batchBaselineId > 0 && Number(row?.batch_baseline_id || 0) === candidate.batchBaselineId) {
    return { score: 105, reason: 'batch baseline' };
  }
  let score = 0;
  let reason = '';
  if (candidate.targetVersionId > 0 && Number(row?.target_version_id || 0) === candidate.targetVersionId) {
    score += 40;
    reason ||= 'target version';
  }
  if (candidate.bundleLabel && bundleLabel === candidate.bundleLabel) {
    score += 18;
    reason ||= 'rollback bundle';
  }
  if (candidate.targetVersionLabel && targetVersionLabel === candidate.targetVersionLabel) {
    score += 12;
    reason ||= 'target label';
  }
  if (candidate.previousVersionLabel && previousVersionLabel === candidate.previousVersionLabel) {
    score += 8;
    reason ||= 'compare label';
  }
  return { score, reason };
};
const resolveRollbackHandoffOutcome = ({ session = null, item = null, kind = '' } = {}) => {
  const rows = [];
  if (latestRollbackOutcome.value) rows.push(latestRollbackOutcome.value);
  if (Array.isArray(bundleRollbackOutcomes.value)) {
    bundleRollbackOutcomes.value.forEach(row => {
      if (row) rows.push(row);
    });
  }
  const candidates = buildRollbackHandoffOutcomeCandidates({ session, item });
  let best = null;
  rows.forEach(row => {
    candidates.forEach(candidate => {
      const match = scoreRollbackHandoffOutcomeMatch(row, candidate);
      if (!best || match.score > best.score) {
        best = {
          row,
          score: match.score,
          reason: match.reason,
        };
      }
    });
  });
  if (best?.score > 0) {
    return {
      row: best.row,
      sourceLabel: `Matched live rollback outcome by ${best.reason}`,
    };
  }
  if (kind === 'view' && !session && !item && latestRollbackOutcome.value) {
    return {
      row: latestRollbackOutcome.value,
      sourceLabel: 'Latest live rollback outcome',
    };
  }
  return null;
};
const buildRollbackOutcomeDecisionLabel = row => {
  const operatorGroup = String(row?.operator_group || '').trim();
  const status = String(row?.status || '').trim();
  if (operatorGroup === 'helped') return 'Rollback helped';
  if (operatorGroup === 'risky') return 'Rollback still risky';
  if (status === 'partial') return 'Rollback partially improved';
  if (status === 'waiting') return 'Rollback awaiting evidence';
  if (status === 'watch') return 'Rollback needs watch';
  return 'Rollback steady';
};
const buildRollbackOutcomeConfidenceLabel = row =>
  String(row?.confidence_label || '').trim() ||
  (row?.confidence_level ? humanRollbackConfidenceLabel(row.confidence_level) : '');
const buildRollbackOutcomeRiskLabel = row => {
  const riskySignals = Array.isArray(row?.risky_signals) ? row.risky_signals : [];
  const helpedSignals = Array.isArray(row?.helped_signals) ? row.helped_signals : [];
  const evidenceGapCount = Number(row?.evidence_gap_count || 0);
  const operatorGroup = String(row?.operator_group || '').trim();
  const status = String(row?.status || '').trim();
  if (operatorGroup === 'risky' && riskySignals.length) {
    return `Still risky on ${riskySignals.slice(0, 2).join(', ')}`;
  }
  if (operatorGroup === 'risky') return 'Still risky after rollback';
  if (status === 'waiting') return 'Waiting for more live evidence';
  if (evidenceGapCount > 0) {
    return `${evidenceGapCount} evidence gap${evidenceGapCount === 1 ? '' : 's'}`;
  }
  if (operatorGroup === 'helped' && helpedSignals.length) {
    return `Improved on ${helpedSignals.slice(0, 2).join(', ')}`;
  }
  if (operatorGroup === 'helped') return 'Risk reduced after rollback';
  if (status === 'watch' || status === 'partial') return 'Mixed live signals';
  return 'No major live regressions surfaced';
};
const buildRollbackOutcomeWhy = row => {
  const parts = [];
  if (row?.summary) parts.push(row.summary);
  if (row?.operator_note) parts.push(row.operator_note);
  if (Array.isArray(row?.risky_signals) && row.risky_signals.length) {
    parts.push(`Still risky on ${row.risky_signals.slice(0, 3).join(', ')}`);
  } else if (Array.isArray(row?.helped_signals) && row.helped_signals.length) {
    parts.push(`Helped by ${row.helped_signals.slice(0, 3).join(', ')}`);
  }
  return parts.filter(Boolean).join(' • ');
};
const buildRollbackOutcomeNext = row =>
  String(
    row?.recommended_action_label ||
      row?.operator_actions?.[0]?.intent_label ||
      row?.operator_actions?.[0]?.label ||
      ''
  ).trim();
const buildRollbackOutcomeTone = row => {
  const operatorGroup = String(row?.operator_group || '').trim();
  const status = String(row?.status || '').trim();
  if (operatorGroup === 'risky') return 'danger';
  if (operatorGroup === 'helped') return 'success';
  if (status === 'waiting' || status === 'watch' || status === 'partial') return 'warning';
  return 'primary';
};
const buildRollbackConfidenceTone = row => {
  const level = String(row?.confidence_level || '').trim();
  if (level === 'high') return 'success';
  if (level === 'medium') return 'primary';
  if (level === 'low') return 'warning';
  return 'muted';
};
const buildRollbackRiskTone = row => {
  const operatorGroup = String(row?.operator_group || '').trim();
  const status = String(row?.status || '').trim();
  const evidenceGapCount = Number(row?.evidence_gap_count || 0);
  if (operatorGroup === 'risky') return 'danger';
  if (status === 'waiting' || status === 'watch' || status === 'partial' || evidenceGapCount > 0) {
    return 'warning';
  }
  if (operatorGroup === 'helped') return 'success';
  return 'primary';
};
const rollbackHandoffTagToneClass = tone => ({
  'deploy-tag--muted': tone === 'muted',
  'deploy-tag--success': tone === 'success',
  'deploy-tag--warning': tone === 'warning',
  'deploy-tag--danger': tone === 'danger',
});
const rollbackHandoffKeylineToneClass = tone => ({
  'deploy-handoff-keyline--success': tone === 'success',
  'deploy-handoff-keyline--warning': tone === 'warning',
  'deploy-handoff-keyline--danger': tone === 'danger',
});
const buildRollbackExplorerHandoffData = ({
  session = null,
  item = null,
  title = '',
  detail = '',
  kind = '',
} = {}) => {
  const url = buildRollbackExplorerDeepLinkUrl({ session, item });
  if (!url) return null;
  const targetLabel = String(title || '').replace(/^Current target:\s*/i, '').trim();
  const sessionLabel = String(session?.sessionLabel || session?.sessionId || '').trim();
  const stepContext = [
    item?.workspaceLabel,
    item?.hopKind ? humanizeRollbackJourneyHopKind(item.hopKind) : '',
  ]
    .filter(Boolean)
    .join(' • ');
  const baseWhy = String(
    item?.summary ||
      item?.entry?.body ||
      item?.actorActionLabel ||
      detail ||
      (session?.latestItem?.summary || session?.latestItem?.actorActionLabel || '')
  ).trim();
  const baseNext = String(
    item?.actorActionLabel ||
      session?.latestItem?.actorActionLabel ||
      (item?.workspaceLabel ? `Re-open ${item.workspaceLabel}` : '') ||
      (session?.latestItem?.workspaceLabel
        ? `Review latest ${session.latestItem.workspaceLabel} hop`
        : '') ||
      'Review the linked rollback flow'
  ).trim();
  const baseBadges = item
    ? buildRollbackSessionJourneyItemBadges(item || null)
    : buildRollbackSessionJourneyItemBadges(session?.latestItem || null);
  const outcomeMatch = resolveRollbackHandoffOutcome({ session, item, kind });
  const matchedOutcome = outcomeMatch?.row || null;
  const summary = String(matchedOutcome?.summary || detail || '').trim();
  const why =
    buildRollbackOutcomeWhy(matchedOutcome) ||
    baseWhy;
  const next =
    buildRollbackOutcomeNext(matchedOutcome) ||
    baseNext;
  const decisionLabel = matchedOutcome
    ? buildRollbackOutcomeDecisionLabel(matchedOutcome)
    : inferRollbackHandoffDecisionLabel({
        item,
        session,
        kind,
      });
  const confidenceLabel = matchedOutcome
    ? buildRollbackOutcomeConfidenceLabel(matchedOutcome)
    : inferRollbackHandoffConfidenceLabel({
        item,
        session,
        badges: baseBadges,
      });
  const riskLabel = matchedOutcome
    ? buildRollbackOutcomeRiskLabel(matchedOutcome)
    : inferRollbackHandoffRiskLabel({
        item,
        session,
        summary: detail,
        why,
        next,
      });
  const decisionTone = matchedOutcome ? buildRollbackOutcomeTone(matchedOutcome) : 'primary';
  const confidenceTone = matchedOutcome ? buildRollbackConfidenceTone(matchedOutcome) : 'muted';
  const riskTone = matchedOutcome ? buildRollbackRiskTone(matchedOutcome) : 'warning';
  const badges = [
    ...(matchedOutcome
      ? [
          {
            label: humanRollbackOutcomeStatusLabel(matchedOutcome.status),
            tone: decisionTone,
          },
          {
            label: humanRollbackOperatorGroupLabel(matchedOutcome.operator_group),
            tone: riskTone,
          },
          outcomeMatch?.sourceLabel
            ? {
                label: outcomeMatch.sourceLabel,
                tone: 'muted',
              }
            : null,
        ].filter(Boolean)
      : []),
    ...baseBadges,
  ];
  const keylineTone =
    riskTone === 'danger'
      ? 'danger'
      : riskTone === 'warning'
        ? 'warning'
        : decisionTone;
  const fallbackRiskLabel = inferRollbackHandoffRiskLabel({
    item,
    session,
    summary: detail,
    why: baseWhy,
    next: baseNext,
  });
  return {
    heading: 'Rollback explorer handoff',
    targetLabel,
    summary,
    sessionLabel,
    stepContext,
    why,
    next,
    decisionLabel,
    confidenceLabel,
    riskLabel: riskLabel || fallbackRiskLabel,
    decisionTone,
    confidenceTone,
    riskTone,
    keylineTone,
    liveOutcomeLabel: outcomeMatch?.sourceLabel || '',
    badges: badges.slice(0, 4),
    filters: rollbackExplorerFilterSummary.value,
    link: url,
  };
};
const buildRollbackExplorerHandoffText = ({
  session = null,
  item = null,
  title = '',
  detail = '',
  kind = '',
} = {}) => {
  const card = buildRollbackExplorerHandoffData({
    session,
    item,
    title,
    detail,
    kind,
  });
  if (!card) return '';
  const lines = [card.heading];
  if (card.targetLabel) lines.push(`Target: ${card.targetLabel}`);
  if (card.summary) lines.push(`Summary: ${card.summary}`);
  if (card.decisionLabel) lines.push(`Decision: ${card.decisionLabel}`);
  if (card.confidenceLabel) lines.push(`Confidence: ${card.confidenceLabel}`);
  if (card.riskLabel) lines.push(`Risk posture: ${card.riskLabel}`);
  if (card.liveOutcomeLabel) lines.push(`Live outcome: ${card.liveOutcomeLabel}`);
  if (card.why) lines.push(`Why this matters: ${card.why}`);
  if (card.next) lines.push(`Suggested next step: ${card.next}`);
  if (card.sessionLabel) lines.push(`Session: ${card.sessionLabel}`);
  if (card.stepContext) lines.push(`Step context: ${card.stepContext}`);
  if (card.filters) lines.push(`Explorer filters: ${card.filters}`);
  lines.push(`Link: ${card.link}`);
  return lines.join('\n');
};
const rollbackExplorerCurrentHandoffCard = computed(() => {
  const target = rollbackExplorerCurrentShareTarget.value;
  return buildRollbackExplorerHandoffData({
    session: target?.session || null,
    item: target?.item || null,
    title: target?.title || '',
    detail: target?.detail || '',
    kind: target?.kind || '',
  });
});
const rollbackExplorerCurrentHandoffPreview = computed(() => {
  const card = rollbackExplorerCurrentHandoffCard.value;
  if (!card) return '';
  return buildRollbackExplorerHandoffText({
    session: rollbackExplorerCurrentShareTarget.value?.session || null,
    item: rollbackExplorerCurrentShareTarget.value?.item || null,
    title: rollbackExplorerCurrentShareTarget.value?.title || '',
    detail: rollbackExplorerCurrentShareTarget.value?.detail || '',
    kind: rollbackExplorerCurrentShareTarget.value?.kind || '',
  });
});
const copyCurrentRollbackExplorerDeepLink = async () => {
  const target = rollbackExplorerCurrentShareTarget.value;
  await copyRollbackExplorerDeepLink({
    session: target?.session || null,
    item: target?.item || null,
    successMessage: target?.successMessage || 'Rollback link copied',
  });
};
const copyCurrentRollbackExplorerHandoffNote = async () => {
  const handoffText = rollbackExplorerCurrentHandoffPreview.value;
  if (!handoffText) {
    alert('Could not build a rollback handoff note yet');
    return;
  }
  try {
    const copied = await writeTextToClipboard(handoffText);
    if (!copied) {
      alert('Clipboard copy is not available in this browser session');
      return;
    }
    alert('Rollback handoff note copied');
  } catch (error) {
    alert(error?.message || 'Failed to copy rollback handoff note');
  }
};
const selectRollbackSession = async (session, { item = null } = {}) => {
  const key = rollbackSessionUrlKey(session);
  const itemKey = String(item?.itemKey || '').trim();
  if (String(route.query?.rollback_session_key || '').trim() === String(key || '').trim()) {
    if (String(route.query?.rollback_session_item_key || '').trim() === itemKey) return;
  }
  const nextQuery = { ...(route.query || {}) };
  if (key) {
    nextQuery.rollback_session_key = key;
  } else {
    delete nextQuery.rollback_session_key;
  }
  if (itemKey) {
    nextQuery.rollback_session_item_key = itemKey;
  } else {
    delete nextQuery.rollback_session_item_key;
  }
  await router.replace({ query: nextQuery });
};
const rollbackSessionSelected = session =>
  rollbackSessionUrlKey(session) &&
  rollbackSessionUrlKey(session) === rollbackSessionUrlKey(selectedRollbackSession.value);
const rollbackJourneyItemSelected = item =>
  String(item?.itemKey || '').trim() &&
  String(item?.itemKey || '').trim() === selectedRollbackItemKey.value;
const buildRollbackSessionToggleKey = session =>
  session?.sessionId ||
  session?.sessionKey ||
  session?.latestItem?.itemKey ||
  session?.sessionLabel ||
  'rollback-session';
const buildRollbackSessionDomId = session =>
  `rollback-session-${rollbackSessionUrlKey(session).replace(/[^a-zA-Z0-9_-]+/g, '-') || 'selected'}`;
const buildRollbackJourneyItemDomId = item =>
  `rollback-session-item-${String(item?.itemKey || '').replace(/[^a-zA-Z0-9_-]+/g, '-') || 'selected'}`;
const syncRollbackSessionExplorerFromQuery = () => {
  rollbackSessionSearch.value = String(route.query?.rollback_session_search || '').trim();
  rollbackSessionWorkspaceFilter.value = String(
    route.query?.rollback_session_workspace || 'all'
  ).trim() || 'all';
  rollbackSessionHopFilter.value = String(route.query?.rollback_session_hop || 'all').trim() || 'all';
};
const syncRollbackSessionExplorerToQuery = async () => {
  const nextQuery = { ...(route.query || {}) };
  const normalizedSearch = String(rollbackSessionSearch.value || '').trim();
  const normalizedWorkspace = String(rollbackSessionWorkspaceFilter.value || 'all').trim() || 'all';
  const normalizedHop = String(rollbackSessionHopFilter.value || 'all').trim() || 'all';
  if (normalizedSearch) {
    nextQuery.rollback_session_search = normalizedSearch;
  } else {
    delete nextQuery.rollback_session_search;
  }
  if (normalizedWorkspace !== 'all') {
    nextQuery.rollback_session_workspace = normalizedWorkspace;
  } else {
    delete nextQuery.rollback_session_workspace;
  }
  if (normalizedHop !== 'all') {
    nextQuery.rollback_session_hop = normalizedHop;
  } else {
    delete nextQuery.rollback_session_hop;
  }
  if (
    String(route.query?.rollback_session_search || '').trim() ===
      String(nextQuery.rollback_session_search || '').trim() &&
    String(route.query?.rollback_session_workspace || '').trim() ===
      String(nextQuery.rollback_session_workspace || '').trim() &&
    String(route.query?.rollback_session_hop || '').trim() ===
      String(nextQuery.rollback_session_hop || '').trim()
  ) {
    return;
  }
  await router.replace({ query: nextQuery });
};
const focusSelectedRollbackSession = async () => {
  const session = selectedRollbackSession.value;
  if (!session) return;
  const toggleKey = buildRollbackSessionToggleKey(session);
  expandedRollbackSessionKeys.value = {
    ...expandedRollbackSessionKeys.value,
    [toggleKey]: true,
  };
  await nextTick();
  const item = selectedRollbackJourneyItem.value;
  if (item) {
    const itemEl = globalThis?.document?.getElementById?.(buildRollbackJourneyItemDomId(item));
    itemEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    return;
  }
  const el = globalThis?.document?.getElementById?.(buildRollbackSessionDomId(session));
  el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};
const rollbackSessionExpanded = session =>
  Boolean(expandedRollbackSessionKeys.value[buildRollbackSessionToggleKey(session)]);
const toggleRollbackSession = session => {
  const key = buildRollbackSessionToggleKey(session);
  expandedRollbackSessionKeys.value = {
    ...expandedRollbackSessionKeys.value,
    [key]: !expandedRollbackSessionKeys.value[key],
  };
};
const rollbackOperatorSessionEntries = session =>
  rollbackSessionExpanded(session)
    ? session?.items || []
    : (session?.items || []).slice(0, 3);
const rollbackOperatorSessionNeedsToggle = session =>
  Number(session?.totalItems || 0) > 3;
const rollbackOperatorSessionToggleLabel = session =>
  rollbackSessionExpanded(session)
    ? 'Hide session trail'
    : `Show session trail${Number(session?.totalItems || 0) ? ` (${session.totalItems})` : ''}`;
const rollbackOperatorSessionHiddenCount = session =>
  Math.max(
    Number(session?.totalItems || 0) - rollbackOperatorSessionEntries(session).length,
    0
  );
const rollbackOperatorSessionHeaderBadges = session =>
  buildRollbackSessionJourneyItemBadges(session?.latestItem || null);
const rollbackOperatorSessionMeta = session => {
  const parts = [];
  if (session?.latestCreatedAt) parts.push(`latest ${formatDateTime(session.latestCreatedAt)}`);
  if (session?.firstCreatedAt && session.firstCreatedAt !== session.latestCreatedAt) {
    parts.push(`started ${formatDateTime(session.firstCreatedAt)}`);
  }
  const summary = buildRollbackSessionJourneySummary(session);
  if (summary) parts.push(summary);
  return parts.join(' • ');
};
const rollbackOperatorSessionStepKey = (session, item, index = 0) =>
  `${buildRollbackSessionToggleKey(session)}::${item?.itemKey || item?.createdAt || index}`;
const rollbackOperatorSessionStepMeta = item => {
  const parts = [];
  const step = Number(item?.step || item?.session?.sessionStep || 0);
  if (step > 0) parts.push(`step ${step}`);
  if (item?.workspaceLabel) parts.push(`via ${item.workspaceLabel}`);
  if (item?.createdAt) parts.push(formatDateTime(item.createdAt));
  if (Array.isArray(item?.evidenceRefs) && item.evidenceRefs.length) {
    parts.push(item.evidenceRefs.map(ref => ref.label).join(' + '));
  }
  if (item?.itemType === 'ledger_hop') {
    const areas = publishLedgerChangedAreasSummary(item?.row);
    if (areas) parts.push(`areas ${areas}`);
  } else {
    const context = buildOperatorNoteTimelineContextLabel(item?.entry);
    if (context) parts.push(context);
  }
  return parts.join(' • ');
};
const rollbackOperatorSessionStepBadges = item =>
  buildRollbackSessionJourneyItemBadges(item || null);
const humanRollbackSessionExplorerWorkspaceLabel = value => {
  const normalized = String(value || '').trim();
  if (!normalized) return 'All workspaces';
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
};
const humanRollbackSessionExplorerHopLabel = value => {
  if (String(value || '').trim() === 'all') return 'All hop kinds';
  return humanizeRollbackJourneyHopKind(value) || 'Unknown hop';
};
const rollbackOperatorSessionSnapshotRecord = item =>
  item?.itemType === 'ledger_hop'
    ? findRollbackActionSnapshot(item?.row || {})
    : item?.evidenceRefs?.find(ref => ref.recordKind === 'release')?.record || null;
const rollbackOperatorSessionBaselineRecord = item =>
  item?.itemType === 'ledger_hop'
    ? findRollbackActionBaseline(item?.row || {})
    : item?.evidenceRefs?.find(ref => ref.recordKind === 'baseline')?.record || null;
const openPublishLedgerVersionHistory = row => {
  const snapshot = findRollbackActionSnapshot(row);
  const baseline = findRollbackActionBaseline(row);
  const ledgerEntry = {
    source: row?.source,
    target_version_label: publishLedgerVersionLabel(
      row?.target_version_id,
      row?.target_version_number
    ),
    compare_version_label:
      Number(row?.previous_published_version_id || 0) > 0 ||
      Number(row?.previous_published_version_number || 0) > 0
        ? publishLedgerVersionLabel(
            row?.previous_published_version_id,
            row?.previous_published_version_number
          )
        : '',
    recommended_action_label: humanPublishLedgerEventLabel(row?.event_type),
    session_id: row?.session_id,
    session_label: row?.session_label,
    session_step: row?.session_step,
    session_origin: row?.session_origin,
    next_workspace: row?.next_workspace,
  };
  if (snapshot) {
    openReleaseEvidenceVersionHistory(snapshot, ledgerEntry);
    return;
  }
  if (baseline) {
    openBatchEvidenceVersionHistory(baseline, ledgerEntry);
  }
};
const buildRollbackJourneyActionPayload = item => {
  const action = buildRollbackJourneyItemAction(item || {});
  if (action.kind === 'evidence_note') {
    action.kind = 'open_release_review';
  }
  return action;
};
const seedRollbackJourneyInDeploy = async item => {
  const session = rollbackOperatorSessions.value.find(candidate =>
    (candidate?.items || []).some(entry => entry.itemKey === item?.itemKey)
  );
  if (session) await selectRollbackSession(session, { item });
  const payload = buildRollbackJourneyActionPayload(item);
  const snapshot = rollbackOperatorSessionSnapshotRecord(item);
  const baseline = rollbackOperatorSessionBaselineRecord(item);
  await applyRollbackActionSeed(payload);
  if (snapshot) {
    await focusReleaseSnapshotHistory(snapshot);
    return;
  }
  if (baseline) {
    await focusBatchBaselineHistory(baseline);
    return;
  }
  releaseSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};
const openRollbackJourneyInAnalyze = async item => {
  const session = rollbackOperatorSessions.value.find(candidate =>
    (candidate?.items || []).some(entry => entry.itemKey === item?.itemKey)
  );
  if (session) await selectRollbackSession(session, { item });
  const payload = buildRollbackJourneyActionPayload(item);
  payload.kind = payload.kind || 'open_analyze_workspace';
  await openRollbackAnalyzeWorkspace(payload);
};
const openRollbackJourneyInTest = async item => {
  const session = rollbackOperatorSessions.value.find(candidate =>
    (candidate?.items || []).some(entry => entry.itemKey === item?.itemKey)
  );
  if (session) await selectRollbackSession(session, { item });
  const payload = buildRollbackJourneyActionPayload(item);
  payload.kind = payload.kind || 'open_test_workspace';
  await openRollbackTestWorkspace(payload);
};
const focusRollbackJourneySnapshot = async item => {
  const session = rollbackOperatorSessions.value.find(candidate =>
    (candidate?.items || []).some(entry => entry.itemKey === item?.itemKey)
  );
  if (session) await selectRollbackSession(session, { item });
  const snapshot = rollbackOperatorSessionSnapshotRecord(item);
  if (snapshot) {
    focusReleaseSnapshotHistory(snapshot);
  }
};
const focusRollbackJourneyBaseline = async item => {
  const session = rollbackOperatorSessions.value.find(candidate =>
    (candidate?.items || []).some(entry => entry.itemKey === item?.itemKey)
  );
  if (session) await selectRollbackSession(session, { item });
  const baseline = rollbackOperatorSessionBaselineRecord(item);
  if (baseline) {
    focusBatchBaselineHistory(baseline);
  }
};
const openRollbackOperatorSessionVersions = async item => {
  const session = rollbackOperatorSessions.value.find(candidate =>
    (candidate?.items || []).some(entry => entry.itemKey === item?.itemKey)
  );
  if (session) await selectRollbackSession(session, { item });
  if (item?.itemType === 'ledger_hop') {
    openPublishLedgerVersionHistory(item?.row || {});
    return;
  }
  const snapshot = rollbackOperatorSessionSnapshotRecord(item);
  if (snapshot) {
    openReleaseEvidenceVersionHistory(snapshot, item?.entry || null);
    return;
  }
  const baseline = rollbackOperatorSessionBaselineRecord(item);
  if (baseline) {
    openBatchEvidenceVersionHistory(baseline, item?.entry || null);
  }
};
const selectRollbackJourneyItem = async (session, item) => {
  await selectRollbackSession(session, { item });
};
const buildNextDeploySessionContext = ({
  current = null,
  nextWorkspace = '',
} = {}) =>
  advanceRollbackSessionContext({
    current,
    sourceWorkspace: 'Deploy',
    nextWorkspace,
  });
const openEvidenceVersionHistory = ({
  record = null,
  entry = null,
  fallbackIntent = 'Rollback evidence timeline',
  sessionContext = null,
  sessionItemKey = '',
} = {}) => {
  openVersionHistoryDialog(
    buildOperatorNoteTimelineVersionDialogContext({
      record,
      entry,
      fallbackIntent,
      sessionContext,
      sessionItemKey,
    })
  );
};
const openReleaseEvidenceVersionHistory = async (snapshot, entry) => {
  if (snapshot) {
    await focusReleaseSnapshotHistory(snapshot);
  }
  const entrySession = rollbackSessionContextFromEntry(entry);
  const sessionContext = buildNextDeploySessionContext({
    current: entrySession.sessionId ? entrySession : currentRollbackSessionContext.value,
    nextWorkspace: 'Versions',
  });
  openEvidenceVersionHistory({
    record: snapshot,
    entry,
    fallbackIntent: 'Rollback evidence snapshot',
    sessionContext,
    sessionItemKey: currentRollbackSessionItemKey.value,
  });
};
const openBatchEvidenceVersionHistory = async (baseline, entry) => {
  if (baseline) {
    await focusBatchBaselineHistory(baseline);
  }
  const entrySession = rollbackSessionContextFromEntry(entry);
  const sessionContext = buildNextDeploySessionContext({
    current: entrySession.sessionId ? entrySession : currentRollbackSessionContext.value,
    nextWorkspace: 'Versions',
  });
  openEvidenceVersionHistory({
    record: baseline,
    entry,
    fallbackIntent: 'Rollback evidence baseline',
    sessionContext,
    sessionItemKey: currentRollbackSessionItemKey.value,
  });
};
const buildDeploySeedVersionDialogContext = () =>
  buildOperatorNoteTimelineVersionDialogContext({
    record: buildRollbackEvidenceCompareBundle({
      record: seededReleaseBundle.value,
      ownerWorkspace: 'deploy',
      ownerActionTitle:
        deploySeedContext.value.followupIntent ||
        deploySeedContext.value.reason ||
        'Rollback evidence timeline',
    }),
    entry: {
      target_version_label: deploySeedContext.value.targetVersionLabel,
      compare_version_label: deploySeedContext.value.compareVersionLabel,
      recommended_action_label: deploySeedContext.value.followupIntent,
      confidence_label: deploySeedContext.value.confidenceTitle,
    },
    fallbackIntent:
      deploySeedContext.value.followupIntent ||
      deploySeedContext.value.reason ||
      'Rollback evidence timeline',
    sessionContext: buildNextDeploySessionContext({
      current: currentRollbackSessionContext.value,
      nextWorkspace: 'Versions',
    }),
    sessionItemKey: currentRollbackSessionItemKey.value,
  });
const maybeOpenVersionHistoryFromSeed = async () => {
  if (!deploySeedContext.value.openVersions) return;
  await nextTick();
  openVersionHistoryDialog(buildDeploySeedVersionDialogContext());
  const nextQuery = { ...(route.query || {}) };
  delete nextQuery.deploy_seed_open_versions;
  await router.replace({ query: nextQuery });
};
const formatInteger = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return Math.round(number).toLocaleString();
};
const formatUsd = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '$0.00';
  return `$${number.toFixed(number < 1 ? 4 : 2)}`;
};
const formatSignedInteger = value => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return '0';
  return `${number > 0 ? '+' : ''}${Math.round(number).toLocaleString()}`;
};
const formatSignedUsd = value => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return '$0.00';
  return `${number > 0 ? '+' : ''}$${Math.abs(number).toFixed(Math.abs(number) < 1 ? 4 : 2)}`.replace(
    '$',
    number < 0 ? '-$' : '$'
  );
};
const formatPercent = (value, digits = 1) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0%';
  return `${number.toFixed(digits)}%`;
};
const formatSignedPercent = value => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return '0.0%';
  return `${number > 0 ? '+' : ''}${number.toFixed(1)}%`;
};
const buildRate = (count, total) => {
  const safeTotal = Number(total);
  if (!Number.isFinite(safeTotal) || safeTotal <= 0) return 0;
  return (Number(count || 0) / safeTotal) * 100;
};
const humanRolloutArmLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'test') return 'Split test';
  if (normalized === 'control') return 'Split control';
  if (normalized === 'rollout') return 'Pinned rollout';
  return 'Published baseline';
};
const humanPublishLedgerEventLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'initial_publish') return 'Initial publish';
  if (normalized === 'rollback') return 'Rollback';
  if (normalized === 'rollout_publish') return 'Inbox rollout';
  if (normalized === 'clear_rollouts') return 'Clear overrides';
  return 'Publish';
};
const publishLedgerVersionLabel = (versionId, versionNumber) => {
  const number = Number(versionNumber);
  if (Number.isFinite(number) && number > 0) return `#${number}`;
  const id = Number(versionId);
  if (Number.isFinite(id) && id > 0) return `ID ${id}`;
  return 'Unknown version';
};
const publishLedgerEntrySummary = row => {
  const eventType = String(row?.event_type || '').trim();
  const target = publishLedgerVersionLabel(
    row?.target_version_id,
    row?.target_version_number
  );
  const previous = publishLedgerVersionLabel(
    row?.previous_published_version_id,
    row?.previous_published_version_number
  );
  const inboxCount =
    Number(row?.inbox_count || 0) || Array(row?.inbox_ids || []).length || 0;
  if (eventType === 'initial_publish') {
    return `Initialized the live published baseline at ${target}.`;
  }
  if (eventType === 'rollback') {
    return previous !== 'Unknown version'
      ? `Rolled the global baseline from ${previous} back to ${target}.`
      : `Rolled the global baseline back to ${target}.`;
  }
  if (eventType === 'rollout_publish') {
    if (String(row?.rollout_mode || '').trim() === 'split') {
      return `Sent ${row?.traffic_percentage || 0}% of traffic in ${inboxCount} inbox${inboxCount === 1 ? '' : 'es'} to ${target} while the rest stayed on ${previous}.`;
    }
    return `Pinned ${inboxCount} inbox${inboxCount === 1 ? '' : 'es'} to ${target} instead of inheriting the global baseline.`;
  }
  if (eventType === 'clear_rollouts') {
    return `Cleared ${inboxCount} inbox rollout override${inboxCount === 1 ? '' : 's'} back to ${target}.`;
  }
  return previous !== 'Unknown version'
    ? `Moved the global published baseline from ${previous} to ${target}.`
    : `Published ${target} as the global live baseline.`;
};
const publishLedgerEvidenceSummary = row => {
  const parts = [];
  if (row?.bundle_title || row?.focus_label) {
    parts.push(
      row?.focus_label || row?.bundle_title || row?.recommendation_code || 'release bundle'
    );
  }
  if (row?.release_snapshot_status) {
    parts.push(`snapshot ${humanReleaseReviewStatus(row.release_snapshot_status).toLowerCase()}`);
  }
  if (row?.batch_baseline_title) {
    parts.push(`baseline ${row.batch_baseline_title}`);
  }
  return parts.join(' • ');
};
const humanPublishLedgerChangedAreaLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'identity') return 'identity';
  if (normalized === 'knowledge_retrieval') return 'knowledge retrieval';
  if (normalized === 'answer_generation') return 'answer generation';
  if (normalized === 'procedure_routing') return 'procedure routing';
  if (normalized === 'channel_runtime') return 'channel runtime';
  if (normalized === 'release_loop') return 'release loop';
  if (normalized === 'risk_policy') return 'risk policy';
  if (normalized === 'brand_profile') return 'brand profile';
  if (normalized === 'template_mode') return 'template mode';
  return normalized.replaceAll('_', ' ');
};
const publishLedgerChangedAreasSummary = row => {
  const labels = Array(row?.changed_areas || []).map(humanPublishLedgerChangedAreaLabel);
  if (!labels.length) return '';
  return labels.join(' • ');
};
const publishLedgerSessionContext = row =>
  normalizeRollbackSessionContext({
    sessionId: row?.session_id || row?.sessionId,
    sessionLabel: row?.session_label || row?.sessionLabel,
    sessionStep: row?.session_step || row?.sessionStep,
    sessionOrigin: row?.session_origin || row?.sessionOrigin,
    nextWorkspace: row?.next_workspace || row?.nextWorkspace,
  });
const publishLedgerSessionBadges = row => {
  const session = publishLedgerSessionContext(row);
  return buildRollbackBreadcrumbBadges({
    source: row?.source,
    targetVersionLabel: publishLedgerVersionLabel(
      row?.target_version_id,
      row?.target_version_number
    ),
    compareVersionLabel:
      Number(row?.previous_published_version_id || 0) > 0 ||
      Number(row?.previous_published_version_number || 0) > 0
        ? publishLedgerVersionLabel(
            row?.previous_published_version_id,
            row?.previous_published_version_number
          )
        : '',
    sessionLabel: session.sessionLabel,
    sessionStep: session.sessionStep,
    sessionOrigin: session.sessionOrigin,
    nextLabel: session.nextWorkspace,
  });
};
const publishLedgerEntrySessionMeta = row => {
  const parts = [];
  if (row?.created_at) parts.push(formatDateTime(row.created_at));
  const evidenceSummary = publishLedgerEvidenceSummary(row);
  if (evidenceSummary) parts.push(evidenceSummary);
  const changedAreas = publishLedgerChangedAreasSummary(row);
  if (changedAreas) parts.push(`areas ${changedAreas}`);
  return parts.join(' • ');
};
const humanRollbackOutcomeStatusLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'improving') return 'Improving';
  if (normalized === 'watch') return 'Watch';
  if (normalized === 'partial') return 'Partial';
  if (normalized === 'waiting') return 'Waiting';
  return 'Steady';
};
const humanRollbackOperatorGroupLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'helped') return 'Helped';
  if (normalized === 'risky') return 'Still risky';
  if (normalized === 'waiting') return 'Waiting';
  return 'Steady';
};
const humanRollbackSignalSeverityLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'positive') return 'Better';
  if (normalized === 'warning') return 'Risk';
  if (normalized === 'info') return 'Watch';
  return 'Flat';
};
const humanRollbackConfidenceLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'high') return 'High confidence';
  if (normalized === 'medium') return 'Medium confidence';
  return 'Low confidence';
};
const humanRollbackPlaybookStatusLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'done') return 'Done';
  if (normalized === 'next') return 'Next';
  if (normalized === 'missing') return 'Missing';
  return 'Watch';
};
const humanRollbackGapSeverityLabel = value => {
  const normalized = String(value || '').trim();
  if (normalized === 'warning') return 'Missing';
  if (normalized === 'watch') return 'Watch';
  return 'Track';
};
const buildRollbackActionToken = action =>
  [
    String(action?.kind || '').trim(),
    String(action?.label || '').trim(),
    String(action?.target_version_id || '').trim(),
    String(action?.compare_version_id || action?.previous_version_id || '').trim(),
    String(action?.release_snapshot_key || '').trim(),
    String(action?.batch_baseline_key || '').trim(),
  ].join(':');
const buildRollbackFollowupBundle = action => {
  const title = String(action?.bundle_title || action?.bundle_label || '').trim();
  const focusLabel = String(action?.focus_label || '').trim();
  const recommendationCode = String(action?.recommendation_code || '').trim();
  const enabled = Boolean(title || focusLabel || recommendationCode);
  return {
    enabled,
    title,
    focusLabel,
    strategyGroupTitle: '',
    recommendationCode,
    ownerWorkspace: 'deploy',
    ownerActionTitle:
      String(action?.intent_label || action?.label || 'Rollback operator follow-up').trim(),
    guidanceEditor: '',
    escalationFocus: '',
    hotspotSummary: '',
    resolvedSummary: '',
    escalatedSummary: '',
    validationSummary:
      String(action?.target_version_label || '').trim() &&
      String(action?.previous_version_label || '').trim()
        ? `${String(action?.target_version_label || '').trim()} vs ${String(action?.previous_version_label || '').trim()}`
        : '',
    stage: 'validation',
  };
};
const formatRollbackSignalValue = row => {
  const key = String(row?.key || '').trim();
  if (key === 'estimated_cost_usd') return formatUsd(row?.target_value);
  if (key.endsWith('_rate')) return formatPercent(row?.target_value, 2);
  return formatInteger(row?.target_value);
};
const formatRollbackSignalBaselineValue = row => {
  const key = String(row?.key || '').trim();
  if (key === 'estimated_cost_usd') return formatUsd(row?.previous_value);
  if (key.endsWith('_rate')) return formatPercent(row?.previous_value, 2);
  return formatInteger(row?.previous_value);
};
const formatRollbackSignalDelta = row => {
  const key = String(row?.key || '').trim();
  if (key === 'estimated_cost_usd') return formatSignedUsd(row?.delta);
  if (key.endsWith('_rate')) return formatSignedPercent(row?.delta);
  return formatSignedInteger(row?.delta);
};
const currentReleaseReviewStatus = computed(() =>
  String(releaseReviewEntry.value?.status || '').trim()
);
const currentReleaseReviewStatusLabel = computed(() =>
  humanReleaseReviewStatus(currentReleaseReviewStatus.value)
);
const activeReleaseReviewSummary = computed(() => {
  if (!seededReleaseBundle.value.enabled) {
    return 'Open Deploy from Analyze or Test with a validated repair bundle to capture release review state.';
  }
  const bundleLabel =
    seededReleaseBundle.value.focusLabel ||
    seededReleaseBundle.value.title ||
    seededReleaseBundle.value.strategyGroupTitle ||
    'Seeded repair bundle';
  if (currentReleaseReviewStatus.value === 'released') {
    return `${bundleLabel} is marked released${releaseReviewEntry.value?.reviewed_at ? ` • ${formatDateTime(releaseReviewEntry.value.reviewed_at)}` : ''}.`;
  }
  if (currentReleaseReviewStatus.value === 'rolled_back') {
    return `${bundleLabel} is marked for rollback${releaseReviewEntry.value?.reviewed_at ? ` • ${formatDateTime(releaseReviewEntry.value.reviewed_at)}` : ''}.`;
  }
  if (currentReleaseReviewStatus.value === 'reviewing') {
    return `${bundleLabel} already has an active release snapshot${releaseReviewEntry.value?.reviewed_at ? ` • ${formatDateTime(releaseReviewEntry.value.reviewed_at)}` : ''}.`;
  }
  return `${bundleLabel} is validation-ready but does not have a saved release snapshot yet.`;
});
const humanBatchBaselineLevel = level => {
  switch (String(level || '').trim()) {
    case 'ready':
      return 'Ready';
    case 'watch':
      return 'Watch';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Idle';
  }
};
const activeBatchBaselineSummary = computed(() => {
  if (!activeBatchBaseline.value) {
    return 'No durable batch release baseline has been captured yet.';
  }

  const parts = [];
  if (activeBatchBaseline.value.title) parts.push(activeBatchBaseline.value.title);
  if (activeBatchBaseline.value.ran_at) {
    parts.push(formatDateTime(activeBatchBaseline.value.ran_at));
  }
  const totalCases = Number(activeBatchBaseline.value.total_cases || 0);
  if (totalCases > 0) parts.push(`${totalCases} batch case${totalCases === 1 ? '' : 's'}`);
  if (activeBatchBaseline.value.top_blocker) {
    parts.push(`top blocker ${activeBatchBaseline.value.top_blocker}`);
  } else if (activeBatchBaseline.value.top_watch_signal) {
    parts.push(`watch ${activeBatchBaseline.value.top_watch_signal}`);
  }
  return parts.join(' • ');
});

const deployCards = computed(() => [
  {
    label: 'Runtime status',
    value: runtimeStatus.value,
    note: runtimeStatus.value === 'active' ? 'AI is allowed to run' : 'AI is disabled',
  },
  {
    label: 'Reply mode',
    value: replyMode.value,
    note: 'Current assistant send strategy',
  },
  {
    label: 'Connected inboxes',
    value: ailabInboxes.value.length,
    note: 'Channels using this assistant',
  },
]);

const humanReplyModeLabel = mode => {
  switch (String(mode || '').trim()) {
    case 'auto_reply':
      return 'Auto reply';
    case 'auto_suggest':
      return 'Auto suggest';
    case 'suggest':
      return 'Suggest only';
    case 'handoff':
      return 'Handoff';
    default:
      return 'Inherit';
  }
};
const runtimeLanguageOptions = [
  { value: '', label: 'Inherit detected language' },
  { value: 'en', label: 'English' },
  { value: 'ms', label: 'Malay' },
  { value: 'th', label: 'Thai' },
];
const communicationStyleOptions = [
  { value: '', label: 'Inherit assistant style' },
  { value: 'adaptive', label: 'Adaptive' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
];
const businessHoursReplyModeOptions = [
  { value: 'inherit', label: 'Keep normal deploy mode' },
  { value: 'auto_reply', label: 'Force auto reply' },
  { value: 'auto_suggest', label: 'Force auto suggest' },
  { value: 'suggest', label: 'Force suggest only' },
  { value: 'handoff', label: 'Force handoff' },
];
const businessHoursResponseBehaviorOptions = [
  { value: 'normal_ai', label: 'Let AI answer normally' },
  {
    value: 'inbox_out_of_office_message',
    label: 'Send the inbox out-of-office message instead',
  },
];
const complianceProfileOptions = [
  { value: 'standard', label: 'Standard' },
  { value: 'strict', label: 'Strict' },
  { value: 'regulated', label: 'Regulated' },
];
const regionCountryOptions = [
  { value: 'MY', label: 'Malaysia' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
];
const segmentTypeOptions = [
  { value: 'reply_language', label: 'Detected reply language' },
  { value: 'conversation_language', label: 'Conversation language' },
  { value: 'vip_conversation', label: 'VIP conversation' },
];
const languageMatchOptions = [
  { value: 'en', label: 'English' },
  { value: 'ms', label: 'Malay' },
  { value: 'th', label: 'Thai' },
];

const normalizeReplyMode = (value, fallback = 'inherit') => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (['auto', 'auto-reply'].includes(normalized)) return 'auto_reply';
  return ['inherit', 'auto_reply', 'auto_suggest', 'suggest', 'handoff'].includes(
    normalized
  )
    ? normalized
    : fallback;
};
const normalizeRuntimeLanguage = value =>
  ['en', 'ms', 'th'].includes(String(value || '').trim().toLowerCase())
    ? String(value || '').trim().toLowerCase()
    : '';
const normalizeCommunicationStyle = value =>
  ['adaptive', 'friendly', 'professional', 'casual', 'formal'].includes(
    String(value || '').trim().toLowerCase()
  )
    ? String(value || '').trim().toLowerCase()
    : '';
const normalizeSegmentBinding = binding => {
  const row = binding || {};
  const segmentType = ['reply_language', 'conversation_language', 'vip_conversation'].includes(
    String(row.segment_type || row.segmentType || '').trim()
  )
    ? String(row.segment_type || row.segmentType || '').trim()
    : 'reply_language';
  const next = {
    id: row.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    segment_type: segmentType,
    match_value:
      segmentType === 'vip_conversation'
        ? 'true'
        : normalizeRuntimeLanguage(row.match_value || row.matchValue),
    reply_mode: normalizeReplyMode(row.reply_mode || row.replyMode, 'inherit'),
    forced_reply_language: normalizeRuntimeLanguage(
      row.forced_reply_language || row.forcedReplyLanguage
    ),
    communication_style_mode: normalizeCommunicationStyle(
      row.communication_style_mode || row.communicationStyleMode
    ),
  };
  if (next.segment_type === 'vip_conversation') next.match_value = 'true';
  return next;
};
const segmentBindingLabel = row => {
  if (row.segment_type === 'vip_conversation') return 'VIP conversation';
  const match = languageMatchOptions.find(option => option.value === row.match_value);
  const prefix =
    row.segment_type === 'conversation_language'
      ? 'Conversation language'
      : 'Detected reply language';
  return `${prefix}: ${match?.label || row.match_value || 'Unset'}`;
};
const formatCommunicationStyleLabel = value => {
  const option = communicationStyleOptions.find(item => item.value === value);
  return option?.label || 'Inherited';
};
const currentChannelRuntime = computed(
  () => aiCloserConfig.value?.prompt_sections?.channel_runtime || {}
);
const currentReleaseLoop = computed(
  () => aiCloserConfig.value?.prompt_sections?.release_loop || {}
);
const currentReleaseSnapshots = computed(() =>
  normalizeReleaseReviewSnapshots(
    releaseReadinessSummary.value?.release_snapshots?.length
      ? releaseReadinessSummary.value.release_snapshots
      : currentReleaseLoop.value?.review_snapshots || []
  )
);
const currentBatchBaselines = computed(() =>
  normalizeBatchBaselines(
    releaseReadinessSummary.value?.batch_baselines?.length
      ? releaseReadinessSummary.value.batch_baselines
      : currentReleaseLoop.value?.batch_baselines || []
  )
);
const publishLedgerEntries = computed(() => {
  const backendRows =
    releaseReadinessSummary.value?.publish_ledger?.length
      ? releaseReadinessSummary.value.publish_ledger
      : configVersionSummary.value?.publish_ledger || [];
  return Array(backendRows);
});
const recentPublishLedgerEntries = computed(() => publishLedgerEntries.value.slice(0, 8));
const publishLedgerSessionBackedCount = computed(
  () => publishLedgerEntries.value.filter(row => publishLedgerSessionContext(row).sessionId).length
);
const publishLedgerEventCounts = computed(
  () => releaseReadinessSummary.value?.publish_ledger_event_counts || {}
);
const latestPublishLedgerEntry = computed(
  () =>
    releaseReadinessSummary.value?.latest_publish_ledger ||
    publishLedgerEntries.value[0] ||
    null
);
const latestRollbackOutcome = computed(
  () => releaseReadinessSummary.value?.latest_rollback_outcome || null
);
const rollbackOutcomeSignalRows = computed(() =>
  Array(latestRollbackOutcome.value?.signal_breakdown || [])
);
const rollbackPlaybookItems = computed(() =>
  Array(latestRollbackOutcome.value?.playbook_items || [])
);
const rollbackEvidenceGaps = computed(() =>
  Array(latestRollbackOutcome.value?.evidence_gaps || [])
);
const rollbackOperatorActions = computed(() =>
  Array(latestRollbackOutcome.value?.operator_actions || [])
);
const bundleRollbackOutcomes = computed(() =>
  Array(releaseReadinessSummary.value?.bundle_rollback_outcomes || [])
);
const bundleRollbackOutcomeCounts = computed(
  () => releaseReadinessSummary.value?.bundle_rollback_outcome_counts || {}
);
const rollbackOperatorCards = computed(() => [
  {
    label: 'Helped',
    value: Number(bundleRollbackOutcomeCounts.value?.helped || 0),
    note: 'Bundle rollbacks currently showing directionally better live signals.',
  },
  {
    label: 'Still risky',
    value: Number(bundleRollbackOutcomeCounts.value?.risky || 0),
    note: 'Rollbacks that still show at least one worse quality signal.',
  },
  {
    label: 'Waiting',
    value: Number(bundleRollbackOutcomeCounts.value?.waiting || 0),
    note: 'Rollbacks that still need more comparable live traffic.',
  },
  {
    label: 'Steady',
    value: Number(bundleRollbackOutcomeCounts.value?.steady || 0),
    note: 'Rollbacks that are live but mostly flat so far.',
  },
]);
const releaseReadinessSummary = computed(
  () => configVersionSummary.value?.release_readiness || {}
);
const seededBatchBaseline = computed(() =>
  findBatchBaseline({
    baselines: currentBatchBaselines.value,
    baselineKey: seededBatchBaselineContext.value.baseline_key,
    setKey: seededBatchBaselineContext.value.set_key,
    bundle: seededReleaseBundle.value.enabled ? seededReleaseBundle.value : {},
  }) ||
  (seededBatchBaselineContext.value.title
    ? {
        ...seededBatchBaselineContext.value,
      }
    : null)
);
const latestBatchBaseline = computed(
  () =>
    currentBatchBaselines.value
      .slice()
      .sort(
        (a, b) =>
          new Date(b?.ran_at || 0).getTime() - new Date(a?.ran_at || 0).getTime()
      )[0] || null
);
const recentReleaseSnapshots = computed(() => currentReleaseSnapshots.value.slice(0, 5));
const recentBatchBaselines = computed(() => currentBatchBaselines.value.slice(0, 5));
const activeBatchBaseline = computed(
  () =>
    seededBatchBaseline.value ||
    latestBatchBaseline.value ||
    releaseReadinessSummary.value?.latest_batch_baseline ||
    null
);
const activePublishEvidenceContext = computed(() => {
  const snapshot = releaseReviewEntry.value || null;
  const baseline = activeBatchBaseline.value || null;
  const bundle = seededReleaseBundle.value?.enabled ? seededReleaseBundle.value : null;
  const session = currentRollbackSessionContext.value?.sessionId
    ? ensureRollbackSessionContext({
        current: currentRollbackSessionContext.value,
        sourceWorkspace: 'Deploy',
        nextWorkspace: 'Deploy',
      })
    : null;
  return {
    release_snapshot_id: snapshot?.id || null,
    release_snapshot_key:
      snapshot?.snapshot_key ||
      (bundle ? buildReleaseReviewSnapshotKey(bundle) : null),
    release_snapshot_status: snapshot?.status || null,
    release_snapshot_reviewed_at: snapshot?.reviewed_at || null,
    batch_baseline_id: baseline?.id || null,
    batch_baseline_key: baseline?.baseline_key || null,
    batch_baseline_title: baseline?.title || null,
    bundle_title: bundle?.title || snapshot?.bundle_title || null,
    focus_label: bundle?.focusLabel || snapshot?.focus_label || null,
    recommendation_code:
      bundle?.recommendationCode || snapshot?.recommendation_code || null,
    session_id: session?.sessionId || null,
    session_label: session?.sessionLabel || null,
    session_step: session?.sessionStep || null,
    session_origin: session?.sessionOrigin || null,
    next_workspace: session?.nextWorkspace || 'Deploy',
  };
});
const versionSummaryVersions = computed(
  () => Array(configVersionSummary.value?.versions || [])
);
const rolloutCandidateVersions = computed(() =>
  versionSummaryVersions.value.filter(row => Number(row?.id) > 0)
);
const publishedVersionId = computed(() => {
  const value =
    configVersionSummary.value?.published_version_id ??
    aiCloserConfig.value?.published_version_id;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
});
const draftVersionId = computed(() => {
  const value =
    configVersionSummary.value?.draft_version_id ??
    aiCloserConfig.value?.draft_version_id;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
});
const publishedVersionRow = computed(
  () =>
    versionSummaryVersions.value.find(
      row => Number(row?.id) === Number(publishedVersionId.value)
    ) || null
);
const draftVersionRow = computed(
  () =>
    versionSummaryVersions.value.find(
      row => Number(row?.id) === Number(draftVersionId.value)
    ) || null
);
const reviewVersionCount = computed(
  () =>
    versionSummaryVersions.value.filter(
      row => String(row?.state || '').trim() === 'review'
    ).length
);
const selectedRolloutVersionRow = computed(
  () =>
    rolloutCandidateVersions.value.find(
      row => Number(row?.id) === Number(selectedRolloutVersionId.value)
    ) || null
);
const rolloutInboxOptions = computed(() =>
  Array(configVersionSummary.value?.inboxes || []).map(inbox => {
    const rolloutDetail =
      Array(configVersionSummary.value?.rollout_details || []).find(
        row => Number(row?.inbox_id) === Number(inbox.id)
      ) || null;
    const rolloutVersionId = Number(rolloutDetail?.config_version_id) || null;
    const rolloutVersion = versionSummaryVersions.value.find(
      row => Number(row?.id) === Number(rolloutVersionId)
    );
    return {
      id: Number(inbox.id),
      name: inbox?.name || resolveInboxName(inbox.id) || `Inbox ${inbox.id}`,
      channelType: inbox?.channel_type || '',
      rolloutVersionId,
      rolloutVersionNumber: Number(rolloutVersion?.version_number) || null,
      trafficPercentage: Number(rolloutDetail?.traffic_percentage) || 100,
      isSplitTest:
        Number(rolloutDetail?.traffic_percentage) > 0 &&
        Number(rolloutDetail?.traffic_percentage) < 100,
      inheritsPublished: !rolloutVersionId,
    };
  })
);
const rolloutRows = computed(() =>
  Array(configVersionSummary.value?.rollout_details || [])
    .map(detail => {
      const inboxId = Number(detail?.inbox_id);
      const versionId = Number(detail?.config_version_id);
      const inbox = (configVersionSummary.value?.inboxes || []).find(
        row => Number(row?.id) === Number(inboxId)
      );
      const version = versionSummaryVersions.value.find(
        row => Number(row?.id) === Number(versionId)
      );
      return {
        inboxId: Number(inboxId),
        inboxLabel:
          inbox?.name || resolveInboxName(inboxId) || `Inbox ${inboxId}`,
        channelType: inbox?.channel_type || '',
        versionId,
        versionNumber: Number(version?.version_number) || null,
        trafficPercentage: Number(detail?.traffic_percentage) || 100,
        isSplitTest:
          Number(detail?.traffic_percentage) > 0 &&
          Number(detail?.traffic_percentage) < 100,
      };
    })
    .filter(row => Number.isFinite(row.inboxId) && row.inboxId > 0)
);
const selectedRolloutInboxCount = computed(() => selectedRolloutInboxIds.value.length);
const selectedRolloutUsesSplit = computed(() => {
  const pct = Number(rolloutTrafficPercentage.value);
  return Number.isFinite(pct) && pct > 0 && pct < 100;
});
const rolloutSelectedVersionIsPublished = computed(
  () =>
    publishedVersionId.value &&
    Number(selectedRolloutVersionId.value) === Number(publishedVersionId.value)
);
const rolloutCanPublish = computed(
  () =>
    Boolean(selectedRolloutInboxCount.value) &&
    Boolean(selectedRolloutVersionId.value) &&
    !(
      selectedRolloutUsesSplit.value &&
      (!publishedVersionId.value || rolloutSelectedVersionIsPublished.value)
    )
);
const rolloutPublishActionLabel = computed(() =>
  selectedRolloutUsesSplit.value
    ? 'Publish split rollout'
    : 'Publish version to selected inboxes'
);
const rolloutGuardrails = computed(() => {
  const items = [];
  const splitCount =
    releaseReadinessSummary.value?.split_rollout_count ||
    rolloutRows.value.filter(row => row.isSplitTest).length;

  if (selectedRolloutUsesSplit.value && !publishedVersionId.value) {
    items.push({
      title: 'Split rollout needs a live baseline',
      body: 'Publish a stable global version first so the remaining traffic has a baseline to stay on.',
      tone: 'warning',
    });
  }
  if (selectedRolloutUsesSplit.value && rolloutSelectedVersionIsPublished.value) {
    items.push({
      title: 'Split target matches the live baseline',
      body: 'Choose a different version than the currently published baseline, otherwise the split will not compare anything.',
      tone: 'warning',
    });
  }
  if (
    selectedRolloutVersionRow.value &&
    !['review', 'published'].includes(String(selectedRolloutVersionRow.value?.state || '').trim())
  ) {
    items.push({
      title: 'Selected rollout version is not review-ready',
      body: 'Promote the target version into review or publish-ready state before pushing it to live traffic.',
      tone: 'warning',
    });
  }
  if (selectedRolloutUsesSplit.value && Number(rolloutTrafficPercentage.value) < 10) {
    items.push({
      title: 'Very small split traffic',
      body: 'Tiny splits can take longer to generate enough volume for a meaningful A/B report.',
      tone: 'info',
    });
  }
  if (selectedRolloutUsesSplit.value && activeBatchBaseline.value?.overall_level === 'blocked') {
    items.push({
      title: 'Latest batch baseline is still blocked',
      body: 'A split rollout can still be useful, but the latest durable batch evidence is not release-ready yet.',
      tone: 'warning',
    });
  }
  if (splitCount > 0) {
    items.push({
      title: `${splitCount} split rollout${splitCount === 1 ? '' : 's'} already active`,
      body: 'Refresh A/B report after changes so usage and quality signals stay comparable across live traffic slices.',
      tone: 'info',
    });
  }
  return items.slice(0, 4);
});
const abReportVersionIds = computed(() =>
  [
    publishedVersionId.value,
    selectedRolloutVersionId.value,
    ...rolloutRows.value.map(row => row.versionId),
  ]
    .map(value => Number(value))
    .filter(value => Number.isFinite(value) && value > 0)
    .filter((value, index, rows) => rows.indexOf(value) === index)
    .slice(0, 6)
);
const abReportRows = computed(() => {
  const baselineVersionId = Number(
    abReport.value?.published_version_id || publishedVersionId.value
  );
  const rows = Array(abReport.value?.versions || []).map(row => {
    const versionRow = versionSummaryVersions.value.find(
      version => Number(version?.id) === Number(row?.version_id)
    );
    const calls = Number(row?.calls || 0);
    const promptInjectionCount =
      Number(row?.events?.['security.prompt_injection.detected'] || 0);
    const handoffRequestedCount =
      Number(row?.events?.['captain.response_builder.handoff_requested'] || 0);
    const armBreakdown = Array(row?.arm_breakdown || []).map(arm => {
      const armCalls = Number(arm?.calls || 0);
      const armPromptInjectionCount =
        Number(arm?.events?.['security.prompt_injection.detected'] || 0);
      const armHandoffRequestedCount =
        Number(arm?.events?.['captain.response_builder.handoff_requested'] || 0);
      return {
        ...arm,
        arm_label: humanRolloutArmLabel(arm?.arm),
        prompt_injection_count: armPromptInjectionCount,
        handoff_requested_count: armHandoffRequestedCount,
        prompt_injection_rate: buildRate(armPromptInjectionCount, armCalls),
        handoff_requested_rate: buildRate(armHandoffRequestedCount, armCalls),
        share_of_version_calls: buildRate(armCalls, calls),
      };
    });
    return {
      ...row,
      version_number: Number(versionRow?.version_number) || null,
      state: String(versionRow?.state || '').trim(),
      prompt_injection_count: promptInjectionCount,
      handoff_requested_count: handoffRequestedCount,
      prompt_injection_rate: buildRate(promptInjectionCount, calls),
      handoff_requested_rate: buildRate(handoffRequestedCount, calls),
      arm_breakdown: armBreakdown,
    };
  });
  const baselineRow =
    rows.find(row => Number(row.version_id) === baselineVersionId) || null;
  const withComparison = rows.map(row => ({
    ...row,
    calls_delta_vs_published:
      baselineRow && Number(row.version_id) !== Number(baselineRow.version_id)
        ? Number(row.calls || 0) - Number(baselineRow.calls || 0)
        : null,
    cost_delta_vs_published:
      baselineRow && Number(row.version_id) !== Number(baselineRow.version_id)
        ? Number(row.estimated_cost_usd || 0) -
          Number(baselineRow.estimated_cost_usd || 0)
        : null,
    prompt_injection_rate_delta_vs_published:
      baselineRow && Number(row.version_id) !== Number(baselineRow.version_id)
        ? Number(row.prompt_injection_rate || 0) -
          Number(baselineRow.prompt_injection_rate || 0)
        : null,
    handoff_rate_delta_vs_published:
      baselineRow && Number(row.version_id) !== Number(baselineRow.version_id)
        ? Number(row.handoff_requested_rate || 0) -
          Number(baselineRow.handoff_requested_rate || 0)
        : null,
  }));

  return withComparison.sort((a, b) => {
    const aPriority =
      Number(a.version_id) === Number(selectedRolloutVersionId.value)
        ? 0
        : Number(a.version_id) === Number(publishedVersionId.value)
          ? 1
          : 2;
    const bPriority =
      Number(b.version_id) === Number(selectedRolloutVersionId.value)
        ? 0
        : Number(b.version_id) === Number(publishedVersionId.value)
          ? 1
          : 2;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return Number(b.calls || 0) - Number(a.calls || 0);
  });
});
const abReportSummaryCards = computed(() => {
  const rows = abReportRows.value;
  const totalCalls = rows.reduce((sum, row) => sum + Number(row.calls || 0), 0);
  const totalCost = rows.reduce(
    (sum, row) => sum + Number(row.estimated_cost_usd || 0),
    0
  );
  const splitArmCalls = rows.reduce(
    (sum, row) =>
      sum +
      row.arm_breakdown.reduce(
        (armSum, arm) =>
          armSum +
          (['control', 'test'].includes(String(arm.arm || '').trim())
            ? Number(arm.calls || 0)
            : 0),
        0
      ),
    0
  );
  const trackedArms = Array.from(
    new Set(
      rows.flatMap(row =>
        row.arm_breakdown.map(arm => humanRolloutArmLabel(arm.arm))
      )
    )
  );
  return [
    {
      label: 'Window',
      value: `${abReportDays.value}d`,
      note: 'Usage and quality signals grouped by version id and rollout arm.',
    },
    {
      label: 'Compared versions',
      value: rows.length,
      note: rows.length
        ? 'Published and rollout candidates with tracked live traffic.'
        : 'No tracked live traffic for the selected comparison set yet.',
    },
    {
      label: 'Tracked calls',
      value: formatInteger(totalCalls),
      note: 'Live model usage events carrying ai_closer version metadata.',
    },
    {
      label: 'Split-arm calls',
      value: formatInteger(splitArmCalls),
      note: trackedArms.length
        ? `Tracked arms: ${trackedArms.join(', ')}.`
        : 'No rollout arm metadata has landed in live traffic yet.',
    },
    {
      label: 'Estimated cost',
      value: formatUsd(totalCost),
      note: 'Estimated from grouped token usage and pricing tables.',
    },
  ];
});
const versionSummaryCards = computed(() => [
  {
    label: 'Published snapshot',
    value: publishedVersionRow.value?.version_number
      ? `#${publishedVersionRow.value.version_number}`
      : publishedVersionId.value
        ? `ID ${publishedVersionId.value}`
        : 'None',
    note: publishedVersionRow.value?.created_at
      ? `Live baseline captured ${formatDateTime(publishedVersionRow.value.created_at)}.`
      : 'No published snapshot metadata loaded yet.',
  },
  {
    label: 'Draft snapshot',
    value: draftVersionRow.value?.version_number
      ? `#${draftVersionRow.value.version_number}`
      : draftVersionId.value
        ? `ID ${draftVersionId.value}`
        : 'None',
    note:
      draftVersionId.value && draftVersionId.value !== publishedVersionId.value
        ? 'A newer draft exists and still needs publish review.'
        : 'Working copy is aligned with the live published baseline.',
  },
  {
    label: 'Review snapshots',
    value: reviewVersionCount.value,
    note:
      reviewVersionCount.value > 0
        ? 'Snapshots are waiting for publish or rollback decisions.'
        : 'No version is currently parked in review state.',
  },
  {
    label: 'Inbox rollouts',
    value: rolloutRows.value.length,
    note:
      rolloutRows.value.length > 0
        ? `${releaseReadinessSummary.value?.split_rollout_count || rolloutRows.value.filter(row => row.isSplitTest).length} split rollout${(releaseReadinessSummary.value?.split_rollout_count || rolloutRows.value.filter(row => row.isSplitTest).length) === 1 ? '' : 's'} active.`
        : 'All inboxes currently inherit the globally published version.',
  },
  {
    label: 'Saved test sets',
    value: releaseReadinessSummary.value.saved_test_set_count || 0,
    note: releaseReadinessSummary.value.latest_saved_test_set?.title
      ? `Latest backend set: ${releaseReadinessSummary.value.latest_saved_test_set.title}.`
      : 'No backend saved test set has been captured yet.',
  },
  {
    label: 'Release evidence',
    value: releaseReadinessSummary.value.batch_baseline_count || 0,
    note: releaseReadinessSummary.value.latest_batch_baseline?.title
      ? `Latest durable batch baseline: ${releaseReadinessSummary.value.latest_batch_baseline.title}.`
      : 'No durable batch baseline has been summarized yet.',
  },
  {
    label: 'Publish ledger',
    value: releaseReadinessSummary.value.publish_ledger_count || 0,
    note: latestPublishLedgerEntry.value
      ? `${humanPublishLedgerEventLabel(latestPublishLedgerEntry.value.event_type)} recorded ${formatDateTime(latestPublishLedgerEntry.value.created_at)}${publishLedgerEvidenceSummary(latestPublishLedgerEntry.value) ? ` • ${publishLedgerEvidenceSummary(latestPublishLedgerEntry.value)}` : ''}${Number(releaseReadinessSummary.value?.publish_ledger_with_evidence_count || 0) > 0 ? ` • ${releaseReadinessSummary.value.publish_ledger_with_evidence_count} linked evidence entr${Number(releaseReadinessSummary.value.publish_ledger_with_evidence_count) === 1 ? 'y' : 'ies'}` : ''}${publishLedgerSessionBackedCount.value > 0 ? ` • ${publishLedgerSessionBackedCount.value} session-backed hop${publishLedgerSessionBackedCount.value === 1 ? '' : 's'}` : ''}${Number(publishLedgerEventCounts.value?.rollback || 0) > 0 ? ` • ${publishLedgerEventCounts.value.rollback} rollback${Number(publishLedgerEventCounts.value.rollback) === 1 ? '' : 's'} logged.` : '.'}`
      : 'No durable publish or rollback ledger entry has been captured yet.',
  },
  {
    label: 'Rollback outcome',
    value: latestRollbackOutcome.value?.status
      ? humanRollbackOutcomeStatusLabel(latestRollbackOutcome.value.status)
      : 'None',
    note: latestRollbackOutcome.value?.summary
      ? `${latestRollbackOutcome.value.summary}${latestRollbackOutcome.value?.confidence_label ? ` • ${latestRollbackOutcome.value.confidence_label}` : ''}${latestRollbackOutcome.value?.recommended_action_label ? ` • ${latestRollbackOutcome.value.recommended_action_label}` : ''}${latestRollbackOutcome.value?.evidence_gap_count ? ` • ${latestRollbackOutcome.value.evidence_gap_count} evidence gap${latestRollbackOutcome.value.evidence_gap_count === 1 ? '' : 's'}` : ''}${bundleRollbackOutcomes.value.length ? ` • ${bundleRollbackOutcomes.value.length} bundle rollback outcome${bundleRollbackOutcomes.value.length === 1 ? '' : 's'} tracked.` : ''}${Number(latestRollbackOutcome.value?.comparison?.prompt_injection_rate_delta) || Number(latestRollbackOutcome.value?.comparison?.handoff_requested_rate_delta) ? ` • prompt injection ${formatSignedPercent(latestRollbackOutcome.value?.comparison?.prompt_injection_rate_delta || 0)} • handoff ${formatSignedPercent(latestRollbackOutcome.value?.comparison?.handoff_requested_rate_delta || 0)}` : ''}`
      : 'No rollback outcome summary is available yet.',
  },
]);
const currentBusinessHoursConfig = computed(
  () => currentChannelRuntime.value?.business_hours || {}
);
const currentRegionComplianceConfig = computed(
  () => currentChannelRuntime.value?.region_compliance || {}
);
const selectedInboxBusinessHoursSummary = computed(() => {
  const inbox = selectedInboxRecord.value || {};
  const workingHoursEnabled = Boolean(inbox?.working_hours_enabled);
  return {
    enabled: workingHoursEnabled,
    timezone: inbox?.timezone || 'UTC',
    outOfOfficeMessage: String(inbox?.out_of_office_message || '').trim(),
  };
});
const normalizeComplianceProfile = value =>
  ['standard', 'strict', 'regulated'].includes(String(value || '').trim().toLowerCase())
    ? String(value || '').trim().toLowerCase()
    : 'standard';
const normalizeRegionCountryCode = value =>
  ['MY', 'ID', 'SG', 'TH', 'VN', 'PH'].includes(String(value || '').trim().toUpperCase())
    ? String(value || '').trim().toUpperCase()
    : '';
const normalizeRegionComplianceBinding = binding => {
  const row = binding || {};
  const countryCode = normalizeRegionCountryCode(row.country_code || row.countryCode) || 'MY';
  const countryLabel =
    regionCountryOptions.find(option => option.value === countryCode)?.label || countryCode;
  return {
    id: row.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    country_code: countryCode,
    country_name: String(row.country_name || row.countryName || countryLabel).trim() || countryLabel,
    compliance_profile: normalizeComplianceProfile(
      row.compliance_profile || row.complianceProfile
    ),
  };
};
const resolveInboxName = id => {
  const num = Number(id);
  if (!Number.isFinite(num)) return '';
  const inbox = inboxById.value?.(num) || {};
  return inbox?.name || String(num);
};

const connectedInboxStrategyRows = computed(() =>
  connectedInboxes.value.map(inbox => {
    const fullInbox = inboxById.value?.(Number(inbox.id)) || inbox || {};
    const explicitBinding = Array(currentChannelRuntime.value?.inbox_bindings || []).find(
      row => Number(row?.inbox_id || row?.inboxId) === Number(inbox.id)
    );
    const settings =
      fullInbox?.captain_ai_settings ||
      fullInbox?.captainAiSettings ||
      fullInbox?.additional_attributes ||
      fullInbox?.additionalAttributes ||
      {};
    const overrideMode = (
      settings?.captain_ai_override_mode ??
      settings?.captainAiOverrideMode ??
      'inherit'
    )
      .toString()
      .trim();
    const overrideReason = (
      settings?.captain_ai_override_reason ??
      settings?.captainAiOverrideReason ??
      ''
    )
      .toString()
      .trim();
    const explicitMode = normalizeReplyMode(
      explicitBinding?.reply_mode || explicitBinding?.replyMode,
      'inherit'
    );
    const effectiveMode =
      explicitMode && explicitMode !== 'inherit'
        ? explicitMode
        : overrideMode && overrideMode !== 'inherit'
          ? overrideMode
          : replyMode.value;

    return {
      id: Number(inbox.id),
      name: inbox.name || fullInbox.name || `Inbox ${inbox.id}`,
      channelType: inbox.channel_type || fullInbox.channel_type || 'channel',
      overrideMode: explicitMode && explicitMode !== 'inherit' ? explicitMode : overrideMode,
      overrideReason,
      effectiveMode,
      forcedReplyLanguage: normalizeRuntimeLanguage(
        explicitBinding?.forced_reply_language || explicitBinding?.forcedReplyLanguage
      ),
      communicationStyleMode: normalizeCommunicationStyle(
        explicitBinding?.communication_style_mode || explicitBinding?.communicationStyleMode
      ),
    };
  })
);

const channelOverrideCount = computed(
  () =>
    connectedInboxStrategyRows.value.filter(
      row => row.overrideMode && row.overrideMode !== 'inherit'
    ).length
);

const selectedInboxEffectiveMode = computed(() =>
  aiOverrideMode.value && aiOverrideMode.value !== 'inherit'
    ? aiOverrideMode.value
    : replyMode.value
);

const perChannelStrategyCards = computed(() => [
  {
    label: 'Assistant default',
    value: humanReplyModeLabel(replyMode.value),
    note: 'This is the fallback runtime mode for channels that do not force a safer inbox override.',
  },
  {
    label: 'Inbox overrides',
    value: channelOverrideCount.value,
    note:
      channelOverrideCount.value > 0
        ? 'One or more connected channels already force a different runtime mode.'
        : 'All connected channels currently inherit the assistant default mode.',
  },
  {
    label: 'Segment runtime',
    value: segmentRuntimeBindings.value.length,
    note:
      segmentRuntimeBindings.value.length > 0
        ? 'Language or VIP segments now override the assistant default when they match.'
        : 'No deploy-level segment runtime rules are active yet.',
  },
  {
    label: 'Business-hours rule',
    value: businessHoursEnabled.value ? 'On' : 'Off',
    note: businessHoursEnabled.value
      ? `${businessHoursReplyModeOptions.find(option => option.value === outsideBusinessHoursReplyMode.value)?.label || outsideBusinessHoursReplyMode.value} when the inbox is outside business hours.`
      : 'Outside-hours runtime still follows the normal deploy path.',
  },
  {
    label: 'Region compliance',
    value: regionComplianceBindings.value.length,
    note:
      regionComplianceBindings.value.length > 0
        ? `${regionComplianceBindings.value.length} market rule${regionComplianceBindings.value.length === 1 ? '' : 's'} can tighten compliance by country.`
        : 'All markets currently inherit the default compliance profile.',
  },
]);

const releaseOwnerCards = computed(() => [
  {
    label: 'Runtime gate',
    value: runtimeStatus.value === 'active' ? 'Open' : 'Closed',
    note:
      runtimeStatus.value === 'active'
        ? 'Runtime is active and can serve connected channels.'
        : 'Runtime is inactive, so nothing is live yet.',
  },
  {
    label: 'Default delivery',
    value: humanReplyModeLabel(replyMode.value),
    note: 'This is the assistant-level delivery contract new inboxes inherit.',
  },
  {
    label: 'Live channels',
    value: connectedInboxes.value.length,
    note:
      connectedInboxes.value.length > 0
        ? 'These inboxes are the real rollout surface for this assistant.'
        : 'No inbox is connected, so there is no rollout surface yet.',
  },
  {
    label: 'Forced safer channels',
    value: channelOverrideCount.value,
    note:
      channelOverrideCount.value > 0
        ? 'Some inboxes override the assistant default to a safer runtime mode.'
        : 'All inboxes currently inherit the same assistant default.',
  },
  {
    label: 'Release review',
    value: currentReleaseReviewStatusLabel.value,
    note: activeReleaseReviewSummary.value,
  },
  {
    label: 'Batch baseline',
    value: activeBatchBaseline.value
      ? humanBatchBaselineLevel(activeBatchBaseline.value.overall_level)
      : 'Missing',
    note: activeBatchBaselineSummary.value,
  },
]);

const releaseChecklist = computed(() => {
  const items = [];
  if (deploySeedReadyForRelease.value) {
    items.push({
      title: 'Repair confidence already passed validation',
      body:
        deploySeedContext.value.confidenceRuns
          ? `${deploySeedContext.value.confidenceRuns} recent validation run${deploySeedContext.value.confidenceRuns === '1' ? '' : 's'} were strong enough to open release review.${deploySeedContext.value.confidenceTitle ? ` ${deploySeedContext.value.confidenceTitle}.` : ''}`
          : 'A validated repair bundle sent you here for release review.',
      tone: 'info',
    });
  }
  if (seededReleaseBundle.value.enabled) {
    items.push({
      title:
        currentReleaseReviewStatus.value === 'released'
          ? 'Latest release decision is captured'
          : currentReleaseReviewStatus.value === 'rolled_back'
            ? 'Rollback decision is captured'
            : currentReleaseReviewStatus.value === 'reviewing'
              ? 'Release snapshot is already open'
              : 'Capture a release snapshot for this repair',
      body:
        seededReleaseBundle.value.focusLabel ||
        seededReleaseBundle.value.title ||
        seededReleaseBundle.value.validationSummary ||
        'Use the panel below to store the release review decision for this validated repair bundle.',
      tone:
        currentReleaseReviewStatus.value === 'rolled_back'
          ? 'warning'
          : 'info',
    });
  }
  if (activeBatchBaseline.value) {
    const totalCases = Number(activeBatchBaseline.value.total_cases || 0);
    items.push({
      title:
        activeBatchBaseline.value.overall_level === 'ready'
          ? 'Durable batch baseline cleared release checks'
          : activeBatchBaseline.value.overall_level === 'watch'
            ? 'Durable batch baseline still needs rollout review'
            : activeBatchBaseline.value.overall_level === 'blocked'
              ? 'Durable batch baseline still has release blockers'
              : 'Durable batch baseline captured',
      body:
        totalCases > 0
          ? `${activeBatchBaseline.value.title || 'Batch baseline'} ran ${totalCases} case${totalCases === 1 ? '' : 's'}${activeBatchBaseline.value.ran_at ? ` • ${formatDateTime(activeBatchBaseline.value.ran_at)}` : ''}.${activeBatchBaseline.value.top_blocker ? ` ${activeBatchBaseline.value.top_blocker}.` : activeBatchBaseline.value.top_watch_signal ? ` ${activeBatchBaseline.value.top_watch_signal}.` : ''}`
          : activeBatchBaselineSummary.value,
      tone:
        activeBatchBaseline.value.overall_level === 'blocked'
          ? 'warning'
          : 'info',
    });
  }
  items.push({
    title:
      runtimeStatus.value === 'active'
        ? 'Runtime is active'
        : 'Activate runtime before release',
    body:
      runtimeStatus.value === 'active'
        ? 'The AI is allowed to run for connected channels.'
        : 'The runtime gate is still closed, so configuration changes are not live.',
    tone: runtimeStatus.value === 'active' ? 'info' : 'warning',
  });
  items.push({
    title:
      connectedInboxes.value.length > 0
        ? 'Channels are connected'
        : 'Connect at least one inbox',
    body:
      connectedInboxes.value.length > 0
        ? `${connectedInboxes.value.length} inbox${connectedInboxes.value.length === 1 ? '' : 'es'} currently point at this assistant.`
        : 'Without a connected inbox, there is nowhere to release this assistant.',
    tone: connectedInboxes.value.length > 0 ? 'info' : 'warning',
  });
  if (channelOverrideCount.value > 0) {
    items.push({
      title: 'Review safer channel overrides',
      body: `${channelOverrideCount.value} inbox${channelOverrideCount.value === 1 ? '' : 'es'} already force a safer mode than the assistant default.`,
      tone: 'info',
    });
  }
  if (businessHoursEnabled.value) {
    items.push({
      title: 'Business-hours fallback is active',
      body:
        outsideBusinessHoursResponseBehavior.value === 'inbox_out_of_office_message'
          ? 'Outside business hours can now switch to the inbox out-of-office message.'
          : 'Outside business hours can now force a safer deploy reply mode.',
      tone: 'info',
    });
  }
  if (handoffEnabled.value && handoffAssignStrategy.value === 'specific') {
    items.push({
      title: 'Specific handoff routing is active',
      body: `${handoffAssignAgentIds.value.length} assignee${handoffAssignAgentIds.value.length === 1 ? '' : 's'} selected for direct handoff routing.`,
      tone: handoffAssignAgentIds.value.length ? 'info' : 'warning',
    });
  }
  if (seededReleaseBundle.value.rollbackTrigger) {
    items.push({
      title: 'Rollback trigger to watch',
      body: seededReleaseBundle.value.rollbackTrigger,
      tone: 'warning',
    });
  }
  return items.slice(0, 5);
});
const versionPublishChecklist = computed(() => {
  const items = [];
  if (publishedVersionId.value) {
    items.push({
      title: `Live version #${publishedVersionRow.value?.version_number || publishedVersionId.value}`,
      body:
        publishedVersionRow.value?.reason ||
        'This is the current published baseline used by default.',
      tone: 'info',
    });
  } else {
    items.push({
      title: 'No live version baseline',
      body: 'Open version history and publish a stable snapshot before wider rollout.',
      tone: 'warning',
    });
  }
  if (draftVersionId.value && draftVersionId.value !== publishedVersionId.value) {
    items.push({
      title: `Draft version #${draftVersionRow.value?.version_number || draftVersionId.value} is ahead`,
      body:
        draftVersionRow.value?.reason ||
        'A newer config snapshot exists and still needs publish review.',
      tone: 'warning',
    });
  }
  if (reviewVersionCount.value > 0) {
    items.push({
      title: `${reviewVersionCount.value} snapshot${reviewVersionCount.value === 1 ? '' : 's'} in review`,
      body: 'Use version history to publish, grey-release, or rollback from those review candidates.',
      tone: 'info',
    });
  }
  if (rolloutRows.value.length > 0) {
    items.push({
      title: `${rolloutRows.value.length} inbox rollout${rolloutRows.value.length === 1 ? '' : 's'} active`,
      body: 'Some inboxes are pinned to a specific version instead of the global live baseline.',
      tone: 'info',
    });
  }
  if (releaseReadinessSummary.value.latest_review_snapshot?.bundle_title) {
    items.push({
      title: 'Latest release snapshot is visible',
      body: `${releaseReadinessSummary.value.latest_review_snapshot.bundle_title}${releaseReadinessSummary.value.latest_review_snapshot.reviewed_at ? ` • ${formatDateTime(releaseReadinessSummary.value.latest_review_snapshot.reviewed_at)}` : ''}`,
      tone: 'info',
    });
  }
  return items.slice(0, 4);
});

const pageWarnings = computed(() =>
  Object.values(loadWarnings.value).filter(Boolean)
);

const resolveLoadMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const focusSeededDeployTarget = async () => {
  if (deploySeedContext.value.inboxId) {
    selectedInboxId.value = deploySeedContext.value.inboxId;
  }
  await nextTick();

  if (deploySeedContext.value.focus === 'release') {
    releaseSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    return;
  }

  if (deploySeedContext.value.focus === 'inbox_controls') {
    inboxControlsSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    return;
  }

  if (deploySeedContext.value.focus === 'business_hours') {
    businessHoursSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    return;
  }

  if (deploySeedContext.value.focus === 'region_compliance') {
    regionComplianceSectionRef.value?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'start',
    });
    return;
  }

  channelStrategySectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};

const clearDeploySeed = async () => {
  const nextQuery = { ...(route.query || {}) };
  DEPLOY_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  await router.replace({ query: nextQuery });
};
const applyRollbackActionSeed = async action => {
  const nextQuery = { ...(route.query || {}) };
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  DEPLOY_BATCH_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  nextQuery.deploy_seed = '1';
  nextQuery.deploy_seed_source = 'Rollback operator';
  nextQuery.deploy_seed_focus = 'release';
  nextQuery.deploy_seed_reason = String(action?.kind || '').trim() || 'rollback_operator_action';
  const session = buildNextDeploySessionContext({
    current: normalizeRollbackSessionContext({
      sessionId: action?.session_id || action?.sessionId,
      sessionLabel: action?.session_label || action?.sessionLabel,
      sessionStep: action?.session_step || action?.sessionStep,
      sessionOrigin: action?.session_origin || action?.sessionOrigin,
    }),
    nextWorkspace: 'Deploy',
  });
  nextQuery.deploy_seed_session_id = session.sessionId;
  nextQuery.deploy_seed_session_label = session.sessionLabel;
  nextQuery.deploy_seed_session_step = String(session.sessionStep || 1);
  nextQuery.deploy_seed_session_origin = session.sessionOrigin || 'Deploy';
  if (session.sessionId) {
    nextQuery.rollback_session_key = session.sessionId;
  }
  if (action?.session_item_key || action?.sessionItemKey) {
    const sessionItemKey = String(action?.session_item_key || action?.sessionItemKey || '').trim();
    nextQuery.deploy_seed_session_item_key = sessionItemKey;
    nextQuery.rollback_session_item_key = sessionItemKey;
  }

  if (action?.bundle_title || action?.bundle_label) {
    nextQuery.deploy_seed_bundle = '1';
    nextQuery.deploy_seed_title = action?.bundle_title || action?.bundle_label || '';
  }
  if (action?.focus_label) nextQuery.deploy_seed_focus_label = action.focus_label;
  if (action?.recommendation_code) {
    nextQuery.deploy_seed_recommendation_code = action.recommendation_code;
  }
  if (action?.batch_baseline_key) {
    nextQuery.deploy_seed_batch_baseline_key = action.batch_baseline_key;
  }
  if (action?.batch_baseline_title) {
    nextQuery.deploy_seed_batch_title = action.batch_baseline_title;
  }

  await router.replace({ query: nextQuery });
};
const openRollbackTestWorkspace = async action => {
  const nextQuery = { ...(route.query || {}), tab: 'test_preview' };
  DEPLOY_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);

  nextQuery.test_source = 'Rollback operator';
  nextQuery.test_reason = String(action?.intent_label || action?.label || '').trim();
  nextQuery.test_query = String(
    action?.bundle_label || action?.focus_label || action?.target_version_label || ''
  ).trim();
  const session = buildNextDeploySessionContext({
    current: normalizeRollbackSessionContext({
      sessionId: action?.session_id || action?.sessionId,
      sessionLabel: action?.session_label || action?.sessionLabel,
      sessionStep: action?.session_step || action?.sessionStep,
      sessionOrigin: action?.session_origin || action?.sessionOrigin,
    }),
    nextWorkspace: 'Test',
  });

  const compareBundle = buildRollbackFollowupBundle(action);
  if (compareBundle.enabled) {
    nextQuery.test_compare_bundle = '1';
    if (compareBundle.title) nextQuery.test_compare_title = compareBundle.title;
    if (compareBundle.focusLabel) nextQuery.test_compare_focus_label = compareBundle.focusLabel;
    if (compareBundle.recommendationCode) {
      nextQuery.test_compare_recommendation_code = compareBundle.recommendationCode;
    }
    if (compareBundle.ownerWorkspace) {
      nextQuery.test_compare_owner_workspace = compareBundle.ownerWorkspace;
    }
    if (compareBundle.ownerActionTitle) {
      nextQuery.test_compare_owner_action_title = compareBundle.ownerActionTitle;
    }
    if (compareBundle.validationSummary) {
      nextQuery.test_compare_validation_summary = compareBundle.validationSummary;
    }
    if (compareBundle.stage) nextQuery.test_compare_stage = compareBundle.stage;
  }

  if (action?.intent_label) nextQuery.test_followup_intent = action.intent_label;
  if (action?.target_version_label) {
    nextQuery.test_followup_target_version_label = action.target_version_label;
  }
  if (action?.compare_version_label || action?.previous_version_label) {
    nextQuery.test_followup_compare_version_label =
      action.compare_version_label || action.previous_version_label;
  }
  nextQuery.test_followup_session_id = session.sessionId;
  nextQuery.test_followup_session_label = session.sessionLabel;
  nextQuery.test_followup_session_step = String(session.sessionStep || 1);
  nextQuery.test_followup_session_origin = session.sessionOrigin || 'Deploy';
  if (session.sessionId) {
    nextQuery.rollback_session_key = session.sessionId;
  }
  if (action?.session_item_key || action?.sessionItemKey) {
    const sessionItemKey = String(action?.session_item_key || action?.sessionItemKey || '').trim();
    nextQuery.test_followup_session_item_key = sessionItemKey;
    nextQuery.rollback_session_item_key = sessionItemKey;
  }

  await router.replace({ query: nextQuery });
};
const openRollbackAnalyzeWorkspace = async action => {
  const nextQuery = { ...(route.query || {}), tab: 'analyze_decision_log', analyze_seed: '1' };
  DEPLOY_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);

  nextQuery.analyze_source = 'Rollback operator';
  nextQuery.analyze_time_window = '7d';
  const session = buildNextDeploySessionContext({
    current: normalizeRollbackSessionContext({
      sessionId: action?.session_id || action?.sessionId,
      sessionLabel: action?.session_label || action?.sessionLabel,
      sessionStep: action?.session_step || action?.sessionStep,
      sessionOrigin: action?.session_origin || action?.sessionOrigin,
    }),
    nextWorkspace: 'Analyze',
  });
  if (action?.intent_label) nextQuery.analyze_followup_intent = action.intent_label;
  if (action?.target_version_label) {
    nextQuery.analyze_followup_target_version_label = action.target_version_label;
  }
  if (action?.compare_version_label || action?.previous_version_label) {
    nextQuery.analyze_followup_compare_version_label =
      action.compare_version_label || action.previous_version_label;
  }
  nextQuery.analyze_followup_session_id = session.sessionId;
  nextQuery.analyze_followup_session_label = session.sessionLabel;
  nextQuery.analyze_followup_session_step = String(session.sessionStep || 1);
  nextQuery.analyze_followup_session_origin = session.sessionOrigin || 'Deploy';
  if (session.sessionId) {
    nextQuery.rollback_session_key = session.sessionId;
  }
  if (action?.session_item_key || action?.sessionItemKey) {
    const sessionItemKey = String(action?.session_item_key || action?.sessionItemKey || '').trim();
    nextQuery.analyze_followup_session_item_key = sessionItemKey;
    nextQuery.rollback_session_item_key = sessionItemKey;
  }

  await router.replace({ query: nextQuery });
};
const applyBatchBaselineSeed = async baseline => {
  const nextQuery = { ...(route.query || {}) };
  DEPLOY_BATCH_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });

  if (baseline) {
    nextQuery.deploy_seed_batch_baseline_key = baseline.baseline_key || '';
    nextQuery.deploy_seed_batch_set_key = baseline.set_key || '';
    nextQuery.deploy_seed_batch_title = baseline.title || '';
    nextQuery.deploy_seed_batch_overall_level = baseline.overall_level || '';
    nextQuery.deploy_seed_batch_total_cases = baseline.total_cases || '';
    nextQuery.deploy_seed_batch_ready_cases = baseline.ready_cases || '';
    nextQuery.deploy_seed_batch_watch_cases = baseline.watch_cases || '';
    nextQuery.deploy_seed_batch_blocked_cases = baseline.blocked_cases || '';
    nextQuery.deploy_seed_batch_ran_at = baseline.ran_at || '';
    nextQuery.deploy_seed_batch_top_blocker = baseline.top_blocker || '';
    nextQuery.deploy_seed_batch_top_watch_signal = baseline.top_watch_signal || '';
  }

  await router.replace({ query: nextQuery });
};
const focusReleaseSnapshotHistory = async snapshot => {
  if (!snapshot) return;

  await router.replace({
    query: {
      ...(route.query || {}),
      deploy_seed: '1',
      deploy_seed_source: snapshot.source || 'Deploy',
      deploy_seed_focus: 'release',
      deploy_seed_reason: 'release_snapshot_history',
      deploy_seed_confidence_level: snapshot.confidence_level || '',
      deploy_seed_confidence_runs: snapshot.confidence_runs || '',
      deploy_seed_confidence_title: snapshot.confidence_title || '',
      deploy_seed_bundle: '1',
      deploy_seed_title: snapshot.bundle_title || '',
      deploy_seed_focus_label: snapshot.focus_label || '',
      deploy_seed_strategy_group_title: snapshot.strategy_group_title || '',
      deploy_seed_recommendation_code: snapshot.recommendation_code || '',
      deploy_seed_owner_workspace: snapshot.owner_workspace || '',
      deploy_seed_owner_action_title: snapshot.owner_action_title || '',
      deploy_seed_guidance_editor: snapshot.guidance_editor || '',
      deploy_seed_escalation_focus: snapshot.escalation_focus || '',
      deploy_seed_hotspot_summary: snapshot.hotspot_summary || '',
      deploy_seed_resolved_summary: snapshot.resolved_summary || '',
      deploy_seed_escalated_summary: snapshot.escalated_summary || '',
      deploy_seed_validation_summary: snapshot.validation_summary || '',
      deploy_seed_stage: snapshot.stage || '',
      deploy_seed_rollback_trigger: snapshot.rollback_trigger || '',
      deploy_seed_batch_baseline_key: snapshot.batch_baseline_key || '',
      deploy_seed_batch_set_key: snapshot.batch_set_key || '',
      deploy_seed_batch_title: snapshot.batch_title || '',
      deploy_seed_batch_overall_level: snapshot.batch_overall_level || '',
      deploy_seed_batch_total_cases: snapshot.batch_total_cases || '',
      deploy_seed_batch_ready_cases: snapshot.batch_ready_cases || '',
      deploy_seed_batch_watch_cases: snapshot.batch_watch_cases || '',
      deploy_seed_batch_blocked_cases: snapshot.batch_blocked_cases || '',
      deploy_seed_batch_ran_at: snapshot.batch_ran_at || '',
      deploy_seed_batch_top_blocker: snapshot.batch_top_blocker || '',
      deploy_seed_batch_top_watch_signal: snapshot.batch_top_watch_signal || '',
    },
  });
};
const focusBatchBaselineHistory = async baseline => {
  await applyBatchBaselineSeed(baseline);
};
const findRollbackActionSnapshot = action => {
  const snapshotId = Number(action?.release_snapshot_id || 0);
  const snapshotKey = String(action?.release_snapshot_key || '').trim();
  return (
    [...currentReleaseSnapshots.value, ...recentReleaseSnapshots.value].find(row => {
      if (snapshotId > 0 && Number(row?.id || 0) === snapshotId) return true;
      if (snapshotKey && String(row?.snapshot_key || '').trim() === snapshotKey) return true;
      return false;
    }) || null
  );
};
const findRollbackActionBaseline = action => {
  const baselineId = Number(action?.batch_baseline_id || 0);
  const baselineKey = String(action?.batch_baseline_key || '').trim();
  return (
    [...currentBatchBaselines.value, ...recentBatchBaselines.value].find(row => {
      if (baselineId > 0 && Number(row?.id || 0) === baselineId) return true;
      if (baselineKey && String(row?.baseline_key || '').trim() === baselineKey) return true;
      return false;
    }) || null
  );
};
const runRollbackAction = async action => {
  const kind = String(action?.kind || '').trim();
  if (!kind) return;

  rollbackActionKey.value = buildRollbackActionToken(action);
  try {
    if (kind === 'open_release_snapshot_history') {
      const snapshot = findRollbackActionSnapshot(action);
      if (snapshot) {
        await focusReleaseSnapshotHistory(snapshot);
      } else {
        await applyRollbackActionSeed(action);
        releaseSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    if (kind === 'open_batch_baseline_history') {
      const baseline = findRollbackActionBaseline(action);
      await applyRollbackActionSeed(action);
      if (baseline) {
        await focusBatchBaselineHistory(baseline);
      } else {
        releaseSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    if (kind === 'focus_release_review') {
      await applyRollbackActionSeed(action);
      releaseSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (kind === 'open_version_history') {
      await applyRollbackActionSeed(action);
      openVersionHistoryDialog({
        ...action,
        session_item_key:
          String(action?.session_item_key || action?.sessionItemKey || '').trim() ||
          currentRollbackSessionItemKey.value,
        ...buildNextDeploySessionContext({
          current: normalizeRollbackSessionContext({
            sessionId: action?.session_id || action?.sessionId,
            sessionLabel: action?.session_label || action?.sessionLabel,
            sessionStep: action?.session_step || action?.sessionStep,
            sessionOrigin: action?.session_origin || action?.sessionOrigin,
          }),
          nextWorkspace: 'Versions',
        }),
      });
      return;
    }

    if (kind === 'open_test_workspace') {
      await openRollbackTestWorkspace(action);
      return;
    }

    if (kind === 'open_analyze_workspace') {
      await openRollbackAnalyzeWorkspace(action);
      return;
    }

    if (kind === 'refresh_ab_report') {
      await loadAbReportSafely();
      return;
    }

    if (kind === 'refresh_versions') {
      await loadVersionSummarySafely();
      return;
    }
  } finally {
    rollbackActionKey.value = '';
  }
};
const deleteReleaseSnapshotHistory = async snapshot => {
  if (!snapshot) return;

  historyActionKey.value = `release:${snapshot.id || snapshot.snapshot_key}`;
  try {
    if (snapshot.id) {
      await AiCloserAPI.deleteReleaseSnapshot(assistantId.value, snapshot.id);
      await loadVersionSummary();
    } else {
      const nextSnapshots = currentReleaseSnapshots.value.filter(
        row => row.snapshot_key !== snapshot.snapshot_key
      );
      await saveReleaseLoopConfig({ review_snapshots: nextSnapshots });
      await loadVersionSummarySafely();
    }

    if (releaseReviewEntry.value?.snapshot_key === snapshot.snapshot_key) {
      releaseReviewEntry.value = null;
      releaseReviewNote.value = '';
    }
    if (
      buildReleaseReviewSnapshotKey(seededReleaseBundle.value) ===
      snapshot.snapshot_key
    ) {
      syncReleaseReviewFromSeed();
    }
    alert('Release snapshot removed.');
  } catch (error) {
    alert(error?.response?.data?.error || error?.message || 'Failed to remove release snapshot');
  } finally {
    historyActionKey.value = '';
  }
};
const deleteBatchBaselineHistory = async baseline => {
  if (!baseline) return;

  historyActionKey.value = `baseline:${baseline.id || baseline.baseline_key}`;
  try {
    if (baseline.id) {
      await AiCloserAPI.deleteBatchBaseline(assistantId.value, baseline.id);
      await loadVersionSummary();
    } else {
      const nextBaselines = currentBatchBaselines.value.filter(
        row => row.baseline_key !== baseline.baseline_key
      );
      await saveReleaseLoopConfig({ batch_baselines: nextBaselines });
      await loadVersionSummarySafely();
    }

    if (seededBatchBaselineContext.value.baseline_key === baseline.baseline_key) {
      await applyBatchBaselineSeed(null);
    }
    alert('Batch baseline removed.');
  } catch (error) {
    alert(error?.response?.data?.error || error?.message || 'Failed to remove batch baseline');
  } finally {
    historyActionKey.value = '';
  }
};
const toggleRolloutInboxSelection = inboxId => {
  const normalizedId = Number(inboxId);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;

  if (selectedRolloutInboxIds.value.includes(normalizedId)) {
    selectedRolloutInboxIds.value = selectedRolloutInboxIds.value.filter(
      id => Number(id) !== normalizedId
    );
    return;
  }

  selectedRolloutInboxIds.value = [
    ...selectedRolloutInboxIds.value,
    normalizedId,
  ];
};
const selectAllRolloutInboxes = () => {
  selectedRolloutInboxIds.value = rolloutInboxOptions.value.map(row => Number(row.id));
};
const selectActiveRolloutInboxes = () => {
  selectedRolloutInboxIds.value = rolloutInboxOptions.value
    .filter(row => row.rolloutVersionId)
    .map(row => Number(row.id));
};
const clearRolloutInboxSelection = () => {
  selectedRolloutInboxIds.value = [];
};
const publishSelectedRollout = async () => {
  if (!assistantId.value) return;
  if (!selectedRolloutVersionId.value) {
    alert('Choose a version before publishing a rollout.');
    return;
  }
  if (!selectedRolloutInboxIds.value.length) {
    alert('Select at least one inbox for rollout.');
    return;
  }
  const trafficPercentage = Math.min(
    100,
    Math.max(1, Number(rolloutTrafficPercentage.value) || 100)
  );
  rolloutTrafficPercentage.value = trafficPercentage;

  isSavingRollout.value = true;
  try {
    await AiCloserAPI.publishConfig(assistantId.value, {
      versionId: Number(selectedRolloutVersionId.value),
      scope: 'inboxes',
      inboxIds: selectedRolloutInboxIds.value,
      trafficPercentage,
      releaseEvidence: activePublishEvidenceContext.value,
      note: releaseReviewNote.value,
    });
    await loadVersionSummary();
    await loadAbReportSafely();
    alert(
      `${trafficPercentage >= 100 ? 'Pinned' : `Assigned ${trafficPercentage}% of traffic for`} version #${selectedRolloutVersionRow.value?.version_number || selectedRolloutVersionId.value} to ${selectedRolloutInboxIds.value.length} inbox${selectedRolloutInboxIds.value.length === 1 ? '' : 'es'}.`
    );
  } catch (error) {
    alert(error?.response?.data?.error || error?.message || 'Failed to publish rollout');
  } finally {
    isSavingRollout.value = false;
  }
};
const clearSelectedRollouts = async () => {
  if (!assistantId.value) return;
  if (!selectedRolloutInboxIds.value.length) {
    alert('Select at least one inbox to clear rollout overrides.');
    return;
  }

  isSavingRollout.value = true;
  try {
    await AiCloserAPI.clearRollouts(assistantId.value, {
      inboxIds: selectedRolloutInboxIds.value,
      releaseEvidence: activePublishEvidenceContext.value,
      note: releaseReviewNote.value,
    });
    await loadVersionSummary();
    await loadAbReportSafely();
    alert(
      `Cleared rollout overrides for ${selectedRolloutInboxIds.value.length} inbox${selectedRolloutInboxIds.value.length === 1 ? '' : 'es'}.`
    );
    selectedRolloutInboxIds.value = [];
  } catch (error) {
    alert(error?.response?.data?.error || error?.message || 'Failed to clear rollout overrides');
  } finally {
    isSavingRollout.value = false;
  }
};
const syncReleaseReviewFromSeed = () => {
  if (!assistantId.value || !seededReleaseBundle.value.enabled) {
    releaseReviewEntry.value = null;
    releaseReviewNote.value = '';
    return;
  }
  const entry = findReleaseReviewSnapshot({
    snapshots: currentReleaseSnapshots.value,
    bundle: seededReleaseBundle.value,
  });
  releaseReviewEntry.value = entry;
  releaseReviewNote.value = String(entry?.note || deploySeedPrefillNote.value || '').trim();
};
const saveReleaseReview = async status => {
  if (!assistantId.value || !seededReleaseBundle.value.enabled) return;
  isSavingReleaseReview.value = true;
  try {
    const payload = {
      ...seededReleaseBundle.value,
      status,
      note: releaseReviewNote.value,
      source: deploySeedContext.value.source || 'Deploy',
      confidence_level: deploySeedContext.value.confidenceLevel,
      confidence_runs: deploySeedContext.value.confidenceRuns,
      confidence_title: deploySeedContext.value.confidenceTitle,
      rollback_trigger: seededReleaseBundle.value.rollbackTrigger,
      batch_baseline_key: activeBatchBaseline.value?.baseline_key,
      batch_set_key: activeBatchBaseline.value?.set_key,
      batch_title: activeBatchBaseline.value?.title,
      batch_ran_at: activeBatchBaseline.value?.ran_at,
      batch_overall_level: activeBatchBaseline.value?.overall_level,
      batch_total_cases: activeBatchBaseline.value?.total_cases,
      batch_ready_cases: activeBatchBaseline.value?.ready_cases,
      batch_watch_cases: activeBatchBaseline.value?.watch_cases,
      batch_blocked_cases: activeBatchBaseline.value?.blocked_cases,
      batch_top_blocker: activeBatchBaseline.value?.top_blocker,
      batch_top_watch_signal: activeBatchBaseline.value?.top_watch_signal,
      reviewed_at: new Date().toISOString(),
    };
    if (releaseReviewEntry.value?.id) {
      await AiCloserAPI.updateReleaseSnapshot(
        assistantId.value,
        releaseReviewEntry.value.id,
        payload
      );
    } else {
      await AiCloserAPI.createReleaseSnapshot(assistantId.value, payload);
    }
    await loadVersionSummary();
    syncReleaseReviewFromSeed();
    alert(`Release review marked ${humanReleaseReviewStatus(status).toLowerCase()}.`);
  } catch (error) {
    try {
      const reviewSnapshots = mergeReleaseReviewSnapshots({
        snapshots: currentReleaseLoop.value?.review_snapshots || [],
        snapshot: {
          ...seededReleaseBundle.value,
          status,
          note: releaseReviewNote.value,
          source: deploySeedContext.value.source || 'Deploy',
          confidenceLevel: deploySeedContext.value.confidenceLevel,
          confidenceRuns: deploySeedContext.value.confidenceRuns,
          confidenceTitle: deploySeedContext.value.confidenceTitle,
          rollbackTrigger: seededReleaseBundle.value.rollbackTrigger,
          batchBaselineKey: activeBatchBaseline.value?.baseline_key,
          batchSetKey: activeBatchBaseline.value?.set_key,
          batchTitle: activeBatchBaseline.value?.title,
          batchRanAt: activeBatchBaseline.value?.ran_at,
          batchOverallLevel: activeBatchBaseline.value?.overall_level,
          batchTotalCases: activeBatchBaseline.value?.total_cases,
          batchReadyCases: activeBatchBaseline.value?.ready_cases,
          batchWatchCases: activeBatchBaseline.value?.watch_cases,
          batchBlockedCases: activeBatchBaseline.value?.blocked_cases,
          batchTopBlocker: activeBatchBaseline.value?.top_blocker,
          batchTopWatchSignal: activeBatchBaseline.value?.top_watch_signal,
          reviewedAt: new Date().toISOString(),
        },
      });
      await saveReleaseLoopConfig({
        review_snapshots: reviewSnapshots,
      });
      releaseReviewEntry.value = findReleaseReviewSnapshot({
        snapshots: reviewSnapshots,
        bundle: seededReleaseBundle.value,
      });
    } catch {
      alert(error?.message || 'Failed to save release review snapshot');
      return;
    }
    await loadVersionSummarySafely();
    alert('Release review snapshot saved through config fallback.');
  } finally {
    isSavingReleaseReview.value = false;
  }
};
const clearReleaseReview = async () => {
  if (!assistantId.value || !seededReleaseBundle.value.enabled) return;
  isSavingReleaseReview.value = true;
  try {
    if (releaseReviewEntry.value?.id) {
      await AiCloserAPI.deleteReleaseSnapshot(
        assistantId.value,
        releaseReviewEntry.value.id
      );
      await loadVersionSummary();
    } else {
      const snapshotKey = buildReleaseReviewSnapshotKey(seededReleaseBundle.value);
      const nextSnapshots = normalizeReleaseReviewSnapshots(
        currentReleaseLoop.value?.review_snapshots || []
      ).filter(snapshot => snapshot.snapshot_key !== snapshotKey);
      await saveReleaseLoopConfig({
        review_snapshots: nextSnapshots,
      });
      await loadVersionSummarySafely();
    }
    releaseReviewEntry.value = null;
    releaseReviewNote.value = '';
    alert('Release review snapshot cleared.');
  } catch (error) {
    alert(error?.message || 'Failed to clear release review snapshot');
  } finally {
    isSavingReleaseReview.value = false;
  }
};

const syncAssistantDeployState = () => {
  const cfg = currentAssistant.value?.config || {};
  replyMode.value = normalizeReplyMode(
    currentChannelRuntime.value?.default_reply_mode || cfg.wlab_ai_reply_mode,
    'auto_reply'
  );
  const rawHandoffEnabled = cfg.handoff_enabled;
  handoffEnabled.value =
    rawHandoffEnabled === undefined || rawHandoffEnabled === null
      ? true
      : Boolean(rawHandoffEnabled);
  handoffMessage.value = cfg.handoff_message || '';
  const rawStrategy = (cfg.handoff_assign_strategy || 'inherit')
    .toString()
    .trim()
    .toLowerCase();
  handoffAssignStrategy.value = [
    'inherit',
    'none',
    'round_robin',
    'specific',
  ].includes(rawStrategy)
    ? rawStrategy
    : 'inherit';
  handoffAssignAgentIds.value = Array(cfg.handoff_assign_agent_ids || [])
    .map(id => Number(id))
    .filter(id => Number.isFinite(id) && id > 0);
  resolutionMessage.value = cfg.resolution_message || '';
};

const syncChannelRuntimeFromConfig = () => {
  segmentRuntimeBindings.value = Array(currentChannelRuntime.value?.segment_bindings || []).map(
    binding => normalizeSegmentBinding(binding)
  );
  businessHoursEnabled.value = currentBusinessHoursConfig.value?.enabled === true;
  outsideBusinessHoursReplyMode.value = normalizeReplyMode(
    currentBusinessHoursConfig.value?.outside_hours_reply_mode,
    'suggest'
  );
  outsideBusinessHoursResponseBehavior.value = ['normal_ai', 'inbox_out_of_office_message'].includes(
    String(currentBusinessHoursConfig.value?.outside_hours_response_behavior || '').trim()
  )
    ? String(currentBusinessHoursConfig.value?.outside_hours_response_behavior || '').trim()
    : 'normal_ai';
  defaultComplianceProfile.value = normalizeComplianceProfile(
    currentRegionComplianceConfig.value?.default_compliance_profile
  );
  regionComplianceBindings.value = Array(
    currentRegionComplianceConfig.value?.region_bindings || []
  ).map(binding => normalizeRegionComplianceBinding(binding));
};

const syncAutoResolveFromSelectedInbox = () => {
  const inbox = selectedInboxRecord.value || {};
  const fallbackSeconds = 60 * 60;
  const raw = inbox?.captain_auto_resolve_after_seconds;
  const seconds =
    raw === null || raw === undefined ? fallbackSeconds : Number(raw);
  autoResolveAfterMinutes.value =
    seconds <= 0 ? 0 : Math.max(1, Math.round(seconds / 60));
};

const syncOverrideFromSelectedInbox = () => {
  const inbox = selectedInboxRecord.value || {};
  const explicitBinding = Array(currentChannelRuntime.value?.inbox_bindings || []).find(
    row => Number(row?.inbox_id || row?.inboxId) === Number(selectedInboxId.value)
  );
  const settings =
    inbox?.captain_ai_settings ||
    inbox?.captainAiSettings ||
    inbox?.additional_attributes ||
    inbox?.additionalAttributes ||
    {};
  const rawMode = (
    explicitBinding?.reply_mode ??
    explicitBinding?.replyMode ??
    settings?.captain_ai_override_mode ??
    settings?.captainAiOverrideMode ??
    ''
  )
    .toString()
    .trim();
  aiOverrideMode.value = normalizeReplyMode(rawMode, 'inherit');
  aiOverrideReason.value = (
    settings?.captain_ai_override_reason ??
    settings?.captainAiOverrideReason ??
    ''
  ).toString();
  forcedReplyLanguage.value = normalizeRuntimeLanguage(
    explicitBinding?.forced_reply_language || explicitBinding?.forcedReplyLanguage
  );
  communicationStyleMode.value = normalizeCommunicationStyle(
    explicitBinding?.communication_style_mode || explicitBinding?.communicationStyleMode
  );
  const rawUserId =
    settings?.captain_ai_override_set_by_user_id ??
    settings?.captainAiOverrideSetByUserId ??
    null;
  aiOverrideMeta.value = {
    setAt: (
      settings?.captain_ai_override_set_at ??
      settings?.captainAiOverrideSetAt ??
      ''
    ).toString(),
    setByUserId:
      rawUserId === null || rawUserId === undefined ? null : Number(rawUserId),
  };
};

const refreshInboxes = () => {
  if (assistantId.value) {
    return store.dispatch('ailabInboxes/get', {
      assistantId: assistantId.value,
    });
  }
  return Promise.resolve();
};

const loadAssistant = () => {
  if (!assistantId.value) return Promise.resolve();
  return store.dispatch('ailabAssistants/show', assistantId.value);
};

const loadInboxRecords = () => store.dispatch('inboxes/get');
const loadAgents = () => store.dispatch('agents/get');

const loadAssistantSafely = async () => {
  try {
    await loadAssistant();
    loadWarnings.value.assistant = '';
  } catch (error) {
    loadWarnings.value.assistant = resolveLoadMessage(
      error,
      'Assistant details could not be refreshed.'
    );
  }
};

const loadConfigSafely = async () => {
  try {
    await loadConfig();
    loadWarnings.value.config = '';
  } catch (error) {
    loadWarnings.value.config = resolveLoadMessage(
      error,
      'Runtime config could not be refreshed.'
    );
  }
};

const refreshInboxesSafely = async () => {
  try {
    await refreshInboxes();
    loadWarnings.value.inboxes = '';
  } catch (error) {
    loadWarnings.value.inboxes = resolveLoadMessage(
      error,
      'Connected inboxes could not be loaded.'
    );
  }
};

const loadConfig = async () => {
  if (!assistantId.value) return;
  await store.dispatch('aiCloser/fetchConfig', {
    assistantId: assistantId.value,
  });
};
const loadVersionSummary = async () => {
  if (!assistantId.value) return;
  isLoadingVersionSummary.value = true;
  try {
    const response = await AiCloserAPI.getReleaseReadiness(assistantId.value);
    configVersionSummary.value = {
      versions: response?.data?.versions || [],
      published_version_id: response?.data?.published_version_id || null,
      draft_version_id: response?.data?.draft_version_id || null,
      inboxes: response?.data?.inboxes || [],
      rollouts: response?.data?.rollouts || {},
      rollout_details: response?.data?.rollout_details || [],
      publish_ledger: response?.data?.publish_ledger || [],
      release_readiness: response?.data?.release_readiness || {},
    };
  } finally {
    isLoadingVersionSummary.value = false;
  }
};
const loadAbReport = async ({ versionIds = abReportVersionIds.value } = {}) => {
  if (!assistantId.value) return;
  if (!versionIds.length) {
    abReport.value = { versions: [] };
    return;
  }

  isLoadingAbReport.value = true;
  try {
    const response = await AiCloserAPI.getAbReport(assistantId.value, {
      days: Number(abReportDays.value) || 7,
      versionIds,
    });
    abReport.value = response?.data || { versions: [] };
  } finally {
    isLoadingAbReport.value = false;
  }
};
const loadVersionSummarySafely = async () => {
  try {
    await loadVersionSummary();
    loadWarnings.value.versions = '';
  } catch (error) {
    loadWarnings.value.versions = resolveLoadMessage(
      error,
      'Config version history could not be refreshed.'
    );
  }
};
const loadAbReportSafely = async () => {
  try {
    await loadAbReport();
    loadWarnings.value.abReport = '';
  } catch (error) {
    loadWarnings.value.abReport = resolveLoadMessage(
      error,
      'A/B report could not be refreshed.'
    );
  }
};
const openVersionHistoryDialog = context => {
  configVersionsDialogRef.value?.open?.(context || null);
};

const refreshAll = async () => {
  await Promise.all([
    loadAssistantSafely(),
    loadConfigSafely(),
    refreshInboxesSafely(),
    loadVersionSummarySafely(),
  ]);
  await loadAbReportSafely();
  syncAssistantDeployState();
  await nextTick();
  if (connectedInboxes.value.length) {
    await loadInboxRecords();
    syncAutoResolveFromSelectedInbox();
    syncOverrideFromSelectedInbox();
  }
  if (handoffEnabled.value && handoffAssignStrategy.value === 'specific') {
    await loadAgents();
  }
};

const channelRuntimePayload = ({ overrideInboxId = null, includeSelectedInbox = false } = {}) => {
  const existing = currentChannelRuntime.value || {};
  const inboxBindings = Array(existing?.inbox_bindings || [])
    .map(binding => ({
      inbox_id: Number(binding?.inbox_id || binding?.inboxId),
      reply_mode: normalizeReplyMode(binding?.reply_mode || binding?.replyMode, 'inherit'),
      forced_reply_language: normalizeRuntimeLanguage(
        binding?.forced_reply_language || binding?.forcedReplyLanguage
      ),
      communication_style_mode: normalizeCommunicationStyle(
        binding?.communication_style_mode || binding?.communicationStyleMode
      ),
    }))
    .filter(binding => Number.isFinite(binding.inbox_id) && binding.inbox_id > 0);
  const nextInboxId = includeSelectedInbox
    ? Number(overrideInboxId || selectedInboxId.value)
    : Number(overrideInboxId);
  let nextInboxBindings = inboxBindings.filter(binding => binding.inbox_id !== nextInboxId);
  if (includeSelectedInbox && Number.isFinite(nextInboxId) && nextInboxId > 0) {
    nextInboxBindings = [
      ...nextInboxBindings,
      {
        inbox_id: nextInboxId,
        reply_mode: normalizeReplyMode(aiOverrideMode.value, 'inherit'),
        forced_reply_language: normalizeRuntimeLanguage(forcedReplyLanguage.value),
        communication_style_mode: normalizeCommunicationStyle(communicationStyleMode.value),
      },
    ];
  }

  return {
    default_reply_mode: normalizeReplyMode(replyMode.value, 'auto_reply'),
    business_hours: {
      enabled: Boolean(businessHoursEnabled.value),
      outside_hours_reply_mode: normalizeReplyMode(
        outsideBusinessHoursReplyMode.value,
        'suggest'
      ),
      outside_hours_response_behavior: ['normal_ai', 'inbox_out_of_office_message'].includes(
        String(outsideBusinessHoursResponseBehavior.value || '').trim()
      )
        ? String(outsideBusinessHoursResponseBehavior.value || '').trim()
        : 'normal_ai',
    },
    region_compliance: {
      default_compliance_profile: normalizeComplianceProfile(defaultComplianceProfile.value),
      region_bindings: regionComplianceBindings.value
        .map(binding => ({
          country_code: normalizeRegionCountryCode(binding.country_code),
          country_name:
            regionCountryOptions.find(option => option.value === normalizeRegionCountryCode(binding.country_code))
              ?.label || String(binding.country_name || '').trim(),
          compliance_profile: normalizeComplianceProfile(binding.compliance_profile),
        }))
        .filter(binding => binding.country_code)
        .sort((left, right) => left.country_code.localeCompare(right.country_code)),
    },
    inbox_bindings: nextInboxBindings
      .filter(
        binding =>
          binding.reply_mode !== 'inherit' ||
          binding.forced_reply_language ||
          binding.communication_style_mode
      )
      .map(binding => ({
        inbox_id: binding.inbox_id,
        reply_mode: binding.reply_mode,
        ...(binding.forced_reply_language
          ? { forced_reply_language: binding.forced_reply_language }
          : {}),
        ...(binding.communication_style_mode
          ? { communication_style_mode: binding.communication_style_mode }
          : {}),
      }))
      .sort((left, right) => left.inbox_id - right.inbox_id),
    segment_bindings: segmentRuntimeBindings.value
      .map(binding => ({
        segment_type: binding.segment_type,
        match_value:
          binding.segment_type === 'vip_conversation'
            ? 'true'
            : normalizeRuntimeLanguage(binding.match_value),
        reply_mode: normalizeReplyMode(binding.reply_mode, 'inherit'),
        forced_reply_language: normalizeRuntimeLanguage(binding.forced_reply_language),
        communication_style_mode: normalizeCommunicationStyle(
          binding.communication_style_mode
        ),
      }))
      .filter(
        binding =>
          binding.segment_type === 'vip_conversation' || binding.match_value
      )
      .filter(
        binding =>
          binding.reply_mode !== 'inherit' ||
          binding.forced_reply_language ||
          binding.communication_style_mode
      )
      .map(binding => ({
        segment_type: binding.segment_type,
        match_value: binding.match_value,
        reply_mode: binding.reply_mode,
        ...(binding.forced_reply_language
          ? { forced_reply_language: binding.forced_reply_language }
          : {}),
        ...(binding.communication_style_mode
          ? { communication_style_mode: binding.communication_style_mode }
          : {}),
      })),
  };
};

const savePromptSectionsPatch = async patch => {
  if (!assistantId.value) return;

  await store.dispatch('aiCloser/updateConfig', {
    assistantId: assistantId.value,
    data: {
      prompt_sections: {
        ...(aiCloserConfig.value?.prompt_sections || {}),
        ...patch,
      },
    },
  });
  await loadConfig();
  syncChannelRuntimeFromConfig();
  syncOverrideFromSelectedInbox();
  syncReleaseReviewFromSeed();
};

const saveChannelRuntimeConfig = async payload => {
  await savePromptSectionsPatch({
    channel_runtime: payload,
  });
};

const saveReleaseLoopConfig = async payload => {
  await savePromptSectionsPatch({
    release_loop: payload,
  });
};

const saveReplyMode = async () => {
  if (!assistantId.value) return;
  isSavingReplyMode.value = true;
  try {
    const nextCfg = {
      ...(currentAssistant.value?.config || {}),
      wlab_ai_reply_mode: replyMode.value,
    };
    await store.dispatch('ailabAssistants/update', {
      id: assistantId.value,
      assistant: { config: nextCfg },
    });
    await store.dispatch('ailabAssistants/show', assistantId.value);
    await saveChannelRuntimeConfig(channelRuntimePayload());
    alert('Reply mode updated');
  } catch (error) {
    alert(error?.message || 'Failed to update reply mode');
  } finally {
    isSavingReplyMode.value = false;
  }
};

const saveHandoffSettings = async () => {
  if (!assistantId.value) return;
  if (
    handoffEnabled.value &&
    handoffAssignStrategy.value === 'specific' &&
    !handoffAssignAgentIds.value.length
  ) {
    alert('Choose at least one assignee when using specific handoff routing');
    return;
  }
  isSavingHandoff.value = true;
  try {
    const nextCfg = {
      ...(currentAssistant.value?.config || {}),
      handoff_enabled: Boolean(handoffEnabled.value),
      handoff_message: handoffMessage.value || '',
      handoff_assign_strategy: handoffAssignStrategy.value || 'inherit',
      handoff_assign_agent_ids:
        handoffAssignStrategy.value === 'specific'
          ? Array(handoffAssignAgentIds.value || [])
              .map(id => Number(id))
              .filter(id => Number.isFinite(id) && id > 0)
          : [],
      resolution_message: resolutionMessage.value || '',
    };
    await store.dispatch('ailabAssistants/update', {
      id: assistantId.value,
      assistant: { config: nextCfg },
    });
    await loadAssistant();
    syncAssistantDeployState();
    alert('Handoff and resolution settings updated');
  } catch (error) {
    alert(error?.message || 'Failed to update handoff settings');
  } finally {
    isSavingHandoff.value = false;
  }
};

const saveRuntimeStatus = async nextStatus => {
  if (!assistantId.value) return;
  isSavingStatus.value = true;
  try {
    await store.dispatch('aiCloser/updateConfig', {
      assistantId: assistantId.value,
      data: { status: nextStatus },
    });
    alert(`Runtime status set to ${nextStatus}`);
    await loadConfig();
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to update runtime status'
    );
  } finally {
    isSavingStatus.value = false;
  }
};

const saveInboxRuntime = async () => {
  const inboxId = Number(selectedInboxId.value);
  if (!Number.isFinite(inboxId) || inboxId <= 0) return;
  isSavingInboxRuntime.value = true;
  try {
    const nextMinutes = Math.max(0, Number(autoResolveAfterMinutes.value) || 0);
    const nextSeconds =
      nextMinutes <= 0 ? 0 : Math.max(60, Math.round(nextMinutes * 60));
    const nextOverrideMode = (aiOverrideMode.value || 'inherit').toString().trim();
    const legacyOverrideMode = ['inherit', 'suggest', 'auto_suggest'].includes(
      nextOverrideMode
    )
      ? nextOverrideMode
      : 'inherit';
    const nextOverrideReason =
      nextOverrideMode === 'inherit' ? '' : (aiOverrideReason.value || '').toString();

    await store.dispatch('inboxes/updateInbox', {
      id: inboxId,
      formData: false,
      captain_auto_resolve_after_seconds: nextSeconds,
      captain_ai_settings: {
        captain_ai_override_mode: legacyOverrideMode,
        captain_ai_override_reason:
          legacyOverrideMode === 'inherit' ? '' : nextOverrideReason,
      },
    });
    await saveChannelRuntimeConfig(
      channelRuntimePayload({ overrideInboxId: inboxId, includeSelectedInbox: true })
    );
    await loadInboxRecords();
    syncAutoResolveFromSelectedInbox();
    syncOverrideFromSelectedInbox();
    alert('Inbox runtime controls updated');
  } catch (error) {
    alert(error?.message || 'Failed to update inbox runtime controls');
  } finally {
    isSavingInboxRuntime.value = false;
  }
};

const addSegmentRuntimeBinding = () => {
  segmentRuntimeBindings.value = [
    ...segmentRuntimeBindings.value,
    normalizeSegmentBinding({ segment_type: 'reply_language', match_value: 'en' }),
  ];
};

const removeSegmentRuntimeBinding = rowId => {
  segmentRuntimeBindings.value = segmentRuntimeBindings.value.filter(row => row.id !== rowId);
};

const saveSegmentRuntimeBindings = async () => {
  if (!assistantId.value) return;
  isSavingChannelStrategy.value = true;
  try {
    await saveChannelRuntimeConfig(channelRuntimePayload());
    alert('Segment runtime strategy updated');
  } catch (error) {
    alert(error?.message || 'Failed to update segment runtime strategy');
  } finally {
    isSavingChannelStrategy.value = false;
  }
};

const saveBusinessHoursStrategy = async () => {
  if (!assistantId.value) return;
  isSavingBusinessHours.value = true;
  try {
    await saveChannelRuntimeConfig(channelRuntimePayload());
    alert('Business-hours runtime strategy updated');
  } catch (error) {
    alert(error?.message || 'Failed to update business-hours runtime strategy');
  } finally {
    isSavingBusinessHours.value = false;
  }
};

const addRegionComplianceBinding = () => {
  regionComplianceBindings.value = [
    ...regionComplianceBindings.value,
    normalizeRegionComplianceBinding({}),
  ];
};

const removeRegionComplianceBinding = rowId => {
  regionComplianceBindings.value = regionComplianceBindings.value.filter(
    row => row.id !== rowId
  );
};

const saveRegionComplianceStrategy = async () => {
  if (!assistantId.value) return;
  isSavingChannelStrategy.value = true;
  try {
    await saveChannelRuntimeConfig(channelRuntimePayload());
    alert('Region and compliance strategy updated');
  } catch (error) {
    alert(error?.message || 'Failed to update region and compliance strategy');
  } finally {
    isSavingChannelStrategy.value = false;
  }
};

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value?.dialogRef?.open());
};

const handleDelete = () => {
  disconnectInboxDialog.value?.dialogRef?.open();
};

const handleAction = ({ action, id }) => {
  selectedInbox.value = ailabInboxes.value.find(inbox => id === inbox.id);
  nextTick(() => {
    if (action === 'delete') handleDelete();
  });
};

const handleCreateClose = async () => {
  dialogType.value = '';
  selectedInbox.value = null;
  await refreshAll();
};

watch(
  () => currentAssistant.value?.config,
  () => {
    syncAssistantDeployState();
  },
  { immediate: true, deep: true }
);

watch(
  () => aiCloserConfig.value?.prompt_sections?.channel_runtime,
  () => {
    syncAssistantDeployState();
    syncChannelRuntimeFromConfig();
    syncOverrideFromSelectedInbox();
  },
  { immediate: true, deep: true }
);

watch(
  () => connectedInboxes.value,
  async inboxes => {
    const currentId = Number(selectedInboxId.value);
    const stillExists = inboxes.some(inbox => Number(inbox.id) === currentId);
    if (!stillExists) {
      selectedInboxId.value = inboxes.length ? inboxes[0].id : null;
    } else if (!selectedInboxId.value && inboxes.length) {
      selectedInboxId.value = inboxes[0].id;
    }
    if (inboxes.length) {
      await loadInboxRecords();
      syncAutoResolveFromSelectedInbox();
      syncOverrideFromSelectedInbox();
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => selectedInboxId.value,
  () => {
    syncAutoResolveFromSelectedInbox();
    syncOverrideFromSelectedInbox();
  }
);
watch(
  () => [
    route.query?.rollback_session_search,
    route.query?.rollback_session_workspace,
    route.query?.rollback_session_hop,
  ],
  () => {
    syncRollbackSessionExplorerFromQuery();
  },
  { immediate: true }
);
watch(
  () => [
    rollbackSessionSearch.value,
    rollbackSessionWorkspaceFilter.value,
    rollbackSessionHopFilter.value,
  ],
  () => {
    syncRollbackSessionExplorerToQuery();
  }
);
watch(
  () => [
    selectedRollbackSessionKey.value,
    selectedRollbackItemKey.value,
    rollbackOperatorSessions.value.map(session => rollbackSessionUrlKey(session)).join('|'),
    rollbackOperatorSessions.value
      .flatMap(session => (session?.items || []).map(item => item?.itemKey))
      .join('|'),
  ],
  () => {
    if (!selectedRollbackSessionKey.value && !selectedRollbackItemKey.value) return;
    focusSelectedRollbackSession();
  },
  { immediate: true }
);

watch(
  () => route.query?.deploy_seed_open_versions,
  async value => {
    if (String(value || '').trim() !== '1') return;
    await maybeOpenVersionHistoryFromSeed();
  }
);
watch(
  () => [
    route.query?.deploy_seed,
    route.query?.deploy_seed_source,
    route.query?.deploy_seed_focus,
    route.query?.deploy_seed_reason,
    route.query?.deploy_seed_inbox_id,
  ],
  () => {
    if (!deploySeedContext.value.enabled) return;
    focusSeededDeployTarget();
  },
  { immediate: true }
);
watch(
  () => [
    assistantId.value,
    route.query?.deploy_seed_bundle,
    route.query?.deploy_seed_title,
    route.query?.deploy_seed_focus_label,
    route.query?.deploy_seed_strategy_group_title,
    route.query?.deploy_seed_recommendation_code,
    route.query?.deploy_seed_validation_summary,
    route.query?.deploy_seed_rollback_trigger,
    JSON.stringify(currentReleaseSnapshots.value || []),
  ],
  () => {
    syncReleaseReviewFromSeed();
  },
  { immediate: true }
);
watch(
  () => [
    rolloutCandidateVersions.value.map(row => row.id).join('|'),
    rolloutInboxOptions.value.map(row => row.id).join('|'),
    draftVersionId.value,
    publishedVersionId.value,
  ],
  () => {
    const validVersionIds = rolloutCandidateVersions.value.map(row => Number(row.id));
    if (!validVersionIds.includes(Number(selectedRolloutVersionId.value))) {
      selectedRolloutVersionId.value =
        draftVersionId.value || publishedVersionId.value || validVersionIds[0] || null;
    }

    const validInboxIds = new Set(rolloutInboxOptions.value.map(row => Number(row.id)));
    selectedRolloutInboxIds.value = selectedRolloutInboxIds.value.filter(id =>
      validInboxIds.has(Number(id))
    );
    if (!Number.isFinite(Number(rolloutTrafficPercentage.value))) {
      rolloutTrafficPercentage.value = 100;
    }
  },
  { immediate: true }
);
watch(
  () => abReportVersionIds.value.join('|'),
  async (nextValue, previousValue) => {
    if (!nextValue || nextValue === previousValue) return;
    await loadAbReportSafely();
  }
);

watch(
  [() => handoffEnabled.value, () => handoffAssignStrategy.value],
  async ([enabled, strategy]) => {
    if (enabled && strategy === 'specific') {
      await loadAgents();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await refreshAll();
  if (deploySeedContext.value.enabled) {
    await focusSeededDeployTarget();
  }
  await maybeOpenVersionHistoryFromSeed();
});
</script>

<template>
  <div class="fin-ai flex flex-col h-full min-h-0">
    <div class="fin-page-header-row deploy-header">
      <div>
        <h1 class="heading o__size-1">Deploy</h1>
        <p class="deploy-muted mt-1">
          Control where the AI is live, how it replies, and which config version is published.
        </p>
      </div>
      <button type="button" class="btn o__secondary" :disabled="isLoadingConfig || isFetchingInboxes" @click="refreshAll">
        Refresh deploy
      </button>
    </div>

    <div class="fin-page-body deploy-shell">
      <div v-if="pageWarnings.length" class="fin-soft-callout deploy-warning-stack">
        <div v-for="warning in pageWarnings" :key="warning" class="deploy-note">
          {{ warning }}
        </div>
      </div>

      <div
        v-if="deploySeedContext.enabled"
        class="deploy-card"
      >
        <div class="deploy-section-header">
          <div>
            <div class="deploy-card-title">Deploy seeded context</div>
            <div class="deploy-note">
              {{ deploySeedSummary || 'A rollout context was brought into Deploy.' }}
            </div>
            <div
              v-if="deploySeedBadges.length"
              class="flex flex-wrap gap-2 mt-2"
            >
              <span
                v-for="badge in deploySeedBadges"
                :key="badge.label"
                class="deploy-tag"
                :class="{ 'deploy-tag--muted': badge.tone !== 'primary' }"
              >
                {{ badge.label }}
              </span>
            </div>
            <div
              v-if="deploySeedContext.confidenceTitle || deploySeedContext.confidenceLevel"
              class="deploy-note mt-2"
            >
              Validation:
              {{ deploySeedContext.confidenceTitle || deploySeedContext.confidenceLevel }}
              <span v-if="deploySeedContext.source === 'Analyze'">
                • promoted from the Analyze repair board
              </span>
            </div>
            <div v-if="deploySeedPrefillNote" class="deploy-note mt-2">
              Prefill note: {{ deploySeedPrefillNote }}
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="btn o__secondary" @click="focusSeededDeployTarget">
              {{ deploySeedPrimaryActionLabel }}
            </button>
            <button type="button" class="btn o__secondary" @click="clearDeploySeed">
              Clear seeded context
            </button>
          </div>
        </div>
      </div>

      <div class="deploy-summary-grid">
        <div v-for="card in deployCards" :key="card.label" class="deploy-stat-card">
          <div class="deploy-label">{{ card.label }}</div>
          <div class="deploy-value">{{ card.value }}</div>
          <div class="deploy-note">{{ card.note }}</div>
        </div>
      </div>

      <div ref="releaseSectionRef" class="deploy-card">
        <div class="deploy-section-header">
          <div>
            <div class="deploy-card-title">Release owner snapshot</div>
            <div class="deploy-note">
              This is the current real go-live surface for the assistant: runtime
              gate, delivery contract, and connected channels.
            </div>
          </div>
        </div>
        <div class="deploy-summary-grid mt-3">
          <div
            v-for="card in releaseOwnerCards"
            :key="card.label"
            class="deploy-stat-card"
          >
            <div class="deploy-label">{{ card.label }}</div>
            <div class="deploy-value">{{ card.value }}</div>
            <div class="deploy-note">{{ card.note }}</div>
          </div>
        </div>
        <div class="deploy-list mt-3">
          <div
            v-for="item in releaseChecklist"
            :key="item.title"
            class="deploy-list-item"
          >
            <div>
              <div class="deploy-mode-title">{{ item.title }}</div>
              <div class="deploy-note">{{ item.body }}</div>
            </div>
            <span class="deploy-tag" :class="{ 'deploy-tag--muted': item.tone === 'warning' }">
              {{ item.tone === 'warning' ? 'Check' : 'Ready' }}
            </span>
          </div>
        </div>
        <div class="deploy-section-header mt-3">
          <div>
            <div class="deploy-card-title">Version and rollout history</div>
            <div class="deploy-note">
              Review the currently published baseline, any newer draft snapshot, and inbox-specific grey releases before wider rollout.
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="btn o__secondary"
              :disabled="isLoadingVersionSummary"
              @click="loadVersionSummarySafely"
            >
              {{ isLoadingVersionSummary ? 'Refreshing versions...' : 'Refresh versions' }}
            </button>
            <button type="button" class="btn o__secondary" @click="openVersionHistoryDialog">
              Open version history
            </button>
          </div>
        </div>
        <div class="deploy-summary-grid mt-3">
          <div
            v-for="card in versionSummaryCards"
            :key="card.label"
            class="deploy-stat-card"
          >
            <div class="deploy-label">{{ card.label }}</div>
            <div class="deploy-value">{{ card.value }}</div>
            <div class="deploy-note">{{ card.note }}</div>
          </div>
        </div>
        <div v-if="versionPublishChecklist.length" class="deploy-list mt-3">
          <div
            v-for="item in versionPublishChecklist"
            :key="item.title"
            class="deploy-list-item"
          >
            <div>
              <div class="deploy-mode-title">{{ item.title }}</div>
              <div class="deploy-note">{{ item.body }}</div>
            </div>
            <span class="deploy-tag" :class="{ 'deploy-tag--muted': item.tone === 'warning' }">
              {{ item.tone === 'warning' ? 'Review' : 'Track' }}
            </span>
          </div>
        </div>
        <div v-if="latestRollbackOutcome" class="deploy-card mt-3">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Rollback operator summary</div>
              <div class="deploy-note">
                Live post-rollback comparison between the restored version and the version that was rolled off.
              </div>
            </div>
            <div class="d-flex gap-2 align-items-center">
              <span class="deploy-tag">
                {{ humanRollbackOutcomeStatusLabel(latestRollbackOutcome.status) }}
              </span>
              <span v-if="latestRollbackOutcome.confidence_level" class="deploy-tag deploy-tag--muted">
                {{ humanRollbackConfidenceLabel(latestRollbackOutcome.confidence_level) }}
              </span>
            </div>
          </div>
          <div class="deploy-summary-grid mt-3">
            <div
              v-for="card in rollbackOperatorCards"
              :key="card.label"
              class="deploy-stat-card"
            >
              <div class="deploy-label">{{ card.label }}</div>
              <div class="deploy-value">{{ card.value }}</div>
              <div class="deploy-note">{{ card.note }}</div>
            </div>
          </div>
          <div class="deploy-note mt-3">
            {{ latestRollbackOutcome.summary }}
          </div>
          <div v-if="latestRollbackOutcome.operator_note" class="deploy-note mt-1">
            {{ latestRollbackOutcome.operator_note }}
          </div>
          <div
            v-if="latestRollbackOutcome.recommended_action_label"
            class="deploy-note mt-1"
          >
            Recommended next action: {{ latestRollbackOutcome.recommended_action_label }}
          </div>
          <div
            v-if="latestRollbackOutcome.confidence_note"
            class="deploy-note mt-1"
          >
            {{ latestRollbackOutcome.confidence_note }}
          </div>
          <div v-if="rollbackOperatorActions.length" class="flex flex-wrap gap-2 mt-3">
            <button
              v-for="action in rollbackOperatorActions"
              :key="buildRollbackActionToken(action)"
              type="button"
              class="btn o__secondary"
              :disabled="rollbackActionKey === buildRollbackActionToken(action)"
              @click="runRollbackAction(action)"
            >
              {{
                rollbackActionKey === buildRollbackActionToken(action)
                  ? 'Working...'
                  : action.label
              }}
            </button>
          </div>
          <div class="deploy-note mt-1">
            {{ latestRollbackOutcome.target_version_label || publishLedgerVersionLabel(latestRollbackOutcome.target_version_id, latestRollbackOutcome.target_version_number) }}
            <span v-if="latestRollbackOutcome.previous_version_label">
              vs {{ latestRollbackOutcome.previous_version_label }}
            </span>
            <span v-if="latestRollbackOutcome.bundle_label">
              • {{ latestRollbackOutcome.bundle_label }}
            </span>
            <span v-if="latestRollbackOutcome.batch_baseline_title">
              • baseline {{ latestRollbackOutcome.batch_baseline_title }}
            </span>
          </div>
          <div class="deploy-summary-grid mt-3">
            <div
              v-for="row in rollbackOutcomeSignalRows"
              :key="row.key"
              class="deploy-stat-card"
            >
              <div class="deploy-label">{{ row.label }}</div>
              <div class="deploy-value">{{ formatRollbackSignalValue(row) }}</div>
              <div class="deploy-note">
                {{ humanRollbackSignalSeverityLabel(row.severity) }}
                •
                Rolled-off: {{ formatRollbackSignalBaselineValue(row) }}
                • delta {{ formatRollbackSignalDelta(row) }}
              </div>
              <div v-if="row.explanation" class="deploy-note mt-1">
                {{ row.explanation }}
              </div>
            </div>
          </div>
          <div v-if="rollbackPlaybookItems.length" class="deploy-list mt-3">
            <div
              v-for="item in rollbackPlaybookItems"
              :key="item.key"
              class="deploy-list-item"
            >
              <div>
                <div class="deploy-mode-title">{{ item.label }}</div>
                <div class="deploy-note">{{ item.detail }}</div>
              </div>
              <div class="flex flex-wrap gap-2 align-items-center">
                <button
                  v-if="item.action?.kind"
                  type="button"
                  class="btn o__secondary"
                  :disabled="rollbackActionKey === buildRollbackActionToken(item.action)"
                  @click="runRollbackAction(item.action)"
                >
                  {{
                    rollbackActionKey === buildRollbackActionToken(item.action)
                      ? 'Working...'
                      : item.action.label
                  }}
                </button>
                <span class="deploy-tag" :class="{ 'deploy-tag--muted': item.status !== 'next' }">
                  {{ humanRollbackPlaybookStatusLabel(item.status) }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="rollbackEvidenceGaps.length" class="deploy-list mt-3">
            <div
              v-for="gap in rollbackEvidenceGaps"
              :key="gap.key"
              class="deploy-list-item"
            >
              <div>
                <div class="deploy-mode-title">{{ gap.label }}</div>
                <div class="deploy-note">{{ gap.detail }}</div>
              </div>
              <span class="deploy-tag deploy-tag--muted">
                {{ humanRollbackGapSeverityLabel(gap.severity) }}
              </span>
            </div>
          </div>
          <div v-if="bundleRollbackOutcomes.length" class="deploy-list mt-3">
            <div
              v-for="row in bundleRollbackOutcomes"
              :key="`${row.bundle_label || row.release_snapshot_key || row.target_version_id}-${row.rollback_created_at}`"
              class="deploy-list-item deploy-list-item--stacked"
            >
              <div class="deploy-mode-title">
                {{ row.bundle_label || row.target_version_label || 'Rollback bundle' }}
                <span class="deploy-note">
                  • {{ humanRollbackOperatorGroupLabel(row.operator_group) }}
                  • {{ humanRollbackOutcomeStatusLabel(row.status) }}
                  <span v-if="row.confidence_level">
                    • {{ humanRollbackConfidenceLabel(row.confidence_level) }}
                  </span>
                </span>
              </div>
              <div class="deploy-note mt-1">
                {{ row.summary }}
              </div>
              <div v-if="row.operator_note" class="deploy-note mt-1">
                {{ row.operator_note }}
              </div>
              <div
                v-if="row.recommended_action_label"
                class="deploy-note mt-1"
              >
                Next action: {{ row.recommended_action_label }}
              </div>
              <div class="deploy-note mt-1">
                {{ row.target_version_label || publishLedgerVersionLabel(row.target_version_id, row.target_version_number) }}
                <span v-if="row.previous_version_label">
                  vs {{ row.previous_version_label }}
                </span>
                <span v-if="row.batch_baseline_title">
                  • baseline {{ row.batch_baseline_title }}
                </span>
              </div>
              <div
                v-if="(row.helped_signals && row.helped_signals.length) || (row.risky_signals && row.risky_signals.length)"
                class="deploy-note mt-1"
              >
                <span v-if="row.helped_signals && row.helped_signals.length">
                  Helped by {{ row.helped_signals.join(', ') }}
                </span>
                <span
                  v-if="row.risky_signals && row.risky_signals.length"
                >
                  <span v-if="row.helped_signals && row.helped_signals.length"> • </span>
                  Still risky on {{ row.risky_signals.join(', ') }}
                </span>
              </div>
              <div
                v-if="row.evidence_gaps && row.evidence_gaps.length"
                class="deploy-note mt-1"
              >
                Gaps:
                {{
                  row.evidence_gaps
                    .map(gap => gap.label)
                    .join(' • ')
                }}
              </div>
              <div
                v-if="row.operator_actions && row.operator_actions.length"
                class="flex flex-wrap gap-2 mt-2"
              >
                <button
                  v-for="action in row.operator_actions.slice(0, 2)"
                  :key="`${row.rollback_created_at}-${buildRollbackActionToken(action)}`"
                  type="button"
                  class="btn o__secondary"
                  :disabled="rollbackActionKey === buildRollbackActionToken(action)"
                  @click="runRollbackAction(action)"
                >
                  {{
                    rollbackActionKey === buildRollbackActionToken(action)
                      ? 'Working...'
                      : action.label
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="rollbackOperatorSessions.length"
          class="deploy-card mt-3"
        >
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Rollback publish-session replay</div>
              <div class="deploy-note">
                Reconstruct the latest operator journey across Deploy, Analyze, Test, linked evidence notes, publish ledger hops, and version follow-through.
              </div>
            </div>
            <span class="deploy-tag">
              {{
                rollbackSessionExplorerFiltered
                  ? `${filteredRollbackOperatorSessions.length}/${rollbackOperatorSessions.length} sessions`
                  : `${rollbackOperatorSessions.length} sessions`
              }}
            </span>
          </div>
          <div class="deploy-field-grid mt-3">
            <label class="deploy-field">
              <span class="deploy-label">Search sessions</span>
              <input
                v-model="rollbackSessionSearch"
                type="text"
                class="deploy-input"
                placeholder="Session id, bundle, note, version"
              />
            </label>
            <label class="deploy-field">
              <span class="deploy-label">Workspace</span>
              <select v-model="rollbackSessionWorkspaceFilter" class="deploy-input">
                <option
                  v-for="option in rollbackSessionWorkspaceOptions"
                  :key="`workspace-${option}`"
                  :value="option"
                >
                  {{ option === 'all' ? 'All workspaces' : humanRollbackSessionExplorerWorkspaceLabel(option) }}
                </option>
              </select>
            </label>
            <label class="deploy-field">
              <span class="deploy-label">Hop kind</span>
              <select v-model="rollbackSessionHopFilter" class="deploy-input">
                <option
                  v-for="option in rollbackSessionHopOptions"
                  :key="`hop-${option}`"
                  :value="option"
                >
                  {{ humanRollbackSessionExplorerHopLabel(option) }}
                </option>
              </select>
            </label>
          </div>
          <div class="deploy-actions" style="justify-content: flex-start;">
            <button
              type="button"
              class="btn o__secondary"
              @click="copyCurrentRollbackExplorerDeepLink"
            >
              Copy current deep link
            </button>
            <button
              type="button"
              class="btn o__secondary"
              @click="copyCurrentRollbackExplorerHandoffNote"
            >
              Copy current handoff note
            </button>
            <button
              v-if="rollbackSessionExplorerFiltered"
              type="button"
              class="btn o__secondary"
              @click="clearRollbackSessionExplorerFilters"
            >
              Clear session filters
            </button>
          </div>
          <div class="deploy-note mt-2">
            {{ rollbackExplorerCurrentShareSummary }}
          </div>
          <div class="deploy-card deploy-card--nested mt-3">
            <div class="deploy-card-title">Current handoff preview</div>
            <div v-if="rollbackExplorerCurrentHandoffCard" class="deploy-handoff-grid mt-3">
              <div class="deploy-handoff-summary-block">
                <span
                  v-if="rollbackExplorerCurrentHandoffCard.decisionLabel"
                  class="deploy-tag"
                  :class="rollbackHandoffTagToneClass(rollbackExplorerCurrentHandoffCard.decisionTone)"
                >
                  {{ rollbackExplorerCurrentHandoffCard.decisionLabel }}
                </span>
                <span
                  v-if="rollbackExplorerCurrentHandoffCard.confidenceLabel"
                  class="deploy-tag"
                  :class="rollbackHandoffTagToneClass(rollbackExplorerCurrentHandoffCard.confidenceTone)"
                >
                  {{ rollbackExplorerCurrentHandoffCard.confidenceLabel }}
                </span>
                <span
                  class="deploy-tag"
                  :class="rollbackHandoffTagToneClass(rollbackExplorerCurrentHandoffCard.riskTone)"
                >
                  {{ rollbackExplorerCurrentHandoffCard.riskLabel }}
                </span>
                <span
                  v-for="badge in rollbackExplorerCurrentHandoffCard.badges || []"
                  :key="`handoff-preview-${badge.label}`"
                  class="deploy-tag"
                  :class="rollbackHandoffTagToneClass(badge.tone)"
                >
                  {{ badge.label }}
                </span>
              </div>
              <div
                class="deploy-handoff-keyline"
                :class="rollbackHandoffKeylineToneClass(rollbackExplorerCurrentHandoffCard.keylineTone)"
              >
                <span class="deploy-label">Decision Snapshot</span>
                <div class="deploy-note">
                  {{ rollbackExplorerCurrentHandoffCard.decisionLabel }}
                  <span v-if="rollbackExplorerCurrentHandoffCard.confidenceLabel">
                    • {{ rollbackExplorerCurrentHandoffCard.confidenceLabel }}
                  </span>
                  <span v-if="rollbackExplorerCurrentHandoffCard.riskLabel">
                    • {{ rollbackExplorerCurrentHandoffCard.riskLabel }}
                  </span>
                </div>
              </div>
              <div>
                <span class="deploy-label">Target</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.targetLabel }}</div>
              </div>
              <div>
                <span class="deploy-label">Summary</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.summary || 'Current rollback explorer handoff' }}</div>
              </div>
              <div v-if="rollbackExplorerCurrentHandoffCard.liveOutcomeLabel">
                <span class="deploy-label">Live Outcome</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.liveOutcomeLabel }}</div>
              </div>
              <div>
                <span class="deploy-label">Why This Matters</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.why }}</div>
              </div>
              <div>
                <span class="deploy-label">Suggested Next Step</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.next }}</div>
              </div>
              <div v-if="rollbackExplorerCurrentHandoffCard.sessionLabel">
                <span class="deploy-label">Session</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.sessionLabel }}</div>
              </div>
              <div v-if="rollbackExplorerCurrentHandoffCard.stepContext">
                <span class="deploy-label">Step Context</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.stepContext }}</div>
              </div>
              <div v-if="rollbackExplorerCurrentHandoffCard.filters">
                <span class="deploy-label">Explorer Filters</span>
                <div class="deploy-note">{{ rollbackExplorerCurrentHandoffCard.filters }}</div>
              </div>
              <div>
                <span class="deploy-label">Link</span>
                <div class="deploy-note deploy-handoff-preview">
                  {{ rollbackExplorerCurrentHandoffCard.link }}
                </div>
              </div>
            </div>
            <div v-else class="deploy-note mt-1">No rollback handoff preview is available yet.</div>
          </div>
          <div v-if="filteredRollbackOperatorSessions.length" class="deploy-list mt-3">
            <div
              v-for="session in filteredRollbackOperatorSessions"
              :key="buildRollbackSessionToggleKey(session)"
              :id="buildRollbackSessionDomId(session)"
              class="deploy-list-item deploy-list-item--stacked"
              :class="{ 'deploy-list-item--selected': rollbackSessionSelected(session) }"
            >
              <div class="deploy-mode-title">
                {{ session.sessionLabel || 'Rollback publish session' }}
              </div>
              <div
                v-if="rollbackSessionSelected(session) && !rollbackSessionMatchesExplorer(session)"
                class="deploy-note mt-1"
              >
                This selected session is pinned in view even though it falls outside the current filters.
              </div>
              <div
                v-if="rollbackOperatorSessionHeaderBadges(session).length"
                class="mt-2 flex flex-wrap gap-2"
              >
                <span
                  v-for="badge in rollbackOperatorSessionHeaderBadges(session)"
                  :key="`${buildRollbackSessionToggleKey(session)}-${badge.label}`"
                  class="deploy-tag"
                  :class="{ 'deploy-tag--muted': badge.tone !== 'primary' }"
                >
                  {{ badge.label }}
                </span>
              </div>
              <div
                v-if="rollbackOperatorSessionMeta(session)"
                class="deploy-note mt-2"
              >
                {{ rollbackOperatorSessionMeta(session) }}
              </div>
              <div class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted">
                <div
                  v-for="(item, index) in rollbackOperatorSessionEntries(session)"
                  :key="rollbackOperatorSessionStepKey(session, item, index)"
                  :id="buildRollbackJourneyItemDomId(item)"
                  :class="[
                    index > 0 ? 'mt-3 pt-3 border-top' : '',
                    rollbackJourneyItemSelected(item)
                      ? 'deploy-journey-item--selected'
                      : '',
                  ]"
                >
                  <div
                    v-if="rollbackJourneyItemSelected(item)"
                    class="deploy-note mb-1"
                  >
                    Deep-linked step
                  </div>
                  <div class="deploy-mode-title">
                    {{
                      item.actorActionLabel ||
                      (item.itemType === 'ledger_hop'
                        ? humanPublishLedgerEventLabel(item.row?.event_type)
                        : 'Evidence note')
                    }}
                  </div>
                  <div class="deploy-note mt-1">
                    {{
                      item.itemType === 'ledger_hop'
                        ? publishLedgerEntrySummary(item.row)
                        : item.entry?.body
                    }}
                  </div>
                  <div
                    v-if="item.itemType === 'ledger_hop' && item.summary"
                    class="deploy-note mt-1"
                  >
                    {{ item.summary }}
                  </div>
                  <div
                    v-if="rollbackOperatorSessionStepBadges(item).length"
                    class="mt-2 flex flex-wrap gap-2"
                  >
                    <span
                      v-for="badge in rollbackOperatorSessionStepBadges(item)"
                      :key="`${rollbackOperatorSessionStepKey(session, item, index)}-${badge.label}`"
                      class="deploy-tag"
                      :class="{ 'deploy-tag--muted': badge.tone !== 'primary' }"
                    >
                      {{ badge.label }}
                    </span>
                  </div>
                  <div
                    v-if="rollbackOperatorSessionStepMeta(item)"
                    class="deploy-note mt-1"
                  >
                    {{ rollbackOperatorSessionStepMeta(item) }}
                  </div>
                  <div class="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="btn o__secondary"
                      @click="selectRollbackJourneyItem(session, item)"
                    >
                      {{ rollbackJourneyItemSelected(item) ? 'Step link active' : 'Link step' }}
                    </button>
                    <button
                      type="button"
                      class="btn o__secondary"
                      @click="copyRollbackJourneyItemDeepLink(session, item)"
                    >
                      Copy step link
                    </button>
                    <button
                      type="button"
                      class="btn o__secondary"
                      @click="seedRollbackJourneyInDeploy(item)"
                    >
                      Seed Deploy
                    </button>
                    <button
                      type="button"
                      class="btn o__secondary"
                      @click="openRollbackJourneyInAnalyze(item)"
                    >
                      Open Analyze
                    </button>
                    <button
                      type="button"
                      class="btn o__secondary"
                      @click="openRollbackJourneyInTest(item)"
                    >
                      Open Test
                    </button>
                    <button
                      v-if="rollbackOperatorSessionSnapshotRecord(item)"
                      type="button"
                      class="btn o__secondary"
                      @click="focusRollbackJourneySnapshot(item)"
                    >
                      Open snapshot
                    </button>
                    <button
                      v-if="rollbackOperatorSessionBaselineRecord(item)"
                      type="button"
                      class="btn o__secondary"
                      @click="focusRollbackJourneyBaseline(item)"
                    >
                      Use baseline
                    </button>
                    <button
                      v-if="rollbackOperatorSessionSnapshotRecord(item) || rollbackOperatorSessionBaselineRecord(item)"
                      type="button"
                      class="btn o__secondary"
                      @click="openRollbackOperatorSessionVersions(item)"
                    >
                      Open versions
                    </button>
                  </div>
                </div>
                <div
                  v-if="rollbackOperatorSessionHiddenCount(session) > 0"
                  class="deploy-note mt-3"
                >
                  {{
                    `${rollbackOperatorSessionHiddenCount(session)} older session step${rollbackOperatorSessionHiddenCount(session) === 1 ? '' : 's'} hidden`
                  }}
                </div>
              </div>
              <div class="flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  class="btn o__secondary"
                  @click="selectRollbackSession(session)"
                >
                  {{ rollbackSessionSelected(session) ? 'Deep link active' : 'Select session' }}
                </button>
                <button
                  type="button"
                  class="btn o__secondary"
                  @click="copyRollbackSessionDeepLink(session)"
                >
                  Copy session link
                </button>
                <button
                  v-if="rollbackOperatorSessionNeedsToggle(session)"
                  type="button"
                  class="btn o__secondary"
                  @click="toggleRollbackSession(session)"
                >
                  {{ rollbackOperatorSessionToggleLabel(session) }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="deploy-note mt-3">
            No rollback sessions match the current filters yet.
          </div>
        </div>
        <div class="deploy-card mt-3">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Publish and rollback ledger</div>
              <div class="deploy-note">
                Durable timeline of global publishes, rollouts, rollback actions, and cleared overrides.
              </div>
            </div>
            <span class="deploy-tag">
              {{ recentPublishLedgerEntries.length }} shown
            </span>
          </div>
          <div v-if="recentPublishLedgerEntries.length" class="deploy-list mt-3">
            <div
              v-for="row in recentPublishLedgerEntries"
              :key="row.id || `${row.event_type}-${row.created_at}`"
              class="deploy-list-item deploy-list-item--stacked"
            >
              <div class="deploy-mode-title">
                {{ humanPublishLedgerEventLabel(row.event_type) }}
                <span class="deploy-note">
                  • {{ formatDateTime(row.created_at) }}
                </span>
              </div>
              <div class="deploy-note mt-1">
                {{ publishLedgerEntrySummary(row) }}
              </div>
              <div
                v-if="publishLedgerSessionBadges(row).length"
                class="mt-2 flex flex-wrap gap-2"
              >
                <span
                  v-for="badge in publishLedgerSessionBadges(row)"
                  :key="`${row.id || row.created_at}-${badge.label}`"
                  class="deploy-tag"
                  :class="{ 'deploy-tag--muted': badge.tone !== 'primary' }"
                >
                  {{ badge.label }}
                </span>
              </div>
              <div class="deploy-note mt-1">
                Target {{ publishLedgerVersionLabel(row.target_version_id, row.target_version_number) }}
                <span v-if="row.target_version_reason">
                  • {{ row.target_version_reason }}
                </span>
                <span v-if="Number(row.inbox_count || 0) > 0">
                  • {{ formatInteger(row.inbox_count) }} inboxes
                </span>
                <span v-if="String(row.rollout_mode || '').trim() === 'split'">
                  • {{ row.traffic_percentage }}% split
                </span>
                <span v-else-if="String(row.rollout_mode || '').trim() === 'pinned'">
                  • pinned rollout
                </span>
                <span v-else-if="String(row.rollout_mode || '').trim() === 'cleared'">
                  • overrides cleared
                </span>
              </div>
              <div
                v-if="publishLedgerEvidenceSummary(row)"
                class="deploy-note mt-1"
              >
                Linked evidence: {{ publishLedgerEvidenceSummary(row) }}
              </div>
              <div
                v-if="publishLedgerEntrySessionMeta(row)"
                class="deploy-note mt-1"
              >
                {{ publishLedgerEntrySessionMeta(row) }}
              </div>
              <div
                v-if="row.change_summary || publishLedgerChangedAreasSummary(row)"
                class="deploy-note mt-1"
              >
                <span v-if="row.change_summary">{{ row.change_summary }}</span>
                <span v-if="publishLedgerChangedAreasSummary(row)">
                  <span v-if="row.change_summary"> • </span>
                  Areas: {{ publishLedgerChangedAreasSummary(row) }}
                </span>
              </div>
              <div v-if="row.note" class="deploy-note mt-1">
                {{ row.note }}
              </div>
            </div>
          </div>
          <div v-else class="deploy-note mt-3">
            No durable publish or rollback ledger entries have been recorded yet. New entries will appear here after the backend migration runs and operators publish, rollback, or clear rollout overrides.
          </div>
        </div>
        <div
          v-if="rolloutCandidateVersions.length && rolloutInboxOptions.length"
          class="deploy-card mt-3"
        >
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Inbox rollout controls</div>
              <div class="deploy-note">
                Grey-release a chosen version to selected inboxes, or clear those inboxes back to the global published baseline.
              </div>
            </div>
            <span class="deploy-tag">
              {{ selectedRolloutInboxCount }} selected
            </span>
          </div>
          <div class="grid gap-3 mt-3">
            <label class="deploy-field">
              <span class="deploy-label">Rollout version</span>
              <select v-model="selectedRolloutVersionId" class="deploy-input">
                <option
                  v-for="version in rolloutCandidateVersions"
                  :key="version.id"
                  :value="version.id"
                >
                  #{{ version.version_number || version.id }}
                  {{ version.reason ? `- ${version.reason}` : '' }}
                </option>
              </select>
            </label>
            <label class="deploy-field">
              <span class="deploy-label">Traffic to selected version</span>
              <input
                v-model.number="rolloutTrafficPercentage"
                type="number"
                min="1"
                max="100"
                class="deploy-input"
                placeholder="100"
              />
              <div class="deploy-note mt-1">
                `100` pins the selected inboxes fully. `1–99` keeps the rest of traffic on the global published version for that inbox.
              </div>
            </label>
            <div class="deploy-actions" style="justify-content: flex-start; margin-top: 0;">
              <button type="button" class="btn o__secondary" @click="selectAllRolloutInboxes">
                Select all inboxes
              </button>
              <button type="button" class="btn o__secondary" @click="selectActiveRolloutInboxes">
                Select active rollouts
              </button>
              <button type="button" class="btn o__secondary" @click="clearRolloutInboxSelection">
                Clear selection
              </button>
            </div>
            <div class="deploy-list">
              <label
                v-for="inbox in rolloutInboxOptions"
                :key="inbox.id"
                class="deploy-list-item"
              >
                <div class="flex items-start gap-3">
                  <input
                    type="checkbox"
                    :checked="selectedRolloutInboxIds.includes(Number(inbox.id))"
                    @change="toggleRolloutInboxSelection(inbox.id)"
                  />
                  <div>
                    <div class="deploy-mode-title">
                      {{ inbox.name }}
                      <span v-if="inbox.channelType" class="deploy-note">({{ inbox.channelType }})</span>
                    </div>
                    <div class="deploy-note">
                      {{
                        inbox.rolloutVersionId
                          ? inbox.isSplitTest
                            ? `${inbox.trafficPercentage}% of traffic goes to version #${inbox.rolloutVersionNumber || inbox.rolloutVersionId}; the rest inherits the live baseline.`
                            : `Pinned to version #${inbox.rolloutVersionNumber || inbox.rolloutVersionId}.`
                          : publishedVersionRow?.version_number
                            ? `Inherits live version #${publishedVersionRow.version_number}.`
                            : publishedVersionId
                              ? `Inherits live version ID ${publishedVersionId}.`
                              : 'No global published baseline yet.'
                      }}
                    </div>
                  </div>
                </div>
                <span class="deploy-tag" :class="{ 'deploy-tag--muted': !inbox.rolloutVersionId }">
                  {{
                    inbox.rolloutVersionId
                      ? inbox.isSplitTest
                        ? 'Split'
                        : 'Rollout'
                      : 'Inherited'
                  }}
                </span>
              </label>
            </div>
            <div class="deploy-actions">
              <button
                type="button"
                class="btn o__secondary"
                :disabled="isSavingRollout || !selectedRolloutInboxCount"
                @click="clearSelectedRollouts"
              >
                {{ isSavingRollout ? 'Saving...' : 'Clear selected overrides' }}
              </button>
              <button
                type="button"
                class="btn o__secondary"
                :disabled="isSavingRollout || !rolloutCanPublish"
                @click="publishSelectedRollout"
              >
                {{ isSavingRollout ? 'Saving...' : rolloutPublishActionLabel }}
              </button>
            </div>
            <div v-if="rolloutGuardrails.length" class="deploy-list">
              <div
                v-for="item in rolloutGuardrails"
                :key="item.title"
                class="deploy-list-item"
              >
                <div>
                  <div class="deploy-mode-title">{{ item.title }}</div>
                  <div class="deploy-note">{{ item.body }}</div>
                </div>
                <span class="deploy-tag" :class="{ 'deploy-tag--muted': item.tone === 'warning' }">
                  {{ item.tone === 'warning' ? 'Review' : 'Track' }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="rolloutRows.length" class="deploy-list mt-3">
          <div
            v-for="row in rolloutRows.slice(0, 4)"
            :key="`${row.inboxId}-${row.versionId}`"
            class="deploy-list-item"
          >
            <div>
              <div class="deploy-mode-title">
                {{ row.inboxLabel }}
                <span v-if="row.channelType" class="deploy-note">({{ row.channelType }})</span>
              </div>
              <div class="deploy-note">
                Uses version
                {{ row.versionNumber ? `#${row.versionNumber}` : `ID ${row.versionId}` }}
                {{
                  row.isSplitTest
                    ? ` for ${row.trafficPercentage}% of traffic; the rest stays on the global baseline.`
                    : ' instead of the global baseline.'
                }}
              </div>
            </div>
            <span class="deploy-tag">{{ row.isSplitTest ? 'Split' : 'Rollout' }}</span>
          </div>
        </div>
        <div class="deploy-card mt-3">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">A/B traffic report</div>
              <div class="deploy-note">
                Compare live usage and key safety signals for the published baseline versus rollout versions.
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <label class="deploy-field" style="min-width: 120px;">
                <span class="deploy-label">Window (days)</span>
                <input
                  v-model.number="abReportDays"
                  type="number"
                  min="1"
                  class="deploy-input"
                />
              </label>
              <button
                type="button"
                class="btn o__secondary"
                :disabled="isLoadingAbReport"
                @click="loadAbReportSafely"
              >
                {{ isLoadingAbReport ? 'Refreshing report...' : 'Refresh report' }}
              </button>
            </div>
          </div>
          <div class="deploy-summary-grid mt-3">
            <div
              v-for="card in abReportSummaryCards"
              :key="card.label"
              class="deploy-stat-card"
            >
              <div class="deploy-label">{{ card.label }}</div>
              <div class="deploy-value">{{ card.value }}</div>
              <div class="deploy-note">{{ card.note }}</div>
            </div>
          </div>
          <div v-if="abReportRows.length" class="deploy-list mt-3">
            <div
              v-for="row in abReportRows.slice(0, 5)"
              :key="row.version_id"
              class="deploy-list-item deploy-list-item--stacked"
            >
              <div class="deploy-mode-title">
                Version {{ row.version_number ? `#${row.version_number}` : `ID ${row.version_id}` }}
                <span v-if="Number(row.version_id) === Number(publishedVersionId)" class="deploy-note">
                  (published baseline)
                </span>
                <span v-else-if="Number(row.version_id) === Number(selectedRolloutVersionId)" class="deploy-note">
                  (selected rollout target)
                </span>
              </div>
              <div class="deploy-note mt-1">
                {{ formatInteger(row.calls) }} calls
                • {{ formatInteger(row.total_tokens) }} total tokens
                • {{ formatUsd(row.estimated_cost_usd) }}
                • prompt injection {{ formatInteger(row.prompt_injection_count) }}
                ({{ formatPercent(row.prompt_injection_rate) }})
                • handoff requested {{ formatInteger(row.handoff_requested_count) }}
                ({{ formatPercent(row.handoff_requested_rate) }})
              </div>
              <div
                v-if="row.calls_delta_vs_published !== null"
                class="deploy-note mt-1"
              >
                vs published:
                {{ formatSignedInteger(row.calls_delta_vs_published) }} calls
                • {{ formatSignedUsd(row.cost_delta_vs_published) }}
                • prompt injection {{ formatSignedPercent(row.prompt_injection_rate_delta_vs_published) }}
                • handoff {{ formatSignedPercent(row.handoff_rate_delta_vs_published) }}
              </div>
              <div
                v-for="arm in row.arm_breakdown"
                :key="`${row.version_id}-${arm.arm}`"
                class="deploy-note mt-1"
              >
                {{ arm.arm_label }}:
                {{ formatInteger(arm.calls) }} calls
                ({{ formatPercent(arm.share_of_version_calls) }} of this version)
                • {{ formatUsd(arm.estimated_cost_usd) }}
                • prompt injection {{ formatInteger(arm.prompt_injection_count) }}
                ({{ formatPercent(arm.prompt_injection_rate) }})
                • handoff {{ formatInteger(arm.handoff_requested_count) }}
                ({{ formatPercent(arm.handoff_requested_rate) }})
              </div>
            </div>
          </div>
          <div v-else class="deploy-note mt-3">
            No tracked live traffic has been recorded yet for the current published/rollout version comparison set.
          </div>
        </div>
        <div v-if="activeBatchBaseline" class="deploy-card mt-3">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">
                {{ activeBatchBaseline.title || 'Durable batch baseline' }}
              </div>
              <div class="deploy-note">
                Use this saved batch evidence as the publish-readiness baseline before marking a repair released.
              </div>
              <div class="deploy-note mt-2">
                {{ activeBatchBaselineSummary }}
              </div>
            </div>
            <span
              class="deploy-tag"
              :class="{ 'deploy-tag--muted': activeBatchBaseline.overall_level === 'blocked' }"
            >
              {{ humanBatchBaselineLevel(activeBatchBaseline.overall_level) }}
            </span>
          </div>
          <div class="deploy-summary-grid mt-3">
            <div class="deploy-stat-card">
              <div class="deploy-label">Total cases</div>
              <div class="deploy-value">{{ activeBatchBaseline.total_cases || 0 }}</div>
              <div class="deploy-note">Questions included in the latest durable batch run.</div>
            </div>
            <div class="deploy-stat-card">
              <div class="deploy-label">Ready</div>
              <div class="deploy-value">{{ activeBatchBaseline.ready_cases || 0 }}</div>
              <div class="deploy-note">Cases that cleared the release baseline.</div>
            </div>
            <div class="deploy-stat-card">
              <div class="deploy-label">Watch</div>
              <div class="deploy-value">{{ activeBatchBaseline.watch_cases || 0 }}</div>
              <div class="deploy-note">Cases that still need rollout review.</div>
            </div>
            <div class="deploy-stat-card">
              <div class="deploy-label">Blocked</div>
              <div class="deploy-value">{{ activeBatchBaseline.blocked_cases || 0 }}</div>
              <div class="deploy-note">Cases that still fail the release baseline.</div>
            </div>
          </div>
        </div>
        <div
          v-if="recentBatchBaselines.length"
          class="deploy-card mt-3"
        >
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Recent batch baseline history</div>
              <div class="deploy-note">
                Reuse or clear durable release evidence without leaving Deploy.
              </div>
            </div>
            <span class="deploy-tag">{{ recentBatchBaselines.length }}</span>
          </div>
          <div class="deploy-list mt-3">
            <div
              v-for="baseline in recentBatchBaselines"
              :key="baseline.id || baseline.baseline_key"
              class="deploy-list-item"
            >
              <div>
                <div class="deploy-mode-title">
                  {{ baseline.title || 'Batch baseline' }}
                </div>
                <div class="deploy-note">
                  {{ humanBatchBaselineLevel(baseline.overall_level) }}
                  <span v-if="baseline.ran_at">
                    • {{ formatDateTime(baseline.ran_at) }}
                  </span>
                  <span>
                    • {{ baseline.total_cases || 0 }} cases
                  </span>
                  <span v-if="baseline.top_blocker">
                    • {{ baseline.top_blocker }}
                  </span>
                  <span v-else-if="baseline.top_watch_signal">
                    • {{ baseline.top_watch_signal }}
                  </span>
                  <span v-if="baseline.operator_note">
                    • {{ baseline.operator_note }}
                  </span>
                  <span v-if="baseline.operator_note_source">
                    • via {{ baseline.operator_note_source }}
                  </span>
                  <span v-if="evidenceHistoryTotalCount(baseline) > 1">
                    • {{ evidenceHistoryTotalCount(baseline) }} rollback notes
                  </span>
                </div>
                <div
                  v-if="evidenceHistoryEntries('baseline', baseline).length"
                  class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted"
                >
                  <div
                    v-for="(entry, index) in evidenceHistoryEntries('baseline', baseline)"
                    :key="evidenceHistoryEntryKey(entry, index)"
                    :class="index > 0 ? 'mt-2' : ''"
                  >
                    <div>{{ entry.body }}</div>
                    <div
                      v-if="evidenceHistoryEntryBadges(entry).length"
                      class="mt-2 flex flex-wrap gap-2"
                    >
                      <span
                        v-for="badge in evidenceHistoryEntryBadges(entry)"
                        :key="badge.label"
                        class="deploy-tag"
                        :class="{ 'deploy-tag--muted': badge.tone !== 'primary' }"
                      >
                        {{ badge.label }}
                      </span>
                    </div>
                    <div
                      v-if="evidenceHistoryEntryMeta(entry)"
                      class="deploy-note mt-1"
                    >
                      {{ evidenceHistoryEntryMeta(entry) }}
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="btn o__secondary"
                        @click="focusBatchBaselineHistory(baseline)"
                      >
                        Use baseline
                      </button>
                      <button
                        type="button"
                        class="btn o__secondary"
                        @click="openBatchEvidenceVersionHistory(baseline, entry)"
                      >
                        Open versions
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="!evidenceHistoryExpanded('baseline', baseline) && evidenceHistoryTotalCount(baseline) > evidenceHistoryEntries('baseline', baseline).length"
                    class="deploy-note mt-2"
                  >
                    {{
                      `${evidenceHistoryTotalCount(baseline) - evidenceHistoryEntries('baseline', baseline).length} older rollback note${evidenceHistoryTotalCount(baseline) - evidenceHistoryEntries('baseline', baseline).length === 1 ? '' : 's'} hidden`
                    }}
                  </div>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-if="evidenceHistoryNeedsToggle(baseline)"
                  type="button"
                  class="btn o__secondary"
                  @click="toggleEvidenceHistory('baseline', baseline)"
                >
                  {{ evidenceHistoryToggleLabel('baseline', baseline) }}
                </button>
                <button
                  type="button"
                  class="btn o__secondary"
                  :disabled="historyActionKey === `baseline:${baseline.id || baseline.baseline_key}`"
                  @click="focusBatchBaselineHistory(baseline)"
                >
                  Use baseline
                </button>
                <button
                  type="button"
                  class="btn o__secondary"
                  :disabled="historyActionKey === `baseline:${baseline.id || baseline.baseline_key}`"
                  @click="deleteBatchBaselineHistory(baseline)"
                >
                  {{ historyActionKey === `baseline:${baseline.id || baseline.baseline_key}` ? 'Removing...' : 'Delete' }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="seededReleaseBundle.enabled" class="deploy-card mt-3">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">
                {{ seededReleaseBundle.title || 'Seeded release review' }}
              </div>
              <div class="deploy-note">
                {{
                  seededReleaseBundle.focusLabel ||
                  seededReleaseBundle.validationSummary ||
                  'Capture the release snapshot, decision, and rollback context for this validated repair bundle.'
                }}
              </div>
              <div
                v-if="releaseReviewEntry?.reviewed_at || seededReleaseBundle.rollbackTrigger || releaseReviewEntry?.batch_title"
                class="deploy-note mt-2"
              >
                <span v-if="releaseReviewEntry?.reviewed_at">
                  Latest snapshot: {{ formatDateTime(releaseReviewEntry.reviewed_at) }}
                </span>
                <span v-if="releaseReviewEntry?.batch_title">
                  <span v-if="releaseReviewEntry?.reviewed_at"> • </span>
                  Batch baseline:
                  {{ releaseReviewEntry.batch_title }}
                  <span v-if="releaseReviewEntry?.batch_overall_level">
                    ({{ humanBatchBaselineLevel(releaseReviewEntry.batch_overall_level) }})
                  </span>
                </span>
                <span v-if="seededReleaseBundle.rollbackTrigger">
                  <span
                    v-if="releaseReviewEntry?.reviewed_at || releaseReviewEntry?.batch_title"
                  >
                    •
                  </span>
                  Rollback trigger: {{ seededReleaseBundle.rollbackTrigger }}
                </span>
              </div>
            </div>
            <span class="deploy-tag" :class="{ 'deploy-tag--muted': currentReleaseReviewStatus === 'rolled_back' }">
              {{ currentReleaseReviewStatusLabel }}
            </span>
          </div>
          <div class="grid gap-3 mt-3">
            <textarea
              v-model="releaseReviewNote"
              class="deploy-input deploy-textarea"
              placeholder="Capture rollout notes, validation evidence, or rollback conditions for this repair bundle."
            />
            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                class="btn o__secondary"
                :disabled="isSavingReleaseReview"
                @click="saveReleaseReview('reviewing')"
              >
                {{ isSavingReleaseReview ? 'Saving...' : 'Save snapshot' }}
              </button>
              <button
                type="button"
                class="btn o__secondary"
                :disabled="isSavingReleaseReview"
                @click="saveReleaseReview('released')"
              >
                Mark released
              </button>
              <button
                type="button"
                class="btn o__secondary"
                :disabled="isSavingReleaseReview"
                @click="saveReleaseReview('rolled_back')"
              >
                Mark rollback
              </button>
              <button
                v-if="releaseReviewEntry"
                type="button"
                class="btn o__secondary"
                :disabled="isSavingReleaseReview"
                @click="clearReleaseReview"
              >
                Clear snapshot
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="recentReleaseSnapshots.length"
          class="deploy-card mt-3"
        >
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Recent release snapshot history</div>
              <div class="deploy-note">
                Review prior release decisions and reopen the same repair bundle context.
              </div>
            </div>
            <span class="deploy-tag">{{ recentReleaseSnapshots.length }}</span>
          </div>
          <div class="deploy-list mt-3">
            <div
              v-for="snapshot in recentReleaseSnapshots"
              :key="snapshot.id || snapshot.snapshot_key"
              class="deploy-list-item"
            >
              <div>
                <div class="deploy-mode-title">
                  {{ snapshot.bundle_title || 'Release snapshot' }}
                </div>
                <div class="deploy-note">
                  {{ humanReleaseReviewStatus(snapshot.status).toLowerCase() }}
                  <span v-if="snapshot.reviewed_at">
                    • {{ formatDateTime(snapshot.reviewed_at) }}
                  </span>
                  <span v-if="snapshot.batch_title">
                    • {{ snapshot.batch_title }}
                  </span>
                  <span v-if="snapshot.note">
                    • {{ snapshot.note }}
                  </span>
                  <span v-if="snapshot.operator_note">
                    • {{ snapshot.operator_note }}
                  </span>
                  <span v-if="snapshot.operator_note_source">
                    • via {{ snapshot.operator_note_source }}
                  </span>
                  <span v-if="evidenceHistoryTotalCount(snapshot) > 1">
                    • {{ evidenceHistoryTotalCount(snapshot) }} rollback notes
                  </span>
                </div>
                <div
                  v-if="evidenceHistoryEntries('release', snapshot).length"
                  class="mt-2 rounded-large border border-neutral-border bg-white px-3 py-2 text-xs text-muted"
                >
                  <div
                    v-for="(entry, index) in evidenceHistoryEntries('release', snapshot)"
                    :key="evidenceHistoryEntryKey(entry, index)"
                    :class="index > 0 ? 'mt-2' : ''"
                  >
                    <div>{{ entry.body }}</div>
                    <div
                      v-if="evidenceHistoryEntryBadges(entry).length"
                      class="mt-2 flex flex-wrap gap-2"
                    >
                      <span
                        v-for="badge in evidenceHistoryEntryBadges(entry)"
                        :key="badge.label"
                        class="deploy-tag"
                        :class="{ 'deploy-tag--muted': badge.tone !== 'primary' }"
                      >
                        {{ badge.label }}
                      </span>
                    </div>
                    <div
                      v-if="evidenceHistoryEntryMeta(entry)"
                      class="deploy-note mt-1"
                    >
                      {{ evidenceHistoryEntryMeta(entry) }}
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="btn o__secondary"
                        @click="focusReleaseSnapshotHistory(snapshot)"
                      >
                        Open snapshot
                      </button>
                      <button
                        type="button"
                        class="btn o__secondary"
                        @click="openReleaseEvidenceVersionHistory(snapshot, entry)"
                      >
                        Open versions
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="!evidenceHistoryExpanded('release', snapshot) && evidenceHistoryTotalCount(snapshot) > evidenceHistoryEntries('release', snapshot).length"
                    class="deploy-note mt-2"
                  >
                    {{
                      `${evidenceHistoryTotalCount(snapshot) - evidenceHistoryEntries('release', snapshot).length} older rollback note${evidenceHistoryTotalCount(snapshot) - evidenceHistoryEntries('release', snapshot).length === 1 ? '' : 's'} hidden`
                    }}
                  </div>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-if="evidenceHistoryNeedsToggle(snapshot)"
                  type="button"
                  class="btn o__secondary"
                  @click="toggleEvidenceHistory('release', snapshot)"
                >
                  {{ evidenceHistoryToggleLabel('release', snapshot) }}
                </button>
                <button
                  type="button"
                  class="btn o__secondary"
                  :disabled="historyActionKey === `release:${snapshot.id || snapshot.snapshot_key}`"
                  @click="focusReleaseSnapshotHistory(snapshot)"
                >
                  Open snapshot
                </button>
                <button
                  type="button"
                  class="btn o__secondary"
                  :disabled="historyActionKey === `release:${snapshot.id || snapshot.snapshot_key}`"
                  @click="deleteReleaseSnapshotHistory(snapshot)"
                >
                  {{ historyActionKey === `release:${snapshot.id || snapshot.snapshot_key}` ? 'Removing...' : 'Delete' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div ref="channelStrategySectionRef" class="deploy-card">
        <div class="deploy-section-header">
          <div>
            <div class="deploy-card-title">Per-channel runtime strategy</div>
            <div class="deploy-note">
              Set the default assistant behavior once, then override specific inboxes
              when a channel needs a safer runtime mode.
            </div>
          </div>
        </div>
        <div class="deploy-summary-grid mt-3">
          <div
            v-for="card in perChannelStrategyCards"
            :key="card.label"
            class="deploy-stat-card"
          >
            <div class="deploy-label">{{ card.label }}</div>
            <div class="deploy-value">{{ card.value }}</div>
            <div class="deploy-note">{{ card.note }}</div>
          </div>
        </div>
        <div v-if="connectedInboxStrategyRows.length" class="deploy-list mt-3">
          <div
            v-for="row in connectedInboxStrategyRows"
            :key="row.id"
            class="deploy-list-item"
          >
            <div>
              <div class="deploy-mode-title">
                {{ row.name }}
                <span class="deploy-note">({{ row.channelType }})</span>
              </div>
              <div class="deploy-note">
                Effective mode: <strong>{{ humanReplyModeLabel(row.effectiveMode) }}</strong>
                <span v-if="row.overrideMode && row.overrideMode !== 'inherit'">
                  • forced by inbox override
                </span>
                <span v-else>• inherits assistant default</span>
              </div>
              <div v-if="row.overrideReason" class="deploy-note">
                Reason: {{ row.overrideReason }}
              </div>
              <div
                v-if="row.forcedReplyLanguage || row.communicationStyleMode"
                class="deploy-note"
              >
                <span v-if="row.forcedReplyLanguage">
                  Forced language:
                  <strong>
                    {{
                      runtimeLanguageOptions.find(option => option.value === row.forcedReplyLanguage)
                        ?.label || row.forcedReplyLanguage
                    }}
                  </strong>
                </span>
                <span v-if="row.forcedReplyLanguage && row.communicationStyleMode"> • </span>
                <span v-if="row.communicationStyleMode">
                  Style:
                  <strong>{{ formatCommunicationStyleLabel(row.communicationStyleMode) }}</strong>
                </span>
              </div>
            </div>
            <span
              class="deploy-tag"
              :class="{
                'deploy-tag--muted': !row.overrideMode || row.overrideMode === 'inherit',
              }"
            >
              {{
                row.overrideMode && row.overrideMode !== 'inherit'
                  ? humanReplyModeLabel(row.overrideMode)
                  : 'Inherited'
              }}
            </span>
          </div>
        </div>
        <div ref="businessHoursSectionRef" class="deploy-card deploy-card--nested mt-4">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Business-hours runtime</div>
              <div class="deploy-note">
                This writes `prompt_sections.channel_runtime.business_hours`. When an
                inbox is outside its own working-hours schedule, runtime can switch to
                a safer mode or mirror the inbox out-of-office message.
              </div>
            </div>
          </div>
          <label class="deploy-checkbox-row mt-3">
            <input v-model="businessHoursEnabled" type="checkbox" />
            <div>
              <div class="deploy-mode-title">Use inbox business-hours schedules</div>
              <div class="deploy-note">
                Only applies when the active inbox already has working hours enabled.
              </div>
            </div>
          </label>
          <div class="deploy-field-grid">
            <label class="deploy-field">
              <span class="deploy-label">Outside business hours</span>
              <select
                v-model="outsideBusinessHoursReplyMode"
                class="deploy-input"
                :disabled="!businessHoursEnabled"
              >
                <option
                  v-for="option in businessHoursReplyModeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <label class="deploy-field">
              <span class="deploy-label">Customer-facing behavior</span>
              <select
                v-model="outsideBusinessHoursResponseBehavior"
                class="deploy-input"
                :disabled="!businessHoursEnabled"
              >
                <option
                  v-for="option in businessHoursResponseBehaviorOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>
          <div class="deploy-note mt-3">
            Selected inbox schedule:
            <strong>
              {{
                selectedInboxBusinessHoursSummary.enabled
                  ? `enabled (${selectedInboxBusinessHoursSummary.timezone})`
                  : 'disabled'
              }}
            </strong>
          </div>
          <div class="deploy-note">
            <span v-if="selectedInboxBusinessHoursSummary.outOfOfficeMessage">
              Out-of-office message is available for this inbox.
            </span>
            <span v-else>
              This inbox does not currently have an out-of-office message saved.
            </span>
          </div>
          <div class="deploy-actions">
            <button
              type="button"
              class="btn o__secondary"
              :disabled="isSavingBusinessHours"
              @click="saveBusinessHoursStrategy"
            >
              {{ isSavingBusinessHours ? 'Saving...' : 'Save business-hours strategy' }}
            </button>
          </div>
        </div>
        <div ref="regionComplianceSectionRef" class="deploy-card deploy-card--nested mt-4">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Region and compliance strategy</div>
              <div class="deploy-note">
                This writes `prompt_sections.channel_runtime.region_compliance`. When
                a conversation exposes `country_code`, runtime can switch to a market
                compliance profile and update the prompt identity country.
              </div>
            </div>
            <button
              type="button"
              class="btn o__secondary"
              @click="addRegionComplianceBinding"
            >
              Add market
            </button>
          </div>
          <div class="deploy-field-grid mt-3">
            <label class="deploy-field">
              <span class="deploy-label">Default compliance profile</span>
              <select v-model="defaultComplianceProfile" class="deploy-input">
                <option
                  v-for="option in complianceProfileOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>
          <div v-if="!regionComplianceBindings.length" class="deploy-empty mt-3">
            No market-specific compliance rules yet.
          </div>
          <div v-else class="deploy-list mt-3">
            <div
              v-for="row in regionComplianceBindings"
              :key="row.id"
              class="deploy-list-item deploy-list-item--stacked"
            >
              <div class="deploy-field-grid">
                <label class="deploy-field">
                  <span class="deploy-label">Market</span>
                  <select v-model="row.country_code" class="deploy-input">
                    <option
                      v-for="option in regionCountryOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
                <label class="deploy-field">
                  <span class="deploy-label">Compliance profile</span>
                  <select v-model="row.compliance_profile" class="deploy-input">
                    <option
                      v-for="option in complianceProfileOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
              </div>
              <div class="deploy-section-header">
                <div class="deploy-note">
                  {{
                    regionCountryOptions.find(option => option.value === row.country_code)?.label ||
                    row.country_code
                  }}
                  uses
                  <strong>{{ row.compliance_profile }}</strong>
                  compliance.
                </div>
                <button
                  type="button"
                  class="btn o__secondary"
                  @click="removeRegionComplianceBinding(row.id)"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          <div class="deploy-actions">
            <button
              type="button"
              class="btn o__secondary"
              :disabled="isSavingChannelStrategy"
              @click="saveRegionComplianceStrategy"
            >
              {{
                isSavingChannelStrategy
                  ? 'Saving...'
                  : 'Save region and compliance strategy'
              }}
            </button>
          </div>
        </div>
        <div class="deploy-card deploy-card--nested mt-4">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Segment runtime rules</div>
              <div class="deploy-note">
                These rules write `prompt_sections.channel_runtime.segment_bindings`.
                When a language or VIP segment matches, it can override reply mode,
                force reply language, or tighten communication style before runtime
                generates the answer.
              </div>
            </div>
            <button type="button" class="btn o__secondary" @click="addSegmentRuntimeBinding">
              Add rule
            </button>
          </div>
          <div v-if="!segmentRuntimeBindings.length" class="deploy-empty mt-3">
            No segment runtime rules yet.
          </div>
          <div v-else class="deploy-list mt-3">
            <div
              v-for="row in segmentRuntimeBindings"
              :key="row.id"
              class="deploy-list-item deploy-list-item--stacked"
            >
              <div class="deploy-field-grid">
                <label class="deploy-field">
                  <span class="deploy-label">Segment</span>
                  <select
                    v-model="row.segment_type"
                    class="deploy-input"
                    @change="row.match_value = row.segment_type === 'vip_conversation' ? 'true' : 'en'"
                  >
                    <option
                      v-for="option in segmentTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
                <label v-if="row.segment_type !== 'vip_conversation'" class="deploy-field">
                  <span class="deploy-label">Matches when</span>
                  <select v-model="row.match_value" class="deploy-input">
                    <option
                      v-for="option in languageMatchOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
                <label v-else class="deploy-field">
                  <span class="deploy-label">Matches when</span>
                  <input value="VIP conversation" class="deploy-input" disabled />
                </label>
              </div>
              <div class="deploy-field-grid">
                <label class="deploy-field">
                  <span class="deploy-label">Reply mode</span>
                  <select v-model="row.reply_mode" class="deploy-input">
                    <option value="inherit">Inherit inbox/default mode</option>
                    <option value="auto_reply">Auto reply</option>
                    <option value="auto_suggest">Auto suggest</option>
                    <option value="suggest">Suggest only</option>
                    <option value="handoff">Handoff</option>
                  </select>
                </label>
                <label class="deploy-field">
                  <span class="deploy-label">Forced reply language</span>
                  <select v-model="row.forced_reply_language" class="deploy-input">
                    <option
                      v-for="option in runtimeLanguageOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
                <label class="deploy-field">
                  <span class="deploy-label">Communication style</span>
                  <select v-model="row.communication_style_mode" class="deploy-input">
                    <option
                      v-for="option in communicationStyleOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
              </div>
              <div class="deploy-section-header">
                <div class="deploy-note">
                  {{ segmentBindingLabel(row) }}
                </div>
                <button
                  type="button"
                  class="btn o__secondary"
                  @click="removeSegmentRuntimeBinding(row.id)"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          <div class="deploy-actions">
            <button
              type="button"
              class="btn o__secondary"
              :disabled="isSavingChannelStrategy"
              @click="saveSegmentRuntimeBindings"
            >
              {{ isSavingChannelStrategy ? 'Saving...' : 'Save segment runtime rules' }}
            </button>
          </div>
        </div>
      </div>

      <div class="deploy-two-col">
        <div ref="inboxControlsSectionRef" class="deploy-card">
          <div class="deploy-card-title">Reply mode</div>
          <div class="deploy-note">
            This writes the assistant-level `wlab_ai_reply_mode` used by runtime routing.
          </div>
          <div class="deploy-mode-grid">
            <label class="deploy-mode-card" :class="{ active: replyMode === 'auto_reply' }">
              <input v-model="replyMode" type="radio" value="auto_reply" />
              <div class="deploy-mode-title">Auto reply</div>
              <div class="deploy-note">AI sends directly without agent review.</div>
            </label>
            <label class="deploy-mode-card" :class="{ active: replyMode === 'auto_suggest' }">
              <input v-model="replyMode" type="radio" value="auto_suggest" />
              <div class="deploy-mode-title">Auto suggest</div>
              <div class="deploy-note">AI drafts and can auto-stage suggestions.</div>
            </label>
            <label class="deploy-mode-card" :class="{ active: replyMode === 'suggest' }">
              <input v-model="replyMode" type="radio" value="suggest" />
              <div class="deploy-mode-title">Suggest</div>
              <div class="deploy-note">AI only suggests, agents decide what to send.</div>
            </label>
          </div>
          <div class="deploy-actions">
            <button type="button" class="btn o__secondary" :disabled="isSavingReplyMode" @click="saveReplyMode">
              {{ isSavingReplyMode ? 'Saving...' : 'Save reply mode' }}
            </button>
          </div>
        </div>

        <div class="deploy-card">
          <div class="deploy-card-title">Runtime status</div>
          <div class="deploy-note">
            This writes `ai_closer_config.status` and controls whether the AI can run at all.
          </div>
          <div class="deploy-status-stack">
            <div class="deploy-status-row" :class="{ active: runtimeStatus === 'active' }">
              <div>
                <div class="deploy-mode-title">Active</div>
                <div class="deploy-note">Assistant can run on connected channels.</div>
              </div>
              <button type="button" class="btn o__secondary" :disabled="isSavingStatus || runtimeStatus === 'active'" @click="saveRuntimeStatus('active')">
                Activate
              </button>
            </div>
            <div class="deploy-status-row" :class="{ active: runtimeStatus === 'inactive' }">
              <div>
                <div class="deploy-mode-title">Inactive</div>
                <div class="deploy-note">Assistant is disabled even if channels remain connected.</div>
              </div>
              <button type="button" class="btn o__secondary" :disabled="isSavingStatus || runtimeStatus === 'inactive'" @click="saveRuntimeStatus('inactive')">
                Deactivate
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="deploy-two-col">
        <div class="deploy-card">
          <div class="deploy-card-title">Handoff & resolution</div>
          <div class="deploy-note">
            These write `handoff_*` and `resolution_message` on the assistant config used by runtime jobs.
          </div>

          <label class="deploy-checkbox-row">
            <input v-model="handoffEnabled" type="checkbox" />
            <div>
              <div class="deploy-mode-title">Allow human handoff</div>
              <div class="deploy-note">
                When enabled, runtime can send the configured handoff message and route to agents.
              </div>
            </div>
          </label>

          <div class="deploy-field-grid">
            <label class="deploy-field">
              <span class="deploy-label">Assignment strategy</span>
              <select v-model="handoffAssignStrategy" class="deploy-input" :disabled="!handoffEnabled || isFetchingAgents">
                <option value="inherit">Inherit inbox defaults</option>
                <option value="none">No assignment</option>
                <option value="round_robin">Round robin</option>
                <option value="specific">Specific agents</option>
              </select>
            </label>
            <label class="deploy-field">
              <span class="deploy-label">Resolution message</span>
              <textarea
                v-model="resolutionMessage"
                class="deploy-input deploy-textarea"
                placeholder="Customer-facing message used when conversations auto-resolve."
              />
            </label>
          </div>

          <label
            v-if="handoffEnabled && handoffAssignStrategy === 'specific'"
            class="deploy-field"
          >
            <span class="deploy-label">Specific assignees</span>
            <select
              v-model="handoffAssignAgentIds"
              class="deploy-input deploy-multiselect"
              multiple
            >
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name || agent.email || `Agent #${agent.id}` }}
              </option>
            </select>
            <span class="deploy-note">Choose one or more agents for direct handoff assignment.</span>
          </label>

          <label class="deploy-field">
            <span class="deploy-label">Handoff message</span>
            <textarea
              v-model="handoffMessage"
              class="deploy-input deploy-textarea"
              :disabled="!handoffEnabled"
              placeholder="One of our team members will reach out soon."
            />
          </label>

          <div class="deploy-actions">
            <button type="button" class="btn o__secondary" :disabled="isSavingHandoff || isFetchingAgents" @click="saveHandoffSettings">
              {{ isSavingHandoff ? 'Saving...' : 'Save handoff settings' }}
            </button>
          </div>
        </div>

        <div class="deploy-card">
          <div class="deploy-card-title">Per-inbox runtime controls</div>
          <div class="deploy-note">
            These save the inbox-level auto-resolve timer and AI override settings on the selected inbox.
          </div>

          <div v-if="!connectedInboxes.length" class="deploy-empty">
            Connect an inbox first to configure auto-resolve and AI safety override.
          </div>

          <template v-else>
            <label class="deploy-field">
              <span class="deploy-label">Selected inbox</span>
              <select v-model="selectedInboxId" class="deploy-input">
                <option v-for="inbox in connectedInboxes" :key="inbox.id" :value="inbox.id">
                  {{ inbox.name }}{{ inbox.channel_type ? ` (${inbox.channel_type})` : '' }}
                </option>
              </select>
            </label>

            <div class="deploy-field-grid">
              <label class="deploy-field">
                <span class="deploy-label">Auto-resolve after</span>
                <div class="deploy-inline-input">
                  <input
                    v-model.number="autoResolveAfterMinutes"
                    type="number"
                    min="0"
                    class="deploy-input"
                  />
                  <span class="deploy-inline-suffix">minutes</span>
                </div>
                <span class="deploy-note">Set `0` to disable automatic resolution.</span>
              </label>

              <label class="deploy-field">
                <span class="deploy-label">AI override mode</span>
                <select v-model="aiOverrideMode" class="deploy-input">
                  <option value="inherit">Inherit assistant mode</option>
                  <option value="auto_reply">Force auto reply</option>
                  <option value="auto_suggest">Force auto suggest</option>
                  <option value="suggest">Force suggest only</option>
                  <option value="handoff">Force handoff</option>
                </select>
              </label>
            </div>

            <div class="deploy-field-grid">
              <label class="deploy-field">
                <span class="deploy-label">Forced reply language</span>
                <select v-model="forcedReplyLanguage" class="deploy-input">
                  <option
                    v-for="option in runtimeLanguageOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="deploy-field">
                <span class="deploy-label">Communication style</span>
                <select v-model="communicationStyleMode" class="deploy-input">
                  <option
                    v-for="option in communicationStyleOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>

            <label class="deploy-field">
              <span class="deploy-label">Override reason</span>
              <input
                v-model="aiOverrideReason"
                type="text"
                class="deploy-input"
                :disabled="aiOverrideMode === 'inherit'"
                placeholder="Why this inbox is using a safer runtime mode"
                maxlength="120"
              />
            </label>

            <div class="deploy-note">
              Last override update:
              <strong>{{ aiOverrideMeta.setAt || 'N/A' }}</strong>
              <span v-if="aiOverrideMeta.setByUserId">
                (user_id={{ aiOverrideMeta.setByUserId }})
              </span>
            </div>
            <div class="deploy-note mt-3">
              Effective runtime mode for this inbox:
              <strong>{{ humanReplyModeLabel(selectedInboxEffectiveMode) }}</strong>
            </div>
            <div class="deploy-note">
              Segment-based overrides are not live here yet. They will land once the
              language/VIP rule set above matches. Inbox controls still act as the
              channel-level fallback beneath those segment rules.
            </div>

            <div class="deploy-actions">
              <button type="button" class="btn o__secondary" :disabled="isSavingInboxRuntime || isUpdatingInbox" @click="saveInboxRuntime">
                {{ isSavingInboxRuntime ? 'Saving...' : 'Save inbox runtime controls' }}
              </button>
            </div>
          </template>
        </div>
      </div>

      <div class="deploy-two-col">
        <div class="deploy-card">
          <div class="deploy-section-header">
            <div>
              <div class="deploy-card-title">Channels & inboxes</div>
              <div class="deploy-note">
                Connect or disconnect inboxes that this assistant should serve.
              </div>
            </div>
            <button type="button" class="btn o__secondary" @click="handleCreate">
              Connect inbox
            </button>
          </div>

          <div v-if="isFetchingInboxes" class="deploy-empty">Loading inboxes...</div>
          <div v-else-if="!ailabInboxes.length" class="deploy-empty">No connected inboxes yet.</div>
          <div v-else class="deploy-list">
            <div v-for="inbox in ailabInboxes" :key="inbox.id" class="deploy-list-item">
              <div>
                <div class="deploy-mode-title">{{ inbox.name }}</div>
                <div class="deploy-note">{{ inbox.channel_type || 'channel' }}</div>
              </div>
              <button type="button" class="btn o__secondary" @click="handleAction({ action: 'delete', id: inbox.id })">
                Disconnect
              </button>
            </div>
          </div>
        </div>
      </div>

      <DeleteDialog
        v-if="selectedInbox"
        ref="disconnectInboxDialog"
        :entity="selectedInbox"
        :delete-payload="{ assistantId: assistantId, inboxId: selectedInbox.id }"
        type="Inboxes"
        @close="handleCreateClose"
      />

      <ConnectInboxDialog
        v-if="dialogType && !isAIGamer"
        ref="connectInboxDialog"
        :assistant-id="assistantId"
        :type="dialogType"
        @close="handleCreateClose"
      />

      <ConnectInboxDialogAIGamer
        v-if="dialogType && isAIGamer"
        ref="connectInboxDialog"
        :assistant-id="assistantId"
        @close="handleCreateClose"
      />
    </div>
  </div>
  <AiCloserConfigVersionsDialog
    ref="configVersionsDialogRef"
    :assistant-id="Number(assistantId || 0)"
    :release-evidence="activePublishEvidenceContext"
    @updated="refreshAll"
  />
</template>

<style scoped>
.deploy-shell { display: flex; flex-direction: column; gap: 16px; min-height: 0; }
.deploy-warning-stack { display: flex; flex-direction: column; gap: 6px; }
.deploy-header, .deploy-section-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
.deploy-summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
.deploy-two-col { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.deploy-card, .deploy-stat-card, .deploy-version-card, .deploy-mode-card, .deploy-status-row, .deploy-list-item { border: 1px solid var(--fin-neutral-border); background: #fff; border-radius: 14px; }
.deploy-card { padding: 16px; }
.deploy-card--nested { background: #fbfdff; }
.deploy-stat-card, .deploy-version-card, .deploy-status-row, .deploy-list-item { padding: 12px; }
.deploy-list-item--selected { border-color: #2563eb; box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1); }
.deploy-journey-item--selected {
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 10px;
  background: rgba(239, 246, 255, 0.7);
  padding: 8px;
}
.deploy-handoff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.deploy-handoff-summary-block,
.deploy-handoff-keyline {
  grid-column: 1 / -1;
}
.deploy-handoff-summary-block {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.deploy-handoff-keyline {
  padding: 10px 12px;
  border: 1px solid rgba(37, 99, 235, 0.12);
  border-radius: 12px;
  background: rgba(239, 246, 255, 0.65);
}
.deploy-handoff-keyline--success {
  border-color: rgba(5, 150, 105, 0.18);
  background: rgba(236, 253, 245, 0.9);
}
.deploy-handoff-keyline--warning {
  border-color: rgba(217, 119, 6, 0.2);
  background: rgba(255, 251, 235, 0.95);
}
.deploy-handoff-keyline--danger {
  border-color: rgba(220, 38, 38, 0.2);
  background: rgba(254, 242, 242, 0.95);
}
.deploy-handoff-preview {
  white-space: pre-line;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  word-break: break-word;
}
.deploy-label { font-size: 11px; line-height: 15px; font-weight: 600; color: #6b7280; display: block; margin-bottom: 4px; }
.deploy-value { font-size: 18px; line-height: 24px; font-weight: 700; color: #111827; }
.deploy-note, .deploy-muted { font-size: 12px; line-height: 17px; color: #6b7280; }
.deploy-card-title, .deploy-mode-title { font-size: 14px; line-height: 19px; font-weight: 650; color: #111827; }
.deploy-mode-grid, .deploy-version-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 12px; }
.deploy-field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 12px; }
.deploy-field { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
.deploy-mode-card { display: flex; flex-direction: column; gap: 6px; padding: 12px; cursor: pointer; }
.deploy-mode-card input { margin: 0; }
.deploy-mode-card.active, .deploy-status-row.active { border-color: #60a5fa; box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.12); }
.deploy-actions { display: flex; justify-content: flex-end; margin-top: 12px; }
.deploy-status-stack, .deploy-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.deploy-status-row, .deploy-list-item { display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.deploy-list-item--stacked { display: block; }
.deploy-checkbox-row { display: flex; align-items: flex-start; gap: 12px; margin-top: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 12px; }
.deploy-checkbox-row input { margin-top: 4px; }
.deploy-input { width: 100%; border: 1px solid #d1d5db; border-radius: 10px; padding: 9px 11px; font-size: 13px; line-height: 18px; color: #111827; background: #fff; }
.deploy-input:disabled { background: #f8fafc; color: #6b7280; }
.deploy-textarea { min-height: 88px; resize: vertical; }
.deploy-multiselect { min-height: 120px; }
.deploy-inline-input { display: flex; align-items: center; gap: 10px; }
.deploy-inline-suffix { font-size: 12px; line-height: 17px; color: #6b7280; white-space: nowrap; }
.deploy-empty { border: 1px dashed #d1d5db; border-radius: 12px; padding: 16px; text-align: center; color: #6b7280; font-size: 13px; margin-top: 12px; }
.deploy-rollout-table { margin-top: 16px; }
.deploy-tag { display: inline-flex; align-items: center; border-radius: 9999px; padding: 4px 8px; background: #eff6ff; color: #1d4ed8; font-size: 10px; line-height: 13px; font-weight: 700; }
.deploy-tag--muted { background: #f3f4f6; color: #6b7280; }
.deploy-tag--success { background: #ecfdf5; color: #047857; }
.deploy-tag--warning { background: #fffbeb; color: #b45309; }
.deploy-tag--danger { background: #fef2f2; color: #b91c1c; }
.mt-3 { margin-top: 12px; }
.mb-2 { margin-bottom: 8px; }
@media (max-width: 1100px) { .deploy-two-col, .deploy-mode-grid, .deploy-version-grid, .deploy-field-grid { grid-template-columns: 1fr; } }
</style>
